// #SPLIT_YARD_V1 — moved out of FleetModule.js unchanged (Bill 10/21: get the big files under 10,000 lines).
import { useState, useEffect, Fragment, useMemo, useRef } from "react";
import { stripCP } from "./ducValid";
import { _accessToken } from "./appShared";   // #EMAIL_SEND_AS_USER_V1
import { findEmp } from "./ShopDash";
import CONSIGN_VERMEER_SEED from "./consignVermeerSeed.json";
import CONSIGN_MSC_SEED from "./consignMscSeed.json";
import { isoWeekYear, isoWeek } from "./weekOf";
import FUEL_YARD_SEED from "./fuelYardHistorySeed.json";
import FUEL_IMPAC_SEED from "./fuelImpacHistorySeed.json";
import { useCalName } from "./staffName";
import CHEM_SPEND_HIST from "./chemSpendHistorySeed.json";
import { iso, num, addDays, cents, isoWeekNum, fmtDate, RivetCorners, money2, AutoCompleteInput, fmtMD, money, CONSIGN_KEY, _csgnNum, _csgnHas, consignTotal, glow, mondayOf, pad2 } from "./fleetShared";

// #FLEET_CHEM_INVENTORY_V1 (2026-08-27) — Bill: Chemicals isn't a simple list, it's a real consignment
// inventory. "We buy in Bulk and basically hold on our internal consignment then allocate to jobs from
// supervisors from inventory... goal is to know the value of what we have on hand." Phase 1 (this): the
// Core Inventory itself — product, item # ("item number is what we supply and have on hand"), unit of
// measure (cost varies by UofM — Bill's own examples: "5 GAL, BAG or 1 GAL"), cost per UofM (all-in —
// "we will include shipping & sales tax in price for each"), and on-hand qty, so the $ value on hand is
// always known and shipments can be received (add to qty) as they arrive. New storage key
// (`fleet-chem-inventory`) — the old flat list (`fleet-yard-chemicals`, 19 rows that were really ad hoc
// allocation entries: a job # jammed into "Unit", a person's name jammed into "Notes") is left
// orphaned, per Bill: "we can delete them all." Phase 2 (next, not this pass): the daily/weekly
// Allocation Log — Date, Supervisor, Job (pulled from Daily, subtracting from this inventory as it
// goes), rolled into a Mon-Sun pivot for weekly expenses — Bill's own sequencing: "he needs an
// inventory first before he can allocate daily. Then daily/weekly allocation."
function ChemicalInventory({ sg, ss, canEdit, canDelete, F }) {
  const STORAGE_KEY = 'fleet-chem-inventory';
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  useEffect(() => { (async () => {
    try { const v = await sg(STORAGE_KEY); setRows(Array.isArray(v) ? v : []); } catch { setRows([]); }
  })(); }, [sg]);
  const persist = async (next) => {
    setRows(next);
    try { await ss(STORAGE_KEY, next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const save = async () => {
    if (!edit) return;
    const name = String(edit.name||'').trim();
    if (!name) { alert('Product name is required.'); return; }
    const rec = { id: edit._orig ? edit._orig.id : (name.toLowerCase().replace(/[^a-z0-9]+/g,'-') + '-' + Date.now().toString(36)),
      name, itemNo: edit.itemNo||'', uom: edit.uom||'', cost: num(edit.cost), qty: num(edit.qty) };
    const next = edit._orig ? (rows||[]).map(r => r.id===edit._orig.id ? rec : r) : [ ...(rows||[]), rec ];
    setEdit(null);
    await persist(next);
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name} from Core Inventory entirely? This does not affect any allocations already logged.`)) return;
    await persist((rows||[]).filter(x => x.id !== r.id));
  };
  const receive = async (r) => {
    const raw = window.prompt(`Shipment received — how many ${r.uom||'units'} of ${r.name} to add to the ${num(r.qty)} on hand?`, '');
    if (raw == null) return;
    const add = num(raw);
    if (!add) return;
    await persist((rows||[]).map(x => x.id===r.id ? { ...x, qty: num(x.qty) + add } : x));
  };
  const btn = { background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:8, padding:'6px 12px', fontSize:12.5, fontWeight:700, cursor:'pointer' };
  const inp = { background:F.bg, border:`1px solid ${F.line}`, borderRadius:7, padding:'6px 9px', fontSize:12.5, color:F.text, width:'100%', boxSizing:'border-box' };
  const th = { padding:'8px 10px', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, textAlign:'left' };
  const td = { padding:'6px 10px', fontSize:12.5, color:F.text, verticalAlign:'middle' };
  const ql = q.trim().toLowerCase();
  const filtered = (rows||[]).filter(r => !ql || `${r.name} ${r.itemNo||''}`.toLowerCase().includes(ql));
  const totalValue = (rows||[]).reduce((sum,r)=> sum + num(r.qty)*num(r.cost), 0);
  return (
    <Fragment>
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6, flexWrap:'wrap' }}>
        <div style={{ fontSize:13, fontWeight:900 }}>Chemicals — Core Inventory</div>
        <div style={{ fontSize:11.5, color:F.muted }}>{(rows||[]).length} product{(rows||[]).length===1?'':'s'}</div>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" style={{ ...inp, width:200 }} />
        <div style={{ flex:1 }} />
        {canEdit && <button onClick={()=>setEdit({ name:'', itemNo:'', uom:'', cost:'', qty:'' })} style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Add item</button>}
      </div>
      <div style={{ fontSize:11.5, color:F.muted, marginBottom:10, lineHeight:1.5 }}>
        What's actually on hand and what it's worth — cost is per unit of measure (a 5 GAL, a BAG, a
        1 GAL, whatever the product ships in), all-in with shipping and sales tax, since price varies by
        product. Allocating this out to jobs by day/week is the next build, once this list is real.
      </div>
      <div style={{ display:'inline-block', background:F.card, border:`1px solid ${F.line}`, borderRadius:10, padding:'8px 14px', marginBottom:14 }}>
        <span style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted }}>Total value on hand </span>
        <span style={{ fontSize:15, fontWeight:900, color:F.green, marginLeft:6 }}>{money2(totalValue)}</span>
      </div>
      {edit && (
        <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10 }}>
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>Product</div>
              <input value={edit.name||''} onChange={e=>setEdit(d=>({ ...d, name:e.target.value }))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>Item #</div>
              <input value={edit.itemNo||''} onChange={e=>setEdit(d=>({ ...d, itemNo:e.target.value }))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>Unit of measure</div>
              <input value={edit.uom||''} onChange={e=>setEdit(d=>({ ...d, uom:e.target.value }))} placeholder="5 GAL, BAG, 1 GAL…" style={inp} />
            </div>
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>Cost / UofM ($, all-in)</div>
              <input value={edit.cost==null?'':edit.cost} onChange={e=>setEdit(d=>({ ...d, cost:e.target.value }))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>On hand qty</div>
              <input value={edit.qty==null?'':edit.qty} onChange={e=>setEdit(d=>({ ...d, qty:e.target.value }))} style={inp} />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <button onClick={save} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
            <button onClick={()=>setEdit(null)} style={btn}>Close without saving</button>
          </div>
        </div>
      )}
      {rows === null ? <div style={{ fontSize:12, color:F.muted }}>Loading…</div>
        : !filtered.length ? <div style={{ fontSize:12, color:F.muted }}>Nothing here yet — add your first product above.</div>
        : (
        <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={th}>Product</th><th style={th}>Item #</th><th style={th}>UofM</th><th style={th}>On hand</th><th style={th}>Cost/UofM</th><th style={th}>Value</th><th style={th}></th></tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ borderTop:`1px solid ${F.line}` }}>
                  <td style={{ ...td, fontWeight:800 }}>{r.name}</td>
                  <td style={{ ...td, color:F.muted }}>{r.itemNo||'—'}</td>
                  <td style={{ ...td, color:F.muted }}>{r.uom||'—'}</td>
                  <td style={td}>{num(r.qty)}</td>
                  <td style={td}>{money2(r.cost)}</td>
                  <td style={{ ...td, fontWeight:800, color:F.green }}>{money2(num(r.qty)*num(r.cost))}</td>
                  <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                    {canEdit && <button onClick={()=>receive(r)} style={{ ...btn, padding:'4px 8px', fontSize:11 }}>+ Receive</button>}{' '}
                    {canEdit && <button onClick={()=>setEdit({ ...r, _orig:r })} style={{ ...btn, padding:'4px 8px', fontSize:11 }}>Edit</button>}{' '}
                    {canDelete && <button onClick={()=>del(r)} style={{ ...btn, padding:'4px 8px', fontSize:11, color:F.red }}>Remove</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Fragment>
  );
}

// #FLEET_CHEM_DISBURSE_V2 (2026-09-01) — Bill tested V1 live, then asked for a real redesign rather
// than a tab toggle: "he could see core inventory while its being depleted on the same screen vs
// having to toggle back and forth... would enjoy seeing this LIVE number moving as items go out...
// the table should track this daily and be able to sort by headers." Layout per his own sketch:
// Core Inventory on the left (condensed — his own complaint was how much empty space it wasted
// alone on a full-width screen), and on the right, stacked: "on top or about the majority of screen
// tracking weekly activity line items (Dispatch then tosses into list pushing down the oldest but
// able to expand list if when needed), then smaller table on the bottom right tracking his This
// week/last week dispatched spend." One component now owns Core Inventory + the Dispatch log
// together in shared state (`inv`/`log` both live here, not split across two independently-fetching
// components) — that's what makes on-hand qty move on screen the instant a dispatch is logged,
// no refresh, no tab switch. Gate note: folded Core Inventory's own add/edit (previously the plain
// `editable` flag on the old standalone tab) onto the same `yardOpsEdit` (Owner/Fleet/Yard) gate
// Dispatch already used — matches the Yard split's own documented intent ("he will own these
// reports") now that both live on one screen; narrower than before, flagged in the write-up.
const CHEM_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// Bill's own "Mon-Sun pivot" — a real 7-day yard week, distinct from Fleet Daily's 5-day labor week (#FLEET_NOSAT_V1)
const chemMondayISO = () => { const d = new Date(); const b = (d.getDay()+6)%7; d.setDate(d.getDate()-b); d.setHours(0,0,0,0); return iso(d); };

// #FLEET_CHEM_DIST3_V1 (2026-09-22) — Bill: needs 3 separate saved recipient lists (Vermeer /
// DitchWitch / DDD), not the one shared list this started as — and wants the same chip add/remove
// UX the Daily Operations Report's own distribution list already uses (App.js), not a window.prompt.
// Reusable here since three lists need the identical editor rendered three times. Typing supports
// comma/semicolon/newline so pasting several addresses at once still works, same as the old prompt.
function DistChips({ title, list, onAdd, onRemove, btn, inp, F }) {
  const [val, setVal] = useState('');
  const commit = () => {
    const found = String(val||'').split(/[\s,;]+/).map(s=>s.trim()).filter(s => s.includes('@'));
    if (!found.length) return;
    onAdd(found);
    setVal('');
  };
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:10.5, fontWeight:800, color:F.muted, textTransform:'uppercase', letterSpacing:.4, marginBottom:4 }}>{title}</div>
      <div style={{ display:'flex', gap:6, marginBottom:6 }}>
        <input value={val} onChange={e=>setVal(e.target.value)} placeholder="email@domain.com (comma for more than one)" style={{ ...inp, flex:1 }} onKeyDown={e=>e.key==='Enter'&&commit()} />
        <button onClick={commit} style={{ ...btn, padding:'4px 10px', fontSize:11 }}>+ Add</button>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {(list||[]).map(email => (
          <span key={email} style={{ background:`${F.accent}22`, color:F.gold, padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
            {email}
            <span onClick={()=>onRemove(email)} style={{ cursor:'pointer', color:F.red, fontWeight:800 }}>×</span>
          </span>
        ))}
        {!(list||[]).length && <span style={{ fontSize:11, color:F.muted }}>No recipients yet</span>}
      </div>
    </div>
  );
}

// #EMAIL_ADDR_CLEAN_V1 (2026-10-29) — "Name <x@y.com>" pasted from Outlook → x@y.com (Graph rejects the brackets).
export const cleanEmails = (v) => [...new Set((Array.isArray(v) ? v : String(v||'').split(/[,;\s]+/))
  .map(a => (String(a||'').match(/[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/) || [''])[0].toLowerCase()).filter(Boolean))];
// #EMAIL_SEND_AS_USER_V1 (2026-10-29) — vendor orders go out FROM the person sending (Jerry), not dakotaops@.
export const sendVendorEmail = async (payload) => {
  let tok = ''; try { tok = (await _accessToken()) || ''; } catch {}
  const res = await fetch('/api/send-email', { method:'POST', headers:{ 'Content-Type':'application/json', ...(tok ? { Authorization:'Bearer ' + tok } : {}) },
    body: JSON.stringify({ ...payload, asUser:true }) });
  return res.json().catch(()=>({}));
};
const sentNote = (out) => out && out.replyTo ? ` (from DOPE — replies go to ${out.replyTo})` : out && out.sentAs ? ` from ${out.sentAs}` : '';

export function ChemicalsYardDashboard({ sg, ss, canEdit, canDelete, F, jobIndex, liveJobs, sups, userRole, upn, sup2duc }) {
  const INV_KEY = 'fleet-chem-inventory';
  const LOG_KEY = 'fleet-chem-disbursement-log';
  // #FLEET_CHEM_CSGN_V1 (2026-09-22) — Bill: some Core Inventory items are consignment stock, not
  // DUC-owned — needs a Code (DCI / DrilRite / V CSGN / D CSGN), and for V CSGN/D CSGN specifically
  // an auto-populated reorder queue that produces an emailable order and tracks it as "Ordered" so
  // it isn't re-ordered. ORD_KEY holds order runs. #FLEET_CHEM_DIST3_V1 — three separate saved
  // recipient lists (Vermeer / DitchWitch / DDD — Bill's names), each its own key, kept apart from
  // Daily Inventory's own Dist… list. AutoCompleteInput wants {label,value} options.
  const ORD_KEY = 'fleet-chem-orders';
  const VDIST_KEY = 'fleet-chem-dist-vermeer';
  const DDIST_KEY = 'fleet-chem-dist-ditchwitch';
  const DDDDIST_KEY = 'fleet-chem-dist-ddd';
  const DDD_SEED_EMAIL = 'bob.macy@dakotautility.com';   // known real recipient, seeds the DDD list until Bill edits it
  const CSGN_CODES = ['DCI', 'DrilRite', 'V CSGN', 'D CSGN'].map(c => ({ label: c, value: c }));
  const todayISO = iso(new Date());
  const [inv, setInv] = useState(null);
  const [log, setLog] = useState(null);
  const [wkStart, setWkStart] = useState(chemMondayISO);
  const [form, setForm] = useState({ date: todayISO, sup:'', duc:'', productId:'', qty:'', kind:'issue', note:'', ducAuto:false });
  const [pendingOvr, setPendingOvr] = useState(null);
  const [feedExpanded, setFeedExpanded] = useState(false);
  const [invQ, setInvQ] = useState('');
  const [invSort, setInvSort] = useState({ key:'name', dir:'asc' });
  const [invEdit, setInvEdit] = useState(null);
  // #FLEET_CHEM_CSGN_V1 — order-queue state: which consignment vendor group is being built into an
  // order (null when nothing's being built), the editable qty per item while building it, and which
  // of the four Orders tabs is showing. #FLEET_CHEM_BOBORDER_V1 (2026-09-22) — Bill: DCI/DrilRite
  // don't have reorder-point automation yet, but he wants a quick way to send Bob Macy (Fleet
  // Manager) an ad-hoc order in the meantime — free-typed lines, not tied to Core Inventory items.
  const [orders, setOrders] = useState(null);
  const [vermeerDist, setVermeerDist] = useState([]);
  const [ditchwitchDist, setDitchwitchDist] = useState([]);
  const [dddDist, setDddDist] = useState([]);
  const [distOpen, setDistOpen] = useState(false);
  const [orderTab, setOrderTab] = useState('needs');   // 'needs' | 'ordered' | 'history' | 'bob'
  const [orderBuild, setOrderBuild] = useState(null);   // { code, qtys:{productId:qty}, note, po }
  const [bobBuild, setBobBuild] = useState({ lines:[{ desc:'', qty:'', uom:'' }], po:'', note:'' });
  const [orderSending, setOrderSending] = useState(false);   // #FLEET_CHEM_ORDERSEND_V1
  const [orderSendMsg, setOrderSendMsg] = useState('');
  // #FLEET_CHEM_PIVOT_V1 (2026-09-10) — Bill: "will need to produce a Pivot with DATE (Month)
  // Product, Supervisor, JOB Numbers & Cost for my summary report that i am still running
  // parallel." Grouped rows Month -> Product, drilling into Supervisor/Job (his pick over a flat
  // table or a Month/Sup x Product cross-tab), Cost = net of returns (matches how Dispatched
  // spend already nets things out). Full log history, not just the current week — a real Pivot
  // needs to reach back across months the way the weekly dashboard above doesn't.
  const [chemView, setChemView] = useState('dash');   // 'dash' | 'pivot'
  const [pivotOpenMonths, setPivotOpenMonths] = useState(() => new Set());
  const [pivotOpenProducts, setPivotOpenProducts] = useState(() => new Set());

  useEffect(() => { (async () => {
    try { const v = await sg(INV_KEY); setInv(Array.isArray(v) ? v : []); } catch { setInv([]); }
    try { const v = await sg(LOG_KEY); setLog(Array.isArray(v) ? v : []); } catch { setLog([]); }
    try { const v = await sg(ORD_KEY); setOrders(Array.isArray(v) ? v : []); } catch { setOrders([]); }
    try { const v = await sg(VDIST_KEY); setVermeerDist(Array.isArray(v) ? v : []); } catch { setVermeerDist([]); }
    try { const v = await sg(DDIST_KEY); setDitchwitchDist(Array.isArray(v) ? v : []); } catch { setDitchwitchDist([]); }
    try { const v = await sg(DDDDIST_KEY); setDddDist(Array.isArray(v) ? v : [DDD_SEED_EMAIL]); } catch { setDddDist([DDD_SEED_EMAIL]); }
  })(); }, [sg]);

  const persistInv = async (next) => { setInv(next); try { await ss(INV_KEY, next); } catch (e) { alert('Could not save inventory: ' + (e.message||e)); } };
  const persistLog = async (next) => { setLog(next); try { await ss(LOG_KEY, next); } catch (e) { alert('Could not save log: ' + (e.message||e)); } };
  const persistOrders = async (next) => { setOrders(next); try { await ss(ORD_KEY, next); } catch (e) { alert('Could not save orders: ' + (e.message||e)); } };
  const invRow = (id) => (inv||[]).find(r => r.id === id);
  // #FLEET_CHEM_CSGNFIX_V1 (2026-09-24) — was "any non-blank code", which wrongly swept
  // DCI/DrilRite (owned stock, just carrying a supplier reference code) into "consigned" and
  // zeroed out Value on hand. Only V CSGN / D CSGN are actual consignment — same two codes
  // needsOrderByCode below already keys off.
  const isConsigned = (r) => { const c = String((r||{}).code||'').trim(); return c === 'V CSGN' || c === 'D CSGN'; };
  // #FLEET_CHEM_DIST3_V1 — generic add/remove backing DistChips for all three lists; add() takes
  // an array so pasting a comma-separated batch adds them all in one save.
  const addDist = async (key, setter, list, emails) => {
    const next = cleanEmails([ ...(list||[]), ...(emails||[]) ]);   // #EMAIL_ADDR_CLEAN_V1
    setter(next);
    try { await ss(key, next); } catch (e) { alert('Could not save the list: ' + (e.message||e)); }
  };
  const removeDist = async (key, setter, list, email) => {
    const next = (list||[]).filter(e => e !== email);
    setter(next);
    try { await ss(key, next); } catch (e) { alert('Could not save the list: ' + (e.message||e)); }
  };

  // ── Core Inventory — add/edit/receive/remove, now sharing `inv` state with Dispatch so a
  // logged dispatch/return is reflected here the instant it saves, no refresh needed. ──
  const saveInvItem = async () => {
    if (!invEdit) return;
    const name = String(invEdit.name||'').trim();
    if (!name) { alert('Product name is required.'); return; }
    const rec = { id: invEdit._orig ? invEdit._orig.id : (name.toLowerCase().replace(/[^a-z0-9]+/g,'-') + '-' + Date.now().toString(36)),
      name, itemNo: invEdit.itemNo||'', uom: invEdit.uom||'', cost: num(invEdit.cost), qty: num(invEdit.qty),
      code: String(invEdit.code||'').trim(), reorderPt: num(invEdit.reorderPt) };
    const next = invEdit._orig ? (inv||[]).map(r => r.id===invEdit._orig.id ? rec : r) : [ ...(inv||[]), rec ];
    setInvEdit(null);
    await persistInv(next);
  };
  const delInvItem = async (r) => {
    if (!window.confirm(`Remove ${r.name} from Core Inventory entirely? This does not affect any dispatch already logged.`)) return;
    await persistInv((inv||[]).filter(x => x.id !== r.id));
  };
  // #FLEET_CHEM_MIN_V1 (2026-09-28) — Jerry (by email, via Bill): "Need to add min (this is what I
  // need to have on hand)... this will build an order to maintain minimum when we draw down the
  // inventory." Same field as the old "Reorder pt (consignment only)" — renamed/generalized to Min
  // on hand and now a real, inline-editable Core Inventory column instead of something buried in
  // Edit. Saves straight off the table, no modal round-trip.
  const saveMin = async (r, val) => {
    const v = num(val);
    if (v === num(r.reorderPt)) return;
    await persistInv((inv||[]).map(x => x.id === r.id ? { ...x, reorderPt: v } : x));
  };
  const receiveInv = async (r) => {
    const raw = window.prompt(`Shipment received — how many ${r.uom||'units'} of ${r.name} to add to the ${num(r.qty)} on hand?`, '');
    if (raw == null) return;
    const add = num(raw);
    if (!add) return;
    await persistInv((inv||[]).map(x => x.id===r.id ? { ...x, qty: num(x.qty) + add } : x));
  };

  // ── Dispatch — same "is this really an open job" gate Daily Inventory's own gateFor gives,
  // collapsed from its warn/lock day-count ladder since chemicals just need the override checkpoint.
  const gate = (rawDuc) => {
    const d = stripCP(rawDuc);
    if (!d) return { state:'empty' };
    const j = jobIndex[d];
    if (!j) return { state:'bad', msg:'Not on the Job List' };
    const st = String(j.stat||'').toLowerCase();
    if (st === 'called in' || st === 'in progress') return { state:'ok', job:j };
    return { state:'needsOvr', job:j, msg:`Job is ${j.stat}` };
  };
  // #FLEET_CHEM_SUPAUTO_V1 -- the same SUP->DUC lookup Daily Inventory already trusts (sup2duc,
  // handed down as a prop), reused here so the Job/DUC field fills itself the moment a Supervisor is
  // typed, same as it already does on Daily. Only touches the field while it's still "auto" -- the
  // instant someone edits DUC by hand (see the AutoCompleteInput onChange below) it's pinned and this
  // effect leaves it alone, exactly like Daily's own pinned-row rule.
  useEffect(() => {
    if (!sup2duc) return;
    const sup = String(form.sup||'').trim();
    if (!sup) return;
    if (form.duc && form.ducAuto === false) return;
    const m = sup2duc(sup, form.date || todayISO);
    if (m.verdict === 'ok' && m.duc && m.duc !== form.duc) {
      setForm(f => ({ ...f, duc: m.duc, ducAuto: true }));
    }
  }, [form.sup, form.date]);   // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => setForm({ date: todayISO, sup:'', duc:'', productId:'', qty:'', kind:'issue', note:'', ducAuto:false });
  const submit = async (ovr) => {
    if (!canEdit) return;
    const p = invRow(form.productId);
    if (!p) { alert('Pick a product.'); return; }
    const qty = num(form.qty);
    if (!qty) { alert('Enter a quantity.'); return; }
    const g = gate(form.duc);
    if (g.state === 'empty') { alert('Enter a Job / DUC number.'); return; }
    if (g.state === 'bad') { alert(`${form.duc} is not on the Job List — check the DUC.`); return; }
    if (g.state === 'needsOvr' && !ovr) { setPendingOvr({ duc: form.duc, g }); return; }
    const sup = String(form.sup||'').trim().toUpperCase();
    if (!sup) { alert('Enter a Supervisor.'); return; }
    const kind = form.kind === 'return' ? 'return' : 'issue';
    const entry = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2,7),
      date: form.date || todayISO, sup, duc: stripCP(form.duc),
      jobCust: g.job ? g.job.cust : '', jobCity: g.job ? g.job.city : '',
      productId: p.id, product: p.name, uom: p.uom || '',
      qty, unitCost: num(p.cost), totalCost: qty * num(p.cost), kind,
      ovr: ovr ? { by: upn || String(userRole||''), at: new Date().toISOString() } : null,
      note: form.note || '', loggedBy: upn || String(userRole||''), loggedAt: new Date().toISOString(),
    };
    const delta = kind === 'issue' ? -qty : qty;
    await persistInv((inv||[]).map(r => r.id === p.id ? { ...r, qty: num(r.qty) + delta } : r));
    await persistLog([ ...(log||[]), entry ]);
    setPendingOvr(null);
    resetForm();
  };
  const delEntry = async (e) => {
    if (!canDelete) return;
    if (!window.confirm(`Remove this ${e.kind} — ${e.qty} ${e.uom||''} ${e.product} — and reverse its effect on ${e.duc}'s stock?`)) return;
    const back = e.kind === 'issue' ? e.qty : -e.qty;
    await persistInv((inv||[]).map(r => r.id === e.productId ? { ...r, qty: num(r.qty) + back } : r));
    await persistLog((log||[]).filter(x => x.id !== e.id));
  };
  // #FLEET_CHEM_JERRYEDIT_V1 (2026-10-12) — Jerry asked (via Bill) to be able to correct this week's
  // dispatch lines himself (e.g. picked the Grand Prairie job by mistake). Rule: ONLY Jerry (plus
  // Owner, so Bill can test) may Edit, and ONLY lines dated in the current Mon–Sun yard week. The
  // moment the week rolls over (Monday 12:00 AM) those lines lock — no Edit, and Remove on a past
  // week is Owner-only. An edit reverses the old line's stock effect and applies the new one, and
  // the line keeps an edit trail (who / when / what it was) so nothing changes silently.
  const CHEM_EDITOR = 'jerry.waedekin@dakotautility.com';
  const isOwnerRole = String(userRole||'') === 'Owner';
  const canEditWeekLine = (e) => {
    const me = String(upn||'').trim().toLowerCase();
    if (me !== CHEM_EDITOR && !isOwnerRole) return false;
    const cur = chemMondayISO();
    const curDates = CHEM_DAYS.map((_,i) => iso(addDays(new Date(cur+'T12:00:00'), i)));
    return curDates.includes(e.date);
  };
  const canRemoveLine = (e) => {
    if (!canDelete) return false;
    if (isOwnerRole) return true;
    const cur = chemMondayISO();
    return String(e.date||'') >= cur;   // past weeks locked for everyone but Owner
  };
  const [rowEdit, setRowEdit] = useState(null);   // { id, date, sup, duc, productId, qty, kind, note }
  const startRowEdit = (e) => setRowEdit({ id:e.id, date:e.date, sup:e.sup||'', duc:e.duc||'', productId:e.productId||'', qty:String(e.qty??''), kind:e.kind==='return'?'return':'issue', note:e.note||'' });
  const saveRowEdit = async () => {
    if (!rowEdit) return;
    const orig = (log||[]).find(x => x.id === rowEdit.id);
    if (!orig) { setRowEdit(null); return; }
    if (!canEditWeekLine(orig)) { alert('This week is closed — that line is locked.'); setRowEdit(null); return; }
    const cur = chemMondayISO();
    const curDates = CHEM_DAYS.map((_,i) => iso(addDays(new Date(cur+'T12:00:00'), i)));
    if (!curDates.includes(rowEdit.date)) { alert('Date has to stay inside the current week.'); return; }
    const p = invRow(rowEdit.productId);
    if (!p) { alert('Pick a product.'); return; }
    const qty = num(rowEdit.qty);
    if (!qty) { alert('Enter a quantity.'); return; }
    const sup = String(rowEdit.sup||'').trim().toUpperCase();
    if (!sup) { alert('Enter a Supervisor.'); return; }
    const g = gate(rowEdit.duc);
    if (g.state === 'empty') { alert('Enter a Job / DUC number.'); return; }
    if (g.state === 'bad') { alert(`${rowEdit.duc} is not on the Job List — check the DUC.`); return; }
    let ovr = orig.ovr || null;
    if (g.state === 'needsOvr') {
      if (stripCP(rowEdit.duc) !== orig.duc || !orig.ovr) {
        if (!window.confirm(`${rowEdit.duc} — ${g.msg}. Charge to it anyway?`)) return;
        ovr = { by: upn || String(userRole||''), at: new Date().toISOString() };
      }
    } else ovr = null;
    const kind = rowEdit.kind === 'return' ? 'return' : 'issue';
    const unitCost = orig.productId === p.id ? num(orig.unitCost) : num(p.cost);
    const before = { date:orig.date, sup:orig.sup, duc:orig.duc, product:orig.product, qty:orig.qty, kind:orig.kind, totalCost:orig.totalCost, note:orig.note||'' };
    const next = { ...orig, date: rowEdit.date, sup, duc: stripCP(rowEdit.duc),
      jobCust: g.job ? g.job.cust : '', jobCity: g.job ? g.job.city : '',
      productId: p.id, product: p.name, uom: p.uom || '',
      qty, unitCost, totalCost: qty * unitCost, kind, ovr, note: rowEdit.note || '',
      edits: [ ...(Array.isArray(orig.edits) ? orig.edits : []), { by: upn || String(userRole||''), at: new Date().toISOString(), before } ] };
    // stock: undo the original line, then apply the corrected one
    const undo = orig.kind === 'issue' ? num(orig.qty) : -num(orig.qty);
    const apply = kind === 'issue' ? -qty : qty;
    let nextInv = (inv||[]).map(r => r.id === orig.productId ? { ...r, qty: num(r.qty) + undo } : r);
    nextInv = nextInv.map(r => r.id === p.id ? { ...r, qty: num(r.qty) + apply } : r);
    await persistInv(nextInv);
    await persistLog((log||[]).map(x => x.id === orig.id ? next : x));
    setRowEdit(null);
  };

  const btn = { background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:8, padding:'6px 12px', fontSize:12.5, fontWeight:700, cursor:'pointer' };
  const inp = { background:F.bg, border:`1px solid ${F.line}`, borderRadius:7, padding:'6px 9px', fontSize:12.5, color:F.text, width:'100%', boxSizing:'border-box' };
  const th = { padding:'7px 9px', fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, textAlign:'left' };
  const td = { padding:'5px 9px', fontSize:12, color:F.text, verticalAlign:'middle' };
  const lbl = { fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 };
  // #FLEET_CHEM_MONEYALIGN_V1 (2026-09-23) — Bill: "$ formatting so it looks clean and even." Same
  // fix as #FLEET_MONEYALIGN_V1 elsewhere in this file — $ sits against its own number (not pinned to
  // the far edge of the column) and the whole group right-aligns with tabular-nums, so Per Unit and
  // Value stack their decimal points instead of the $ signs landing at a different x per row.
  const ChemMoney = ({ v, color, dim }) => (Number(v)||0) === 0
    ? <span style={{ display:'block', textAlign:'right', color:F.muted }}>—</span>
    : (
      <span style={{ display:'flex', justifyContent:'flex-end', fontWeight:800,
        fontVariantNumeric:'tabular-nums', color: color || (dim ? F.muted : F.text) }}>
        <span style={{ display:'inline-flex', gap:3 }}>
          <span style={{ color:F.muted, fontWeight:700 }}>$</span><span>{cents(v)}</span>
        </span>
      </span>
    );

  if (inv === null || log === null) return <div style={{ fontSize:12, color:F.muted }}>Loading…</div>;

  // #FLEET_CHEM_SUPAUTO_V1 -- one dispatch form, so plain consts (no useMemo needed) are plenty; the
  // per-row memoization on Daily Inventory's own ducOptions/supOptions is what ~300 rows actually needs.
  const ducOptions = liveJobs.map(j => ({ value:j.duc, label:j.duc, sub:[j.cust,j.city].filter(Boolean).join(' · ') }));
  const supOptions = sups.map(s => ({ value:s, label:s }));
  const supMatch = sup2duc ? sup2duc(form.sup, form.date || todayISO) : null;

  const wkDates = CHEM_DAYS.map((_,i) => iso(addDays(new Date(wkStart+'T12:00:00'), i)));
  const wkNum = isoWeekNum(new Date(wkStart+'T12:00:00'));
  const weekEntries = (log||[]).filter(e => wkDates.includes(e.date))
    .sort((a,b) => (b.date+(b.loggedAt||'')).localeCompare(a.date+(a.loggedAt||'')));
  const FEED_CAP = 8;
  const feedRows = feedExpanded ? weekEntries : weekEntries.slice(0, FEED_CAP);
  const weekTotal = weekEntries.reduce((s,e)=> s + (e.kind==='return' ? -e.totalCost : e.totalCost), 0);

  const lastWeekStart = iso(addDays(new Date(wkStart+'T12:00:00'), -7));
  const lastWeekDates = CHEM_DAYS.map((_,i) => iso(addDays(new Date(lastWeekStart+'T12:00:00'), i)));
  const lastWeekTotal = (log||[]).filter(e => lastWeekDates.includes(e.date))
    .reduce((s,e)=> s + (e.kind==='return' ? -e.totalCost : e.totalCost), 0);
  const wowDelta = weekTotal - lastWeekTotal;
  const wowPct = lastWeekTotal ? (wowDelta / lastWeekTotal * 100) : null;

  const priorYearTotal = (() => {
    const y = new Date(wkStart+'T12:00:00').getFullYear() - 1;
    return (log||[]).filter(e => {
      const d = new Date(e.date+'T12:00:00'); if (isNaN(d.getTime())) return false;
      return d.getFullYear() === y && isoWeekNum(d) === wkNum;
    }).reduce((s,e)=> s + (e.kind==='return' ? -e.totalCost : e.totalCost), 0);
  })();
  const yoyDelta = weekTotal - priorYearTotal;
  const yoyPct = priorYearTotal ? (yoyDelta / priorYearTotal * 100) : null;

  const byProduct = {};
  weekEntries.forEach(e => { byProduct[e.product] = (byProduct[e.product]||0) + (e.kind==='return' ? -e.totalCost : e.totalCost); });
  const topProducts = Object.entries(byProduct).sort((a,b)=>b[1]-a[1]).slice(0,3);

  const ql = invQ.trim().toLowerCase();
  const sortArrow = (key) => invSort.key===key ? (invSort.dir==='asc'?' ▲':' ▼') : '';
  const clickSort = (key) => setInvSort(s => ({ key, dir: s.key===key && s.dir==='asc' ? 'desc' : 'asc' }));
  const sortedInv = (inv||[]).filter(r => !ql || `${r.name} ${r.itemNo||''}`.toLowerCase().includes(ql)).slice().sort((a,b) => {
    const mul = invSort.dir === 'asc' ? 1 : -1;
    if (invSort.key === 'code') return String(a.code||'').localeCompare(String(b.code||'')) * mul;
    if (invSort.key === 'onhand') return (num(a.qty) - num(b.qty)) * mul;
    if (invSort.key === 'value') return (num(a.qty)*num(a.cost) - num(b.qty)*num(b.cost)) * mul;
    return String(a.name||'').localeCompare(String(b.name||'')) * mul;
  });
  const totalValue = (inv||[]).filter(r=>!isConsigned(r)).reduce((s,r)=> s + num(r.qty)*num(r.cost), 0);
  const consignedValue = (inv||[]).filter(isConsigned).reduce((s,r)=> s + num(r.qty)*num(r.cost), 0);

  // #FLEET_CHEM_PIVOT_V1 — Month -> Product -> Supervisor/Job, net $ (issue minus return).
  // monthLabel keeps the sort key ('YYYY-MM', string-sortable) separate from the display label.
  const monthLabel = (ym) => { const d = new Date(ym+'-01T12:00:00'); return isNaN(d.getTime()) ? ym : d.toLocaleString('en-US',{ month:'long', year:'numeric' }); };
  // Plain computation, not useMemo — this whole component already computes its other derived
  // values (weekEntries, byProduct, sortedInv, totalValue, etc.) as plain consts below the
  // `if (inv === null || log === null) return …` early-return above, and a hook can't live after
  // a conditional return (React's "Hooks must be called in the exact same order" rule) — that's
  // what broke the build (`useMemo is called conditionally`, FleetModule.js:5152).
  const pivotMonths = (() => {
    const byMonth = {};
    (log||[]).forEach(e => {
      const ym = String(e.date||'').slice(0,7);
      if (!ym) return;
      const net = e.kind==='return' ? -e.totalCost : e.totalCost;
      const qtyNet = e.kind==='return' ? -e.qty : e.qty;
      if (!byMonth[ym]) byMonth[ym] = { total:0, products:{} };
      const mo = byMonth[ym];
      mo.total += net;
      if (!mo.products[e.product]) mo.products[e.product] = { total:0, uom:e.uom, sups:{} };
      const pr = mo.products[e.product];
      pr.total += net;
      const sk = `${e.sup}||${e.duc}`;
      if (!pr.sups[sk]) pr.sups[sk] = { sup:e.sup, duc:e.duc, jobCust:e.jobCust, jobCity:e.jobCity, qty:0, cost:0 };
      pr.sups[sk].qty += qtyNet;
      pr.sups[sk].cost += net;
    });
    return Object.entries(byMonth).sort((a,b) => b[0].localeCompare(a[0]));
  })();
  const pivotGrandTotal = pivotMonths.reduce((s,[,mo]) => s + mo.total, 0);
  const toggleMonth = (ym) => setPivotOpenMonths(s => { const n = new Set(s); n.has(ym) ? n.delete(ym) : n.add(ym); return n; });
  const toggleProduct = (key) => setPivotOpenProducts(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // #FLEET_CHEM_PIVOT_V1 — export mirrors the on-screen drill-down: one row per Month/Product/
  // Supervisor/Job so Bill can drop it straight into his parallel Excel summary and check it line
  // by line, plus TOTAL rows per month and a grand total, same footer convention as the other CSV
  // exports in this file (dayCsv/weekRollupCsv above).
  const chemCsvCell = (v) => { const s = String(v == null ? '' : v); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
  const pivotCsv = () => {
    const head = ['Month','Product','Supervisor','Job Number','Customer','City','Qty','Cost'];
    const lines = [head.join(',')];
    pivotMonths.forEach(([ym, mo]) => {
      Object.entries(mo.products).sort((a,b) => b[1].total - a[1].total).forEach(([product, pr]) => {
        Object.values(pr.sups).sort((a,b) => b.cost - a.cost).forEach(s => {
          lines.push([monthLabel(ym), product, s.sup, s.duc, s.jobCust, s.jobCity, s.qty, s.cost.toFixed(2)].map(chemCsvCell).join(','));
        });
      });
      lines.push(['TOTAL — ' + monthLabel(ym), '', '', '', '', '', '', mo.total.toFixed(2)].map(chemCsvCell).join(','));
    });
    lines.push(['GRAND TOTAL', '', '', '', '', '', '', pivotGrandTotal.toFixed(2)].map(chemCsvCell).join(','));
    return lines.join('\n');
  };
  const downloadPivotCsv = () => {
    const name = `Chemicals Pivot ${iso(new Date())}.csv`;
    const blob = new Blob(['﻿' + pivotCsv()], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 4000);
    return name;
  };

  // #FLEET_CHEM_CSGN_V1 — Needs Order: any V CSGN/D CSGN item at or below its reorder point, that
  // isn't already sitting in an open ('ordered') order record — so a flagged item drops off the
  // list the moment it's queued and doesn't reappear just because it's still low while awaited.
  // Actually receiving stock still goes through the existing +Rcv action on Core Inventory (single
  // source of truth for on-hand qty) — "Mark received" here only clears the queue.
  // #FLEET_CHEM_ORDERQTY_V1 (2026-09-22) — Bill: wants to visually see qty in route per item on
  // Core Inventory (before On hand), bold red, display-only — never folded into On hand or Value.
  const orderedQtyByProduct = {};
  (orders||[]).forEach(o => { if (o.status === 'ordered') (o.items||[]).forEach(it => { if (it.productId) orderedQtyByProduct[it.productId] = (orderedQtyByProduct[it.productId]||0) + num(it.qty); }); });
  const openOrderProductIds = new Set(Object.keys(orderedQtyByProduct));
  const needsOrderByCode = {};
  (inv||[]).forEach(r => {
    const code = String(r.code||'').trim();
    if (!code) return;   // no vendor to route an order to — flagged on Core Inventory instead, not queued
    if (openOrderProductIds.has(r.id)) return;
    if (!(num(r.reorderPt) > 0) || num(r.qty) > num(r.reorderPt)) return;
    (needsOrderByCode[code] = needsOrderByCode[code] || []).push(r);
  });
  const needsOrderCount = Object.values(needsOrderByCode).reduce((n,a)=>n+a.length, 0);
  const openOrders = (orders||[]).filter(o => o.status === 'ordered').sort((a,b) => (b.createdAt||'').localeCompare(a.createdAt||''));
  const orderHistory = (orders||[]).filter(o => o.status !== 'ordered').sort((a,b) => (b.createdAt||'').localeCompare(a.createdAt||''));
  const vendorName = (code) => code === 'V CSGN' ? 'Vermeer' : code === 'D CSGN' ? 'Ditch Witch' : code;

  // #FLEET_CHEM_ORDERPRINT_V1 (2026-09-22) — Bill: order request needs logo/address/PO. mailto
  // can't embed an image in the body (hard limit, same one #FLEET_CSV_MAIL_V1 and PMDashboard's
  // own #PO_PRINT_V1/emailPo already documented) — so this reuses that exact established pattern:
  // a real window.print() letterhead (Fleet's own /ddd-logo.png, per #PO_LOGO_FIX_V1's Fleet-vs-DUC
  // logo rule) the mail draft points at for Print > Save as PDF > attach.
  const printOrder = (rec) => {
    const win = window.open('', '_blank', 'width=850,height=1100');
    if (!win) { alert('Allow pop-ups so the printable order can open.'); return; }
    const U = (v) => (v!=null && String(v).trim()) ? String(v).replace(/</g,'&lt;') : '&nbsp;';
    const itemRows = (rec.items||[]).map(it => `<tr><td>${U(it.qty)}</td><td>${U(it.uom)}</td><td>${U(it.name)}</td></tr>`).join('');
    const title = (rec.code === 'V CSGN' || rec.code === 'D CSGN') ? `Consignment Order — ${vendorName(rec.code)}` : `Order Request — ${vendorName(rec.code)}`;
    const html = '<!doctype html><html><head><meta charset="utf-8"><title>' + U(title) + '</title><style>'
      + '@page{margin:0.6in;} body{font-family:Arial,Helvetica,sans-serif;color:#111;font-size:13px;}'
      + '.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1a1206;padding-bottom:10px;margin-bottom:16px;}'
      + '.co{text-align:right;font-size:11px;color:#333;line-height:1.5;}'
      + 'h1{font-size:20px;margin:0 0 2px;} .ponum{font-size:15px;font-weight:bold;color:#a3720a;}'
      + '.row{margin:7px 0;} .lbl{display:inline-block;width:130px;font-weight:bold;} .val{display:inline-block;border-bottom:1px solid #333;min-width:220px;padding:0 5px;}'
      + 'table{width:100%;border-collapse:collapse;margin-top:14px;} th,td{border:1px solid #999;padding:5px 7px;font-size:12px;text-align:left;} th{background:#f0f0f0;}'
      + '.foot{text-align:center;font-style:italic;font-size:10px;color:#555;margin-top:36px;}'
      + '</style></head><body onload="setTimeout(function(){window.print();},300)">'
      + '<div class="hdr"><img src="/ddd-logo-black.png" alt="Dakota Directional Drilling" style="height:60px">'   // #FLEET_LOGO_BW_V1
      + '<div class="co"><b>Dakota Utility Contractors</b><br>2501 S. Oak Grove Rd.<br>Ennis, TX 75119<br>Office: (972) 875-6344 &middot; dakotautility.com</div></div>'
      + '<h1>' + U(title) + '</h1>' + (rec.po ? ('<div class="ponum">PO ' + U(rec.po) + '</div>') : '')
      + '<div class="row"><span class="lbl">Date:</span><span class="val">' + U(fmtDate(String(rec.createdAt||'').slice(0,10))) + '</span></div>'
      + '<div class="row"><span class="lbl">Requested by:</span><span class="val">' + U(rec.createdBy) + '</span></div>'
      + (rec.to ? ('<div class="row"><span class="lbl">To:</span><span class="val">' + U(rec.to) + '</span></div>') : '')
      + '<table><thead><tr><th>Qty</th><th>UofM</th><th>Item</th></tr></thead><tbody>' + itemRows + '</tbody></table>'
      + (rec.note ? ('<div class="row" style="margin-top:14px"><span class="lbl">Note:</span><span class="val" style="min-width:400px">' + U(rec.note) + '</span></div>') : '')
      + '<div class="foot">Dakota Utility Contractors &middot; #WEAREDAKOTA &middot; dakotautility.com</div>'
      + '</body></html>';
    win.document.write(html); win.document.close();
  };
  // #FLEET_CHEM_ORDERSEND_V1 (2026-09-28) — Bill: the mailto+print flow queued an unsent Outlook
  // draft with no attachment and made him manually Print > Save as PDF > attach it himself. Same
  // fix Daily Ops already uses (#DAILYOPS_SUBMITTEDBY_V1 in App.js/PMDashboard.js): submit straight
  // from the system via /api/send-email (Microsoft Graph, server-side) to the distribution list,
  // order details right in the HTML body (letterhead look via the site's own /ddd-logo.png at an
  // absolute URL — the same technique SafetyModule's #SAFETY_EMAIL_V1 already uses, since a relative
  // path won't render in a mail client), and a "Submitted by" line at the bottom, same as Dailies.
  // printOrder() above stays available as a manual Print button on Ordered/History for anyone who
  // still wants a paper copy — it's just no longer forced into every send.
  const _nameFromUpn = (u) => {
    const local = String(u || '').split('@')[0];
    if (!local) return '';
    return local.split(/[._-]+/).filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };
  const escE = (v) => String(v==null?'':v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const orderEmailHtml = (rec, title) => {
    const logoUrl = ((typeof window !== 'undefined' && window.location) ? window.location.origin : '') + '/ddd-logo-black.png';   // #FLEET_LOGO_BW_V1
    const itemRows = (rec.items||[]).map(it => '<tr><td style="padding:5px 8px;border:1px solid #ccc">' + escE(it.qty) + '</td><td style="padding:5px 8px;border:1px solid #ccc">' + escE(it.uom||'') + '</td><td style="padding:5px 8px;border:1px solid #ccc">' + escE(it.name) + '</td></tr>').join('');
    const submittedBy = _nameFromUpn(rec.createdBy) || String(rec.createdBy||'') || '—';
    return '<div style="font-family:Arial,Helvetica,sans-serif;color:#111;font-size:13px">'
      + '<div style="display:flex;align-items:center;gap:12px;border-bottom:2px solid #1a1206;padding-bottom:10px;margin-bottom:14px"><img src="' + logoUrl + '" alt="Dakota Directional Drilling" style="height:48px" onerror="this.style.display=\'none\'"><div style="font-size:11px;color:#333;line-height:1.5"><b>Dakota Utility Contractors</b><br>2501 S. Oak Grove Rd. &middot; Ennis, TX 75119<br>Office: (972) 875-6344 &middot; dakotautility.com</div></div>'
      + '<h2 style="margin:0 0 4px;font-size:18px">' + escE(title) + '</h2>'
      + (rec.po ? ('<div style="font-size:13px;font-weight:bold;color:#a3720a;margin-bottom:6px">PO ' + escE(rec.po) + '</div>') : '')
      + '<div style="font-size:12px;color:#555;margin-bottom:10px">' + escE(fmtDate(String(rec.createdAt||'').slice(0,10))) + '</div>'
      + '<table style="width:100%;max-width:520px;border-collapse:collapse"><thead><tr><th style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;text-align:left">Qty</th><th style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;text-align:left">UofM</th><th style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;text-align:left">Item</th></tr></thead><tbody>' + itemRows + '</tbody></table>'
      + (rec.note ? ('<div style="margin-top:12px;font-size:12px"><b>Note:</b> ' + escE(rec.note) + '</div>') : '')
      + '<p style="color:#888;font-size:11.5px;margin-top:22px">Submitted by: ' + escE(submittedBy) + '</p>'
      + '<p style="color:#888;font-size:11.5px;margin:2px 0 0">#WEAREDAKOTA</p>'
      + '</div>';
  };
  const startOrderBuild = (code) => {
    const rows = needsOrderByCode[code] || [];
    const qtys = {}; rows.forEach(r => { qtys[r.id] = num(r.reorderPt) || ''; });
    setOrderBuild({ code, qtys, note:'', po:'' });
  };
  const sendOrder = async () => {
    if (!orderBuild) return;
    const rows = (needsOrderByCode[orderBuild.code] || []).filter(r => num(orderBuild.qtys[r.id]) > 0);
    if (!rows.length) { alert('Enter a quantity for at least one item.'); return; }
    const distList = orderBuild.code === 'V CSGN' ? vermeerDist : orderBuild.code === 'D CSGN' ? ditchwitchDist : dddDist;
    const to = distList.filter(Boolean);
    if (!to.length) { alert(`No ${vendorName(orderBuild.code)} recipients set yet — click Recipients above and add at least one email first.`); return; }
    const items = rows.map(r => ({ productId:r.id, name:r.name, uom:r.uom||'', qty:num(orderBuild.qtys[r.id]) }));
    const rec = { id: Date.now().toString(36)+Math.random().toString(36).slice(2,7), code: orderBuild.code,
      status:'ordered', items, note: orderBuild.note||'', po: orderBuild.po||'',
      createdAt: new Date().toISOString(), createdBy: upn || String(userRole||'') };
    await persistOrders([ ...(orders||[]), rec ]);
    const isCsgn = orderBuild.code === 'V CSGN' || orderBuild.code === 'D CSGN';
    const label = isCsgn ? 'Consignment order' : 'Order request';
    const title = `${label} — ${vendorName(orderBuild.code)}`;
    setOrderBuild(null);
    setOrderSending(true);
    setOrderSendMsg('Sending…');
    try {
      const data = await sendVendorEmail({ to, subject: `${title} — ${iso(new Date())}`, html: orderEmailHtml(rec, title) });   // #EMAIL_SEND_AS_USER_V1
      if (data && data.ok) setOrderSendMsg(`✓ Sent to ${data.count||to.length} recipient${(data.count||to.length)===1?'':'s'}${sentNote(data)}`);
      else setOrderSendMsg('Saved, but the email failed to send: ' + ((data && (data.message||data.error)) || 'unknown error') + ' — use Print on this order under Ordered to send it manually.');
    } catch (err) {
      setOrderSendMsg('Saved, but the email failed to send: ' + (err && err.message ? err.message : 'network error') + ' — use Print on this order under Ordered to send it manually.');
    }
    setOrderSending(false);
    setTimeout(()=>setOrderSendMsg(''), 9000);
  };
  // #FLEET_CHEM_BOBORDER_V1 — free-form DCI/DrilRite order to Bob Macy. Not tied to Core Inventory
  // product IDs (typed lines), same printable-letterhead + mailto pattern as sendOrder above, and
  // lands in the same `orders` array so it shows up in Ordered/History alongside V/D CSGN orders.
  const addBobLine = () => setBobBuild(b => ({ ...b, lines:[ ...b.lines, { desc:'', qty:'', uom:'' } ] }));
  const removeBobLine = (i) => setBobBuild(b => ({ ...b, lines: b.lines.filter((_,ix)=>ix!==i) }));
  const setBobLine = (i, patch) => setBobBuild(b => ({ ...b, lines: b.lines.map((l,ix)=> ix===i ? { ...l, ...patch } : l) }));
  const sendBobOrder = async () => {
    const lines = bobBuild.lines.filter(l => String(l.desc||'').trim() && num(l.qty) > 0);
    if (!lines.length) { alert('Enter at least one item with a description and quantity.'); return; }
    const to = dddDist.filter(Boolean);
    if (!to.length) { alert('No DDD recipients set yet — click Recipients above and add at least one email first.'); return; }
    const items = lines.map(l => ({ name: l.desc.trim(), qty: num(l.qty), uom: l.uom||'' }));
    const rec = { id: Date.now().toString(36)+Math.random().toString(36).slice(2,7), code:'Fleet Order',
      status:'ordered', items, note: bobBuild.note||'', po: bobBuild.po||'', to: to.join(','),
      createdAt: new Date().toISOString(), createdBy: upn || String(userRole||'') };
    await persistOrders([ ...(orders||[]), rec ]);
    const title = 'Order request — Fleet (DCI / DrilRite)';
    setBobBuild({ lines:[{ desc:'', qty:'', uom:'' }], po:'', note:'' });
    setOrderSending(true);
    setOrderSendMsg('Sending…');
    try {
      const data = await sendVendorEmail({ to, subject: `Fleet Order Request — ${iso(new Date())}`, html: orderEmailHtml(rec, title) });   // #EMAIL_SEND_AS_USER_V1
      if (data && data.ok) setOrderSendMsg(`✓ Sent to ${data.count||to.length} recipient${(data.count||to.length)===1?'':'s'}${sentNote(data)}`);
      else setOrderSendMsg('Saved, but the email failed to send: ' + ((data && (data.message||data.error)) || 'unknown error') + ' — use Print on this order under Ordered to send it manually.');
    } catch (err) {
      setOrderSendMsg('Saved, but the email failed to send: ' + (err && err.message ? err.message : 'network error') + ' — use Print on this order under Ordered to send it manually.');
    }
    setOrderSending(false);
    setTimeout(()=>setOrderSendMsg(''), 9000);
  };
  const markOrderReceived = async (o) => {
    if (!window.confirm(`Mark this ${vendorName(o.code)} order received?\n\nThis just clears it from the queue — use +Rcv on Core Inventory to actually add the stock once it physically arrives.`)) return;
    await persistOrders((orders||[]).map(x => x.id===o.id ? { ...x, status:'received', receivedAt:new Date().toISOString() } : x));
  };
  const cancelOrder = async (o) => {
    if (!window.confirm(`Cancel this ${vendorName(o.code)} order? It'll move to History and the items become eligible for Needs Order again.`)) return;
    await persistOrders((orders||[]).map(x => x.id===o.id ? { ...x, status:'cancelled', cancelledAt:new Date().toISOString() } : x));
  };

  return (
    <Fragment>
      {/* #FLEET_CHEM_PIVOT_V1 — Dispatch dashboard vs. the Month/Product/Supervisor Pivot report */}
      <div style={{ display:'flex', gap:8, marginBottom:14 }}>
        <button onClick={()=>setChemView('dash')} style={{ ...btn, borderRadius:20, ...(chemView==='dash' ? { background:F.accent, color:'#04121C' } : {}) }}>Dispatch</button>
        <button onClick={()=>setChemView('pivot')} style={{ ...btn, borderRadius:20, ...(chemView==='pivot' ? { background:F.accent, color:'#04121C' } : {}) }}>Pivot</button>
      </div>

      {chemView === 'dash' && (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px,1fr))', gap:16, alignItems:'start' }}>

      {/* ── LEFT — Core Inventory: live, sortable, condensed ─────────────── */}
      <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px', position:'relative' }}>
        <RivetCorners />
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6, flexWrap:'wrap' }}>
          <div style={{ fontSize:12.5, fontWeight:900 }}>Core Inventory</div>
          <div style={{ fontSize:10.5, color:F.muted }}>{(inv||[]).length}</div>
          <div style={{ flex:1 }} />
          <div style={{ fontSize:10.5, color:F.muted }}>Value on hand <b style={{ color:F.green }}>{money2(totalValue)}</b>{consignedValue>0 && <span> · + {money2(consignedValue)} consigned (not owned, not counted)</span>}</div>
          {canEdit && <button onClick={()=>setInvEdit({ name:'', itemNo:'', uom:'', cost:'', qty:'' })} style={{ ...btn, padding:'3px 9px', fontSize:11, background:F.accent, color:'#04121C' }}>+ Add</button>}
        </div>
        <input value={invQ} onChange={e=>setInvQ(e.target.value)} placeholder="Search…" style={{ ...inp, marginBottom:8 }} />

        {invEdit && (
          <div style={{ background:F.cardHi, border:`1px solid ${F.accent}66`, borderRadius:10, padding:'10px 12px', marginBottom:10 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              <input value={invEdit.name||''} onChange={e=>setInvEdit(d=>({ ...d, name:e.target.value }))} placeholder="Product" style={inp} />
              <input value={invEdit.itemNo||''} onChange={e=>setInvEdit(d=>({ ...d, itemNo:e.target.value }))} placeholder="Item #" style={inp} />
              <input value={invEdit.uom||''} onChange={e=>setInvEdit(d=>({ ...d, uom:e.target.value }))} placeholder="UofM" style={inp} />
              <input value={invEdit.cost==null?'':invEdit.cost} onChange={e=>setInvEdit(d=>({ ...d, cost:e.target.value }))} placeholder="Cost/UofM" style={inp} />
              <input value={invEdit.qty==null?'':invEdit.qty} onChange={e=>setInvEdit(d=>({ ...d, qty:e.target.value }))} placeholder="On hand qty" style={inp} />
              {/* #FLEET_CHEM_CSGN_V1 — Code: blank = DUC-owned stock, or a consignment tag (free-
                  type, these four are just suggestions). Reorder pt only matters once a code is set
                  — it's what the Orders panel's Needs Order tab watches for V CSGN/D CSGN. */}
              <AutoCompleteInput options={CSGN_CODES} value={invEdit.code||''} onChange={v=>setInvEdit(d=>({ ...d, code:v }))} style={inp} placeholder="Code (blank = owned)" />
              <input value={invEdit.reorderPt==null?'':invEdit.reorderPt} onChange={e=>setInvEdit(d=>({ ...d, reorderPt:e.target.value }))} placeholder="Min on hand (reorder trigger)" style={inp} />
            </div>
            <div style={{ display:'flex', gap:6, marginTop:8 }}>
              <button onClick={saveInvItem} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
              <button onClick={()=>setInvEdit(null)} style={btn}>Close</button>
            </div>
          </div>
        )}

        <div style={{ border:`1px solid ${F.line}`, borderRadius:10, overflow:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th onClick={()=>clickSort('name')} style={{ ...th, cursor:'pointer' }}>Product{sortArrow('name')}</th>
              <th onClick={()=>clickSort('code')} style={{ ...th, cursor:'pointer' }}>Code{sortArrow('code')}</th>
              <th style={th}>UofM</th>
              <th style={{ ...th, textAlign:'right' }}>Per Unit</th>
              <th style={{ ...th, textAlign:'right' }} title="Qty currently on order, awaiting arrival — visual only, not counted in On hand or Value">Ordered</th>
              <th style={{ ...th, textAlign:'right', color:F.violet }} title="Min on hand — drops at/below this build an order in Needs Order (coded items) or just flag amber here (no vendor code)">Min</th>
              <th onClick={()=>clickSort('onhand')} style={{ ...th, cursor:'pointer', textAlign:'right' }}>On hand{sortArrow('onhand')}</th>
              <th onClick={()=>clickSort('value')} style={{ ...th, cursor:'pointer', textAlign:'right' }}>Value{sortArrow('value')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {!sortedInv.length
                ? <tr><td style={td} colSpan={9}>Nothing yet — add your first product.</td></tr>
                : sortedInv.map(r => (
                  <tr key={r.id} style={{ borderTop:`1px solid ${F.line}` }}>
                    <td style={td}>
                      <div style={{ fontWeight:800 }}>{r.name}</div>
                      {r.itemNo && <div style={{ fontSize:10, color:F.muted }}>{r.itemNo}</div>}
                    </td>
                    <td style={{ ...td, color: isConsigned(r)?F.gold:F.muted, fontWeight: isConsigned(r)?800:400 }}>
                      {r.code || '—'}
                    </td>
                    <td style={{ ...td, color:F.muted }}>{r.uom || '—'}</td>
                    <td style={{ ...td, textAlign:'right' }}><ChemMoney v={r.cost} dim /></td>
                    <td style={{ ...td, textAlign:'right', fontWeight:800, color:F.red }}>{orderedQtyByProduct[r.id] ? orderedQtyByProduct[r.id] : <span style={{ color:F.muted, fontWeight:400 }}>—</span>}</td>
                    <td style={{ ...td, textAlign:'right' }}>
                      {canEdit ? (
                        <input type="number" defaultValue={r.reorderPt||''} placeholder="—"
                          onBlur={e=>saveMin(r, e.target.value)}
                          style={{ ...inp, width:56, textAlign:'right', color:F.violet, fontWeight:800, padding:'3px 6px' }} />
                      ) : (
                        <span style={{ color:F.violet, fontWeight:800 }}>{num(r.reorderPt)>0 ? num(r.reorderPt) : '—'}</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign:'right', fontWeight:800, color: num(r.qty)<=0 ? F.red : (num(r.reorderPt)>0 && num(r.qty)<=num(r.reorderPt)) ? F.amber : F.text }}>{num(r.qty)}</td>
                    <td style={{ ...td, textAlign:'right' }}><ChemMoney v={num(r.qty)*num(r.cost)} color={F.green} /></td>
                    <td style={{ ...td, whiteSpace:'nowrap', textAlign:'right' }}>
                      {canEdit && <button onClick={()=>receiveInv(r)} style={{ ...btn, padding:'2px 6px', fontSize:10 }}>+Rcv</button>}{' '}
                      {canEdit && <button onClick={()=>setInvEdit({ ...r, _orig:r })} style={{ ...btn, padding:'2px 6px', fontSize:10 }}>Edit</button>}{' '}
                      {canDelete && <button onClick={()=>delInvItem(r)} style={{ ...btn, padding:'2px 6px', fontSize:10, color:F.red }}>×</button>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── RIGHT — Dispatch feed (majority) + Spend summary (smaller, bottom) ───────── */}
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <button onClick={()=>setWkStart(iso(addDays(new Date(wkStart+'T12:00:00'), -7)))} style={{ ...btn, padding:'3px 9px' }}>◀</button>
          <div style={{ fontSize:13, fontWeight:900 }}>WK {wkNum} · Week ending {fmtMD(new Date(wkDates[6]+'T12:00:00'))}</div>
          <button onClick={()=>setWkStart(iso(addDays(new Date(wkStart+'T12:00:00'), 7)))} style={{ ...btn, padding:'3px 9px' }}>▶</button>
          {wkStart !== chemMondayISO() && <button onClick={()=>setWkStart(chemMondayISO())} style={{ ...btn, padding:'3px 9px', fontSize:11 }}>This week</button>}
        </div>

        <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', position:'relative' }}>
          <RivetCorners />
          <div style={{ fontSize:13, fontWeight:900, marginBottom:8 }}>Dispatch</div>

          {!canEdit && <div style={{ fontSize:11.5, color:F.muted, marginBottom:10 }}>View only.</div>}

          {canEdit && (
            <div style={{ background:F.cardHi, border:`1px solid ${F.line}`, borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
              {/* #FLEET_CHEM_GRIDFIX_V1 -- CSS Grid classic: a track sized minmax(120px,1fr) still won't
                  shrink a child below its CONTENT's own minimum width (grid items default to
                  min-width:auto), and a native type="date" input's rendered value ("Sep 2, 2026")
                  can't shrink that far on mobile -- so the Date box was overflowing out of its own
                  column and eating into Supervisor's. minWidth:0 on every grid item is the standard
                  fix: it tells each cell it's allowed to shrink to the track width instead of its
                  content's natural size. */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px,1fr))', gap:8 }}>
                <div style={{ minWidth:0 }}>
                  <div style={lbl}>Date</div>
                  <input type="date" value={form.date||todayISO} onChange={e=>setForm(f=>({ ...f, date:e.target.value }))} style={inp} />
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={lbl}>Supervisor</div>
                  <AutoCompleteInput options={supOptions} value={form.sup} onChange={v=>setForm(f=>({ ...f, sup:v }))} style={inp} />
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={lbl}>Job / DUC</div>
                  <AutoCompleteInput options={ducOptions} value={form.duc} onChange={v=>setForm(f=>({ ...f, duc:v.toUpperCase(), ducAuto:false }))} style={inp} placeholder="E4611809" />
                  {form.sup && supMatch && supMatch.verdict==='ok' && supMatch.fallback && (
                    <div style={{ fontSize:10.5, color:F.gold, marginTop:3 }}>Auto-filled from {form.sup}'s last Daily on {fmtMD(new Date(supMatch.matchedDate+'T12:00:00'))} -- confirm if this is a new job.</div>
                  )}
                  {form.sup && supMatch && supMatch.verdict==='conflict' && (
                    <div style={{ fontSize:10.5, color:F.amber, marginTop:3 }}>{form.sup} posted to {supMatch.ducs.length} jobs on Daily today -- pick the right one.</div>
                  )}
                  {form.sup && supMatch && supMatch.verdict==='missing' && !form.duc && (
                    <div style={{ fontSize:10.5, color:F.muted, marginTop:3 }}>No Daily activity found for {form.sup} yet -- enter the Job/DUC manually.</div>
                  )}
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={lbl}>Product</div>
                  <select value={form.productId} onChange={e=>setForm(f=>({ ...f, productId:e.target.value }))} style={inp}>
                    <option value="">Pick…</option>
                    {(inv||[]).map(r => <option key={r.id} value={r.id}>{r.name}{r.uom?` (${r.uom})`:''} — {num(r.qty)} on hand</option>)}
                  </select>
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={lbl}>Qty</div>
                  <input value={form.qty} onChange={e=>setForm(f=>({ ...f, qty:e.target.value }))} style={inp} />
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={lbl}>Issue / Return</div>
                  <select value={form.kind} onChange={e=>setForm(f=>({ ...f, kind:e.target.value }))} style={inp}>
                    <option value="issue">Issue — going out</option>
                    <option value="return">Return — coming back</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop:8 }}>
                <div style={lbl}>Note (optional)</div>
                <input value={form.note} onChange={e=>setForm(f=>({ ...f, note:e.target.value }))} style={inp} />
              </div>
              {pendingOvr && (
                <div style={{ marginTop:10, background:`${F.amber}18`, border:`1px solid ${F.amber}66`, borderRadius:8, padding:'8px 10px', fontSize:12 }}>
                  <b style={{ color:F.amber }}>{pendingOvr.duc}</b> — {pendingOvr.g.msg}. Charge to it anyway?
                  <div style={{ marginTop:6, display:'flex', gap:8 }}>
                    <button onClick={()=>submit(true)} style={{ ...btn, background:F.amber, color:'#2A1B00' }}>Override & save</button>
                    <button onClick={()=>setPendingOvr(null)} style={btn}>Cancel</button>
                  </div>
                </div>
              )}
              {!pendingOvr && <button onClick={()=>submit(false)} style={{ ...btn, marginTop:10, background:F.accent, color:'#04121C' }}>+ Log it</button>}
            </div>
          )}


          <div style={{ fontSize:11, fontWeight:800, color:F.muted, textTransform:'uppercase', letterSpacing:.4, marginBottom:6 }}>
            This week's activity{weekEntries.length ? ` (${weekEntries.length})` : ''}
          </div>
          {!weekEntries.length ? <div style={{ fontSize:12, color:F.muted }}>Nothing logged this week yet.</div> : (
            <Fragment>
              <div style={{ border:`1px solid ${F.line}`, borderRadius:10, overflow:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr><th style={th}>Date</th><th style={th}>Sup</th><th style={th}>Job</th><th style={th}>Product</th><th style={th}>Qty</th><th style={th}>$</th><th style={th}>Status</th><th style={th}></th></tr></thead>
                  <tbody>
                    {feedRows.map(e => (rowEdit && rowEdit.id === e.id) ? (
                      <tr key={e.id} style={{ borderTop:`1px solid ${F.line}`, background:`${F.accent}12` }}>
                        <td style={td}><input type="date" value={rowEdit.date} min={chemMondayISO()} max={iso(addDays(new Date(chemMondayISO()+'T12:00:00'), 6))} onChange={ev=>setRowEdit(r=>({ ...r, date:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:130 }} /></td>
                        <td style={td}><input value={rowEdit.sup} list="chem-edit-sups" onChange={ev=>setRowEdit(r=>({ ...r, sup:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:90 }} /></td>
                        <td style={td}><input value={rowEdit.duc} list="chem-edit-ducs" onChange={ev=>setRowEdit(r=>({ ...r, duc:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:105 }} /></td>
                        <td style={td}>
                          <select value={rowEdit.productId} onChange={ev=>setRowEdit(r=>({ ...r, productId:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:150 }}>
                            {[...(inv||[])].sort((a,b)=>String(a.name).localeCompare(String(b.name))).map(r => <option key={r.id} value={r.id}>{r.name}{r.uom ? ` (${r.uom})` : ''}</option>)}
                          </select>
                          <select value={rowEdit.kind} onChange={ev=>setRowEdit(r=>({ ...r, kind:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:150, marginTop:4 }}>
                            <option value="issue">Issue — going out</option>
                            <option value="return">Return — coming back</option>
                          </select>
                        </td>
                        <td style={td}><input value={rowEdit.qty} inputMode="decimal" onChange={ev=>setRowEdit(r=>({ ...r, qty:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:60 }} /></td>
                        <td style={td} colSpan={2}><input value={rowEdit.note} placeholder="Note / reason" onChange={ev=>setRowEdit(r=>({ ...r, note:ev.target.value }))} style={{ ...inp, padding:'3px 5px', width:130 }} /></td>
                        <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                          <button onClick={saveRowEdit} style={{ ...btn, padding:'3px 8px', fontSize:10.5, background:F.accent, color:'#04121C' }}>Save</button>{' '}
                          <button onClick={()=>setRowEdit(null)} style={{ ...btn, padding:'3px 8px', fontSize:10.5 }}>Cancel</button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={e.id} style={{ borderTop:`1px solid ${F.line}` }}>
                        <td style={td}>{fmtMD(new Date(e.date+'T12:00:00'))}</td>
                        <td style={td}>{e.sup}</td>
                        <td style={td}>{e.duc}{e.jobCity ? <span style={{ color:F.muted }}> · {e.jobCity}</span> : null}</td>
                        <td style={td}>{e.kind==='return' ? '↩ ' : ''}{e.product}</td>
                        <td style={td}>{e.qty}</td>
                        <td style={{ ...td, fontWeight:800, color: e.kind==='return' ? F.green : F.text }}>{e.kind==='return'?'−':''}{money2(e.totalCost)}</td>
                        <td style={td}>{e.ovr ? <span style={{ color:F.amber, fontWeight:800 }}>✓ OVR</span> : <span style={{ color:F.green, fontWeight:800 }}>✓</span>}
                          {Array.isArray(e.edits) && e.edits.length > 0 && (() => { const last = e.edits[e.edits.length-1]; const b = last.before||{};
                            return <span title={`Edited by ${last.by} ${new Date(last.at).toLocaleString()}\nWas: ${b.date} · ${b.sup} · ${b.duc} · ${b.product} · ${b.qty} (${b.kind})`} style={{ marginLeft:6, color:F.amber, fontSize:10.5, fontWeight:800, cursor:'help' }}>✎ edited</span>; })()}
                        </td>
                        <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                          {canEditWeekLine(e) && <button onClick={()=>startRowEdit(e)} disabled={!!rowEdit} style={{ ...btn, padding:'3px 7px', fontSize:10.5, opacity: rowEdit ? .4 : 1 }}>Edit</button>}{' '}
                          {canRemoveLine(e) && <button onClick={()=>delEntry(e)} style={{ ...btn, padding:'3px 7px', fontSize:10.5, color:F.red }}>Remove</button>}
                          {!canEditWeekLine(e) && !canRemoveLine(e) && String(e.date||'') < chemMondayISO() && <span title="Week closed — locked" style={{ color:F.muted, fontSize:11 }}>🔒</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <datalist id="chem-edit-sups">{supOptions.map(o => <option key={o.value} value={o.value} />)}</datalist>
              <datalist id="chem-edit-ducs">{ducOptions.map(o => <option key={o.value} value={o.value}>{o.sub}</option>)}</datalist>
              {weekEntries.length > FEED_CAP && (
                <button onClick={()=>setFeedExpanded(x=>!x)} style={{ ...btn, marginTop:8, fontSize:11 }}>
                  {feedExpanded ? 'Show less' : `Show all ${weekEntries.length}`}
                </button>
              )}
            </Fragment>
          )}
        </div>

        <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px', position:'relative' }}>
          <RivetCorners />
          <div style={{ fontSize:12, fontWeight:900, marginBottom:8 }}>Dispatched spend</div>
          <div style={{ display:'flex', gap:18, flexWrap:'wrap' }}>
            <div>
              <div style={lbl}>This wk</div>
              <div style={{ fontSize:16, fontWeight:900, color:F.gold }}>{money(weekTotal)}</div>
            </div>
            <div>
              <div style={lbl}>Last wk</div>
              <div style={{ fontSize:14, fontWeight:800, color:F.text }}>{money(lastWeekTotal)}
                {wowPct!=null && <span style={{ fontSize:10.5, color: wowDelta<=0?F.green:F.red, marginLeft:4 }}>{wowDelta<=0?'▼':'▲'}{Math.abs(wowPct).toFixed(0)}%</span>}
              </div>
            </div>
            <div>
              <div style={lbl}>Same wk LY</div>
              <div style={{ fontSize:14, fontWeight:800, color:F.text }}>{money(priorYearTotal)}
                {yoyPct!=null && <span style={{ fontSize:10.5, color: yoyDelta<=0?F.green:F.red, marginLeft:4 }}>{yoyDelta<=0?'▼':'▲'}{Math.abs(yoyPct).toFixed(0)}%</span>}
              </div>
            </div>
          </div>
          {!!topProducts.length && (
            <div style={{ marginTop:10, borderTop:`1px solid ${F.line}`, paddingTop:8 }}>
              <div style={lbl}>Top this week</div>
              {topProducts.map(([name,amt]) => (
                <div key={name} style={{ display:'flex', justifyContent:'space-between', fontSize:11.5, padding:'2px 0' }}>
                  <span style={{ color:F.text }}>{name}</span>
                  <span style={{ fontWeight:800, color: amt<0?F.green:F.text }}>{money(amt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* #FLEET_CHEM_CSGN_V1 (2026-09-22) — Bill: consignment items (V CSGN / D CSGN) need a
            reorder-point-driven queue that auto-populates an emailable order and tracks it as
            "Ordered" so nothing gets ordered twice. Same bottom-right slot as Dispatched spend,
            its own tab strip: Needs Order (auto, editable before sending) / Ordered (sent, awaiting
            arrival — actual stock increase still happens the normal way, via +Rcv on Core
            Inventory once it physically shows up) / History (received or cancelled). */}
        <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px', position:'relative' }}>
          <RivetCorners />
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:12, fontWeight:900 }}>Orders</div>
            <div style={{ flex:1 }} />
            {canEdit && <button onClick={()=>setDistOpen(o=>!o)} title="Who consignment orders go to" style={{ ...btn, padding:'3px 9px', fontSize:11, color:F.muted }}>Recipients ({vermeerDist.length+ditchwitchDist.length+dddDist.length})…</button>}
          </div>
          {distOpen && (
            <div style={{ background:F.cardHi, border:`1px solid ${F.line}`, borderRadius:8, padding:'10px 12px', marginBottom:10 }}>
              <DistChips title="Vermeer" list={vermeerDist} onAdd={emails=>addDist(VDIST_KEY,setVermeerDist,vermeerDist,emails)} onRemove={email=>removeDist(VDIST_KEY,setVermeerDist,vermeerDist,email)} btn={btn} inp={inp} F={F} />
              <DistChips title="DitchWitch" list={ditchwitchDist} onAdd={emails=>addDist(DDIST_KEY,setDitchwitchDist,ditchwitchDist,emails)} onRemove={email=>removeDist(DDIST_KEY,setDitchwitchDist,ditchwitchDist,email)} btn={btn} inp={inp} F={F} />
              <DistChips title="DDD" list={dddDist} onAdd={emails=>addDist(DDDDIST_KEY,setDddDist,dddDist,emails)} onRemove={email=>removeDist(DDDDIST_KEY,setDddDist,dddDist,email)} btn={btn} inp={inp} F={F} />
            </div>
          )}
          <div style={{ display:'flex', gap:6, marginBottom:10 }}>
            <button onClick={()=>setOrderTab('needs')} style={{ ...btn, padding:'4px 10px', fontSize:11, borderRadius:20, ...(orderTab==='needs' ? { background:F.accent, color:'#04121C' } : {}) }}>Needs Order{needsOrderCount ? ` (${needsOrderCount})` : ''}</button>
            <button onClick={()=>setOrderTab('ordered')} style={{ ...btn, padding:'4px 10px', fontSize:11, borderRadius:20, ...(orderTab==='ordered' ? { background:F.accent, color:'#04121C' } : {}) }}>Ordered{openOrders.length ? ` (${openOrders.length})` : ''}</button>
            <button onClick={()=>setOrderTab('history')} style={{ ...btn, padding:'4px 10px', fontSize:11, borderRadius:20, ...(orderTab==='history' ? { background:F.accent, color:'#04121C' } : {}) }}>History</button>
            <button onClick={()=>setOrderTab('bob')} style={{ ...btn, padding:'4px 10px', fontSize:11, borderRadius:20, ...(orderTab==='bob' ? { background:F.accent, color:'#04121C' } : {}) }}>Order Bob</button>
          </div>
          {(orderSending || orderSendMsg) && (
            <div style={{ fontSize:11, fontWeight:700, marginBottom:10, color: orderSending ? F.muted : (orderSendMsg.startsWith('✓') ? F.green : F.amber) }}>{orderSending ? 'Sending…' : orderSendMsg}</div>
          )}

          {orderTab === 'needs' && (
            !Object.keys(needsOrderByCode).length
              ? <div style={{ fontSize:11.5, color:F.green, fontWeight:700 }}>✓ Nothing at or below its reorder point right now.</div>
              : Object.entries(needsOrderByCode).map(([code, rows]) => (
                <div key={code} style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11.5, fontWeight:800, color:F.gold, marginBottom:4 }}>{vendorName(code)} ({code}) — {rows.length} item{rows.length===1?'':'s'} low</div>
                  {orderBuild && orderBuild.code === code ? (
                    <div style={{ background:F.cardHi, border:`1px solid ${F.accent}66`, borderRadius:8, padding:'8px 10px' }}>
                      {rows.map(r => (
                        <div key={r.id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                          <div style={{ flex:1, fontSize:11.5 }}>{r.name} <span style={{ color:F.muted }}>({num(r.qty)} on hand{r.uom?`, ${r.uom}`:''})</span></div>
                          <input value={orderBuild.qtys[r.id]==null?'':orderBuild.qtys[r.id]} onChange={e=>setOrderBuild(b=>({ ...b, qtys:{ ...b.qtys, [r.id]:e.target.value } }))} style={{ ...inp, width:70 }} placeholder="Qty" />
                        </div>
                      ))}
                      <div style={{ display:'flex', gap:6, marginTop:4 }}>
                        <input value={orderBuild.po||''} onChange={e=>setOrderBuild(b=>({ ...b, po:e.target.value }))} placeholder="PO # (optional)" style={{ ...inp, flex:1 }} />
                      </div>
                      <input value={orderBuild.note} onChange={e=>setOrderBuild(b=>({ ...b, note:e.target.value }))} placeholder="Note (optional)" style={{ ...inp, marginTop:4, marginBottom:8 }} />
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={sendOrder} disabled={orderSending} style={{ ...btn, background:F.accent, color:'#04121C', fontSize:11, opacity: orderSending?0.6:1 }}>Build & send order</button>
                        <button onClick={()=>setOrderBuild(null)} style={{ ...btn, fontSize:11 }}>Cancel</button>
                      </div>
                      {!(orderBuild.code === 'V CSGN' ? vermeerDist : orderBuild.code === 'D CSGN' ? ditchwitchDist : dddDist).length && <div style={{ fontSize:10.5, color:F.amber, marginTop:6 }}>No {vendorName(orderBuild.code)} recipients set yet — click Recipients above so the To: line isn't blank.</div>}
                    </div>
                  ) : (
                    <Fragment>
                      {rows.map(r => (
                        <div key={r.id} style={{ fontSize:11.5, color:F.muted, padding:'1px 0' }}>{r.name} — {num(r.qty)} on hand (reorder @ {num(r.reorderPt)})</div>
                      ))}
                      {canEdit && <button onClick={()=>startOrderBuild(code)} style={{ ...btn, marginTop:5, padding:'3px 9px', fontSize:11 }}>Build order…</button>}
                    </Fragment>
                  )}
                </div>
              ))
          )}

          {orderTab === 'ordered' && (
            !openOrders.length
              ? <div style={{ fontSize:11.5, color:F.muted }}>Nothing on order right now.</div>
              : openOrders.map(o => (
                <div key={o.id} style={{ borderTop:`1px solid ${F.line}`, padding:'6px 0' }}>
                  <div style={{ fontSize:11.5, fontWeight:800 }}>{vendorName(o.code)} — {fmtDate(o.createdAt.slice(0,10))} <span style={{ fontWeight:400, color:F.muted }}>({o.items.length} item{o.items.length===1?'':'s'})</span></div>
                  {o.items.map(it => <div key={it.productId} style={{ fontSize:11, color:F.muted }}>{it.qty} {it.uom||''} {it.name}</div>)}
                  <div style={{ display:'flex', gap:6, marginTop:4 }}>
                    <button onClick={()=>printOrder(o)} style={{ ...btn, padding:'2px 8px', fontSize:10.5, color:F.muted }}>Print</button>
                    {canEdit && <button onClick={()=>markOrderReceived(o)} style={{ ...btn, padding:'2px 8px', fontSize:10.5, color:F.green }}>Mark received</button>}
                    {canEdit && <button onClick={()=>cancelOrder(o)} style={{ ...btn, padding:'2px 8px', fontSize:10.5, color:F.red }}>Cancel</button>}
                  </div>
                </div>
              ))
          )}

          {orderTab === 'history' && (
            !orderHistory.length
              ? <div style={{ fontSize:11.5, color:F.muted }}>Nothing here yet.</div>
              : orderHistory.map(o => (
                <div key={o.id} style={{ borderTop:`1px solid ${F.line}`, padding:'6px 0' }}>
                  <div style={{ fontSize:11.5, fontWeight:800 }}>{vendorName(o.code)} — {fmtDate(o.createdAt.slice(0,10))} <span style={{ fontWeight:400, color: o.status==='cancelled'?F.red:F.green }}>{o.status}</span></div>
                  {o.items.map(it => <div key={it.productId} style={{ fontSize:11, color:F.muted }}>{it.qty} {it.uom||''} {it.name}</div>)}
                  <button onClick={()=>printOrder(o)} style={{ ...btn, padding:'2px 8px', fontSize:10.5, color:F.muted, marginTop:4 }}>Print</button>
                </div>
              ))
          )}

          {/* #FLEET_CHEM_BOBORDER_V1 — free-form order to Bob Macy for DCI/DrilRite (or anything
              else) — typed lines, not tied to a Core Inventory product. Same print-letterhead +
              mailto pattern, lands in the same orders/History as the V/D CSGN flow. */}
          {orderTab === 'bob' && (
            <div>
              <div style={{ fontSize:11, color:F.muted, marginBottom:8 }}>Ad-hoc order to Bob Macy (Fleet Manager) — DCI, DrilRite, or anything else not yet tracked on Core Inventory. Free-typed for now.</div>
              {canEdit ? (
                <div style={{ background:F.cardHi, border:`1px solid ${F.accent}66`, borderRadius:8, padding:'8px 10px' }}>
                  {bobBuild.lines.map((l,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
                      <input value={l.desc} onChange={e=>setBobLine(i,{ desc:e.target.value })} placeholder="Item / description" style={{ ...inp, flex:2 }} />
                      <input value={l.qty} onChange={e=>setBobLine(i,{ qty:e.target.value })} placeholder="Qty" style={{ ...inp, width:60 }} />
                      <input value={l.uom} onChange={e=>setBobLine(i,{ uom:e.target.value })} placeholder="UofM" style={{ ...inp, width:70 }} />
                      {bobBuild.lines.length > 1 && <button onClick={()=>removeBobLine(i)} style={{ ...btn, padding:'2px 6px', fontSize:10, color:F.red }}>×</button>}
                    </div>
                  ))}
                  <button onClick={addBobLine} style={{ ...btn, padding:'3px 9px', fontSize:11, marginBottom:8 }}>+ Add line</button>
                  <input value={bobBuild.po} onChange={e=>setBobBuild(b=>({ ...b, po:e.target.value }))} placeholder="PO # (optional)" style={{ ...inp, marginBottom:6 }} />
                  <input value={bobBuild.note} onChange={e=>setBobBuild(b=>({ ...b, note:e.target.value }))} placeholder="Note (optional)" style={{ ...inp, marginBottom:8 }} />
                  <button onClick={sendBobOrder} disabled={orderSending} style={{ ...btn, background:F.accent, color:'#04121C', fontSize:11, opacity: orderSending?0.6:1 }}>Build & send to Bob</button>
                </div>
              ) : <div style={{ fontSize:11.5, color:F.muted }}>View only.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
      )}

      {chemView === 'pivot' && (
        <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', position:'relative' }}>
          <RivetCorners />
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:10 }}>
            <div style={{ fontSize:13, fontWeight:900 }}>Chemicals Pivot</div>
            <div style={{ fontSize:10.5, color:F.muted }}>Month → Product → Supervisor/Job · net of returns</div>
            <div style={{ flex:1 }} />
            <div style={{ fontSize:11, color:F.muted }}>Grand total <b style={{ color:F.gold }}>{money(pivotGrandTotal)}</b></div>
            <button onClick={downloadPivotCsv} style={{ ...btn, background:F.accent, color:'#04121C' }}>Export CSV</button>
          </div>

          {!pivotMonths.length ? <div style={{ fontSize:12, color:F.muted }}>Nothing logged yet.</div> : (
            <div style={{ border:`1px solid ${F.line}`, borderRadius:10, overflow:'hidden' }}>
              {pivotMonths.map(([ym, mo]) => {
                const monthOpen = pivotOpenMonths.has(ym);
                const products = Object.entries(mo.products).sort((a,b) => b[1].total - a[1].total);
                return (
                  <div key={ym} style={{ borderTop:`1px solid ${F.line}` }}>
                    <div onClick={()=>toggleMonth(ym)} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', cursor:'pointer', background:F.cardHi }}>
                      <span style={{ fontSize:11, color:F.muted, width:14 }}>{monthOpen ? '▾' : '▸'}</span>
                      <span style={{ fontSize:12.5, fontWeight:900, flex:1 }}>{monthLabel(ym)}</span>
                      <span style={{ fontSize:10.5, color:F.muted }}>{products.length} product{products.length===1?'':'s'}</span>
                      <span style={{ fontSize:13, fontWeight:900, color:F.gold, minWidth:90, textAlign:'right' }}>{money(mo.total)}</span>
                    </div>
                    {monthOpen && products.map(([product, pr]) => {
                      const pKey = ym + '|' + product;
                      const prodOpen = pivotOpenProducts.has(pKey);
                      const sups = Object.values(pr.sups).sort((a,b) => b.cost - a.cost);
                      return (
                        <div key={pKey}>
                          <div onClick={()=>toggleProduct(pKey)} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px 7px 30px', cursor:'pointer', borderTop:`1px solid ${F.line}` }}>
                            <span style={{ fontSize:10.5, color:F.muted, width:14 }}>{prodOpen ? '▾' : '▸'}</span>
                            <span style={{ fontSize:12, fontWeight:800, flex:1 }}>{product}{pr.uom ? <span style={{ color:F.muted, fontWeight:400 }}> ({pr.uom})</span> : null}</span>
                            <span style={{ fontSize:10.5, color:F.muted }}>{sups.length} sup/job{sups.length===1?'':'s'}</span>
                            <span style={{ fontSize:12, fontWeight:800, color: pr.total<0?F.green:F.text, minWidth:90, textAlign:'right' }}>{money(pr.total)}</span>
                          </div>
                          {prodOpen && (
                            <div style={{ borderTop:`1px solid ${F.line}` }}>
                              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                <thead><tr><th style={{ ...th, paddingLeft:48 }}>Supervisor</th><th style={th}>Job</th><th style={{ ...th, textAlign:'right' }}>Qty</th><th style={{ ...th, textAlign:'right', paddingRight:12 }}>Cost</th></tr></thead>
                                <tbody>
                                  {sups.map((s,i) => (
                                    <tr key={i} style={{ borderTop:`1px solid ${F.line}` }}>
                                      <td style={{ ...td, paddingLeft:48 }}>{s.sup}</td>
                                      <td style={td}>{s.duc}{s.jobCity ? <span style={{ color:F.muted }}> · {s.jobCity}</span> : null}</td>
                                      <td style={{ ...td, textAlign:'right' }}>{s.qty}</td>
                                      <td style={{ ...td, textAlign:'right', paddingRight:12, fontWeight:800, color: s.cost<0?F.green:F.text }}>{money(s.cost)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
}

// #FLEET_YARD_INVENTORY_V1 (2026-08-27) — Bill: "Jerry will have a few more items such as Chemicals,
// Tooling Inventory." Simple named list — name, qty, unit, location, notes — same shape for both;
// the storage key is the only thing that differs. First pass on purpose (Bill: "build out as we go").
// Chemicals moved to ChemicalInventory above (#FLEET_CHEM_INVENTORY_V1) — Tooling still uses this.
// #FLEET_TIMECLOCK_V1 (2026-09-01) — Bill: pilot a real payroll clock in/out for Jerry (Yard, hourly
// "Manager") as the building block for a later fleet-wide Time & Attendance rollout — "clock in/out
// process that locks when approved and totals for the week... building block we mirror much later for
// others." Real timestamp punches (not typed hours — the existing daily App.js Time & Att tab stays
// as-is for the roles already using it), Mon-Sun week grouping with the same iso/mondayOf/addDays
// helpers Chemicals already uses, an auto Regular/OT split (>40 hrs/wk), and a submit -> approve/
// reopen workflow gated on masterEdit (Owner/Fleet — Bob Macy) so a week is genuinely locked once
// approved, matching Bill's own framing of Bob as the approver since Jerry's Yard already reports
// into Bob's Fleet rollup. "Me" is resolved by matching the logged-in upn against hr-employees, not
// hardcoded to Jerry, so this generalizes cleanly when the rest of Fleet gets their own sign-on later.
const TC_PUNCH_KEY = 'fleet-yard-timeclock-punches';

const TC_WEEK_KEY  = 'fleet-yard-timeclock-weeks';

const tcHours = (inAt, outAt) => { if (!inAt || !outAt) return 0; const ms = new Date(outAt) - new Date(inAt); return ms > 0 ? ms/3600000 : 0; };

const tcFmtT = (ts) => { if (!ts) return '—'; const d = new Date(ts); let h = d.getHours(); const m = pad2(d.getMinutes()); const ap = h>=12?'PM':'AM'; h = h%12; if (h===0) h=12; return `${h}:${m} ${ap}`; };

const tcFmtHrs = (n) => (Math.round((n||0)*100)/100).toFixed(2);

const tcTimeInputVal = (ts) => { if (!ts) return ''; const d = new Date(ts); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };

// #FLEET_TIMECLOCK_EDIT_V1
// #FLEET_TIMECLOCK_PAYCODE_V1 (2026-09-02) — Bill: "REG, OT, VAC, SICK, Ride time totals could be
// broken out to support weekly total." Same pay-code vocabulary as the Payroll pilot being scoped
// alongside this. TIMED codes use the In/Out boxes and compute hours from the clock; every other
// code has no clock to punch (can't "clock in" for a vacation day) so its hours are typed directly.
const TC_PAY_CODES = ['Regular','Ride time','Vacation','Sick','Perdiem','Training'];

const TC_TIMED_CODES = new Set(['Regular','Ride time']);

const tcPunchHours = (p) => TC_TIMED_CODES.has(p.payCode||'Regular') ? tcHours(p.inAt,p.outAt) : num(p.hours);

// #FLEET_TIMECLOCK_PMJOB_V1 (2026-09-02) — Bill's original Payroll-pilot spec, applied to Jerry's
// Time Clock: "PM Code: ... the 4 PMs + Shop ... If Shop is chosen the next field JOB # Defaults to
// Maintenance otherwise they will need to choose the job number ... In Progress or Called in jobs
// but if completed (Pop up confirming job as this is completed), emp can override for the 1st 5
// days, then Override goes to PM until 14 days past completion, then closed." Duplicated from
// App.js's PMS/PM_NAMES rather than imported (FleetModule.js has no shared scope with App.js — same
// convention as the duplicated Celebrations math) — Auger ('AU') deliberately excluded, Bill's own
// count was "the 4 PMs", Shop is the 5th option added separately.
const TC_PM_CODES = ['CT','CJ','PG','RM'];

const TC_PM_NAMES = { CT:'Cory Tate', CJ:'Chris Jurcik', PG:'Perry Graff', RM:'Ruben Mendez' };

const TC_JOB_GRACE_DAYS = 5;

// employee can self-enter a completed job through this many days past completion
const TC_JOB_MGMT_DAYS  = 14;

// a manager/approver can enter one through this many days; beyond = closed (DM approval, not wired)
// Same messy-date parsing as the main module's daysSinceComplete (114 clean ISO dates plus
// stragglers like "CXL"/"HOLD") — duplicated here since that helper is a closure inside the main
// FleetModule component, not reachable from this sibling component.
const tcDaysSinceComplete = (v) => {
  const s0 = String(v == null ? '' : v).trim();
  if (!s0) return null;
  let d = null;
  let m = s0.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) d = new Date(+m[1], +m[2]-1, +m[3]);
  if (!d) { m = s0.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m) { const y = +m[3] < 100 ? 2000 + +m[3] : +m[3]; d = new Date(y, +m[1]-1, +m[2]); } }
  if (!d) { m = s0.match(/^(\d{1,2})\/(\d{1,2})(?!\d)/);
    if (m) d = new Date(new Date().getFullYear(), +m[1]-1, +m[2]); }
  if (!d || isNaN(d.getTime())) return null;
  const t = new Date(); t.setHours(0,0,0,0); d.setHours(0,0,0,0);
  return Math.round((t - d) / 86400000);
};

// #FLEET_SHOPLABOR_WEEKLY_V1 (2026-09-03) -- Bill, Fleet Dash: "Need a weekly Labor Spend Hours by
// Week from WK01 to WK35 (To add as we go) with the youngest WK on the bottom and newest on top...
// Just Shop HRS by Name summary and collapse to Week Total." No live pipe yet ("will get the piping
// done eventually") -- same bridge pattern Fuel . Weekly used before its importer existed: typed or
// pasted in by hand each week. Seeded with Bill's real WK35 numbers (from his own Shop Time pivot,
// total 486.51) so this isn't empty on first load. Storage key: fleet-shoplabor-weekly, shape
// { weeks: [{ id, year, wk, entries:[{name,hours}] }] } -- total is always derived, never stored, so
// it can't drift from the entries.
const SHOP_LABOR_SEED_WK35 = {
  id: 'wk-2026-35', year: 2026, wk: 35,
  entries: [
    { name: 'Barr, Terry',      hours: 68.67 },
    { name: 'Bigham, Kenneth',  hours: 60.25 },
    { name: 'Derden, Cody',     hours: 42.73 },
    { name: 'Faltys, Arik',     hours: 52.5  },
    { name: 'Johnson, Johnny',  hours: 69.36 },
    { name: 'Juarez, Mariano',  hours: 83    },
    { name: 'Lee, Jordan',      hours: 53.75 },
    { name: 'Waedekin, Jerry',  hours: 56.25 },
  ],
};

const shopLaborTotal = (wk) => Math.round((((wk && wk.entries) || []).reduce((s,e)=> s + (Number(e.hours)||0), 0)) * 100) / 100;

const hrsFmt = (n) => (Math.round((Number(n)||0) * 100) / 100).toString();

export function ShopLaborWeekly({ sg, ss, SHOP, canEdit }) {
  const [weeks, setWeeks] = useState(null);
  const [open, setOpen] = useState({});
  const [form, setForm] = useState(null);   // null = closed; otherwise the week object being added/edited

  useEffect(() => { (async () => {
    try {
      const v = await sg('fleet-shoplabor-weekly');
      let list = Array.isArray(v && v.weeks) ? v.weeks : [];
      if (!list.length) {
        list = [SHOP_LABOR_SEED_WK35];
        try { if (ss) await ss('fleet-shoplabor-weekly', { weeks: list }); } catch {}
      }
      setWeeks(list);
    } catch { setWeeks([]); }
  })(); }, [sg]);

  const persist = async (next) => {
    setWeeks(next);
    try { await ss('fleet-shoplabor-weekly', { weeks: next }); } catch (e) { alert('Could not save: ' + (e.message || e)); }
  };

  // Newest week on top, oldest on bottom -- Bill's own words: "youngest WK on the bottom and newest on top."
  const sorted = useMemo(() => [...(weeks || [])].sort((a, b) => (b.year - a.year) || (b.wk - a.wk)), [weeks]);
  const lastCrew = sorted.length ? sorted[0].entries.map(e => e.name) : [];

  const startAdd = () => {
    const latest = sorted[0];
    const nextWk = latest ? (latest.wk >= 52 ? 1 : latest.wk + 1) : 1;
    const nextYr = latest ? (latest.wk >= 52 ? latest.year + 1 : latest.year) : new Date().getFullYear();
    setForm({ id: '', year: nextYr, wk: nextWk, entries: lastCrew.length ? lastCrew.map(name => ({ name, hours: '' })) : [{ name: '', hours: '' }], paste: '' });
  };
  const startEdit = (wk) => setForm({ ...wk, entries: wk.entries.map(e => ({ ...e })), paste: '' });

  const setRow = (i, field, val) => setForm(f => ({ ...f, entries: f.entries.map((e, idx) => idx === i ? { ...e, [field]: val } : e) }));
  const addRow = () => setForm(f => ({ ...f, entries: [...f.entries, { name: '', hours: '' }] }));
  const delRow = (i) => setForm(f => ({ ...f, entries: f.entries.filter((_, idx) => idx !== i) }));

  // Paste importer -- "Name<TAB>Hours" (or "Name, Hours") per line, straight out of a spreadsheet,
  // same idea as Fuel . Weekly's Impac paste importer.
  const applyPaste = () => {
    const rows = (form.paste || '').split('\n').map(l => l.trim()).filter(Boolean).map(line => {
      const parts = line.split(/\t|,(?!\d)/).map(s => s.trim());
      if (parts.length < 2) return null;
      const hours = Number(String(parts[parts.length - 1]).replace(/[^0-9.\-]/g, ''));
      const name = parts.slice(0, parts.length - 1).join(', ').trim();
      if (!name || !isFinite(hours)) return null;
      return { name, hours };
    }).filter(Boolean);
    if (!rows.length) return;
    setForm(f => ({ ...f, entries: rows, paste: '' }));
  };

  const saveForm = async () => {
    if (!form) return;
    const entries = form.entries.filter(e => e.name && String(e.name).trim() && e.hours !== '' && Number(e.hours) >= 0);
    if (!entries.length || !form.wk) { alert('Add at least one name with hours, and a week number.'); return; }
    const rec = { id: form.id || `wk-${form.year}-${form.wk}`, year: Number(form.year), wk: Number(form.wk), entries: entries.map(e => ({ name: e.name.trim(), hours: Number(e.hours) })) };
    const next = [...(weeks || []).filter(w => w.id !== rec.id), rec];
    await persist(next);
    setForm(null);
  };

  const deleteWeek = async (id) => {
    if (!window.confirm("Delete this week's Shop labor entry? This can't be undone.")) return;
    await persist((weeks || []).filter(w => w.id !== id));
  };

  if (weeks === null) return <div style={{ fontSize: 12, color: SHOP.steel }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', color: SHOP.accent }}>Weekly Labor Spend Hours . Shop</div>
        {canEdit && !form && (
          <button onClick={startAdd} style={{ cursor: 'pointer', background: SHOP.accent, border: 'none', color: '#1B1D21', borderRadius: 4, padding: '3px 10px', fontSize: 10.5, fontWeight: 800 }}>+ Add week</button>
        )}
      </div>

      {!sorted.length && <div style={{ fontSize: 11.5, color: SHOP.steel }}>Nothing logged yet.</div>}

      {sorted.length > 0 && (
        <div style={{ border: `1px solid ${SHOP.panelBorder}`, borderRadius: 6, overflow: 'hidden' }}>
          {sorted.map(wk => {
            const total = shopLaborTotal(wk);
            const isOpen = !!open[wk.id];
            return (
              <div key={wk.id} style={{ borderTop: `1px solid ${SHOP.panelBorder}` }}>
                <div onClick={() => setOpen(o => ({ ...o, [wk.id]: !o[wk.id] }))}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: SHOP.panel }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: SHOP.steel, fontSize: 10, width: 10, display: 'inline-block' }}>{isOpen ? '▼' : '▶'}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: SHOP.text }}>WK{String(wk.wk).padStart(2, '0')} . {wk.year}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 900, color: SHOP.accent2 }}>{hrsFmt(total)} hrs</span>
                    {canEdit && (
                      <Fragment>
                        <span onClick={(e) => { e.stopPropagation(); startEdit(wk); }} title="Edit" style={{ fontSize: 11, color: SHOP.steel, cursor: 'pointer' }}>✎</span>
                        <span onClick={(e) => { e.stopPropagation(); deleteWeek(wk.id); }} title="Delete" style={{ fontSize: 11, color: SHOP.steel, cursor: 'pointer' }}>✕</span>
                      </Fragment>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div style={{ padding: '4px 12px 10px', background: '#1B1D21' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                      <tbody>
                        {wk.entries.map((e, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${SHOP.panelBorder}` }}>
                            <td style={{ padding: '4px 6px', color: SHOP.text }}>{e.name}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', color: SHOP.steel }}>{hrsFmt(e.hours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <div style={{ marginTop: 12, background: SHOP.panel, border: `1px solid ${SHOP.accent}66`, borderRadius: 6, padding: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 9, color: SHOP.steel, fontWeight: 800, textTransform: 'uppercase' }}>Week #</div>
              <input type="number" value={form.wk} onChange={e => setForm(f => ({ ...f, wk: e.target.value }))}
                style={{ width: 60, background: '#1B1D21', border: `1px solid ${SHOP.panelBorder}`, borderRadius: 4, color: SHOP.text, padding: '3px 6px' }} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: SHOP.steel, fontWeight: 800, textTransform: 'uppercase' }}>Year</div>
              <input type="number" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
                style={{ width: 76, background: '#1B1D21', border: `1px solid ${SHOP.panelBorder}`, borderRadius: 4, color: SHOP.text, padding: '3px 6px' }} />
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 12, fontWeight: 900, color: SHOP.accent2 }}>
              {hrsFmt(form.entries.reduce((s, e) => s + (Number(e.hours) || 0), 0))} hrs
            </div>
          </div>

          {form.entries.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5, alignItems: 'center' }}>
              <input placeholder="Name" value={e.name} onChange={ev => setRow(i, 'name', ev.target.value)}
                style={{ flex: 1, background: '#1B1D21', border: `1px solid ${SHOP.panelBorder}`, borderRadius: 4, color: SHOP.text, padding: '4px 8px', fontSize: 12 }} />
              <input placeholder="Hrs" type="number" step="0.01" value={e.hours} onChange={ev => setRow(i, 'hours', ev.target.value)}
                style={{ width: 80, background: '#1B1D21', border: `1px solid ${SHOP.panelBorder}`, borderRadius: 4, color: SHOP.text, padding: '4px 8px', fontSize: 12 }} />
              <span onClick={() => delRow(i)} style={{ cursor: 'pointer', color: SHOP.steel, fontSize: 12 }}>✕</span>
            </div>
          ))}
          <button onClick={addRow} style={{ cursor: 'pointer', background: 'transparent', border: `1px dashed ${SHOP.panelBorder}`, color: SHOP.steel, borderRadius: 4, padding: '3px 10px', fontSize: 11, marginTop: 2, marginBottom: 10 }}>+ Add name</button>

          <div style={{ fontSize: 9.5, color: SHOP.steel, fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Or paste Name + Hours (one per line)</div>
          <textarea value={form.paste} onChange={e => setForm(f => ({ ...f, paste: e.target.value }))} placeholder={'Barr, Terry\tHOURS\nBigham, Kenneth\tHOURS'}
            rows={3} style={{ width: '100%', background: '#1B1D21', border: `1px solid ${SHOP.panelBorder}`, borderRadius: 4, color: SHOP.text, padding: 6, fontSize: 11.5, marginBottom: 8, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={applyPaste} style={{ cursor: 'pointer', background: SHOP.panel, border: `1px solid ${SHOP.panelBorder}`, color: SHOP.text, borderRadius: 4, padding: '5px 12px', fontSize: 11.5, fontWeight: 700 }}>Apply paste</button>
            <div style={{ flex: 1 }} />
            <button onClick={() => setForm(null)} style={{ cursor: 'pointer', background: 'transparent', border: `1px solid ${SHOP.panelBorder}`, color: SHOP.steel, borderRadius: 4, padding: '5px 14px', fontSize: 11.5, fontWeight: 700 }}>Cancel</button>
            <button onClick={saveForm} style={{ cursor: 'pointer', background: SHOP.accent, border: 'none', color: '#1B1D21', borderRadius: 4, padding: '5px 14px', fontSize: 11.5, fontWeight: 800 }}>Save week</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function YardTimeClock({ sg, ss, F, userRole, upn, canApprove, jobIndex, selfClock, forEmail }) {
  // #FLEET_TIMECARD_EACH_V1 — an approver (Bob / Owner) looks at ONE person's card at a time: the one he came in
  // for (forEmail, from that person's dash) or the one he picks off the team list on the right.
  const [pick, setPick] = useState('');
  const target = canApprove ? String(pick || forEmail || '').trim().toLowerCase() : '';
  const [emps, setEmps] = useState(null);
  const [punches, setPunches] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [roleMap, setRoleMap] = useState(null);   // #FLEET_TIMECLOCK_WHOSECLOCK_V1 — who actually holds the Yard login role
  const [wkStart, setWkStart] = useState(() => iso(mondayOf(new Date())));

  useEffect(() => { (async () => {
    try { const v = await sg('hr-employees'); setEmps(Array.isArray(v) ? v : []); } catch { setEmps([]); }
  })(); }, [sg]);
  useEffect(() => { (async () => {
    try { const v = await sg(TC_PUNCH_KEY); setPunches(Array.isArray(v) ? v : []); } catch { setPunches([]); }
  })(); }, [sg]);
  useEffect(() => { (async () => {
    try { const v = await sg(TC_WEEK_KEY); setWeeks(Array.isArray(v) ? v : []); } catch { setWeeks([]); }
  })(); }, [sg]);
  useEffect(() => { (async () => {
    try { const v = await sg('role-map'); setRoleMap(v && typeof v==='object' && !Array.isArray(v) ? v : {}); } catch { setRoleMap({}); }
  })(); }, [sg]);

  // #FLEET_TIMECLOCK_MERGE_V1 (2026-10-06) — this clock had one user (Jerry), so every save wrote back the whole
  // punch list as this screen remembered it. With the shop staff punching it too (#FLEET_SHOPDASH_V1), two people
  // clocking in a minute apart would erase one of the punches. Every save now re-reads the list and applies only
  // its own change to it. If the re-read fails, the copy on screen is used rather than writing an empty list.
  const mutatePunches = async (fn) => {
    let fresh = null;
    try { fresh = await sg(TC_PUNCH_KEY); } catch { fresh = null; }
    const next = fn(Array.isArray(fresh) ? fresh : (punches||[]));
    setPunches(next);
    try { await ss(TC_PUNCH_KEY, next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const mutateWeeks = async (fn) => {
    let fresh = null;
    try { fresh = await sg(TC_WEEK_KEY); } catch { fresh = null; }
    const next = fn(Array.isArray(fresh) ? fresh : (weeks||[]));
    setWeeks(next);
    try { await ss(TC_WEEK_KEY, next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };

  // #FLEET_TIMECLOCK_WHOSECLOCK_V1 (2026-09-02) — Bill: opened Time Clock as Owner (checking on Jerry)
  // and got "William Payne's Time Clock" instead — matching purely by the viewer's own upn is right
  // for someone clocking themselves in, wrong for an overseer looking in on the Yard portal. `role-map`
  // (App.js: email -> app login role) is the real source of truth for "who holds the Yard role," not
  // hr-employees' own department tag. If the viewer themselves is that person, it's their own clock
  // (self-service). Otherwise show the Yard employee's clock, read-only — see `isMine` below.
  const me = useMemo(() => {
    if (!emps || !roleMap) return null;
    if (target) return findEmp(emps, target);   // #FLEET_TIMECARD_EACH_V1 — the person the approver asked for
    const myEmail = String(upn||'').trim().toLowerCase();
    const yardEmails = Object.keys(roleMap).filter(k => roleMap[k] === 'Yard').map(k => k.toLowerCase());
    if (myEmail && yardEmails.includes(myEmail)) {
      return emps.find(e => String(e.email||'').trim().toLowerCase() === myEmail) || null;
    }
    // #FLEET_SHOPDASH_V1 — shop staff (Fleet role) open this from their own dash: it is their own clock.
    if (selfClock) return findEmp(emps, myEmail);
    const targetEmail = yardEmails[0];   // single Yard login today; needs a picker once more than one exists
    if (targetEmail) return emps.find(e => String(e.email||'').trim().toLowerCase() === targetEmail) || null;
    return myEmail ? (emps.find(e => String(e.email||'').trim().toLowerCase() === myEmail) || null) : null;
  }, [emps, roleMap, upn, selfClock, target]);
  // #FLEET_TIMECARD_EACH_V1 — everyone who punches this clock: the Fleet and Yard logins. One card each.
  const team = useMemo(() => {
    if (!canApprove || !emps || !roleMap) return [];
    return Object.keys(roleMap).filter(k => roleMap[k] === 'Fleet' || roleMap[k] === 'Yard')
      .map(k => ({ email: k.toLowerCase(), role: roleMap[k], emp: findEmp(emps, k) }))
      .sort((a, b) => (a.role === b.role ? 0 : a.role === 'Yard' ? 1 : -1) || a.email.localeCompare(b.email));
  }, [canApprove, emps, roleMap]);
  // #FLEET_SHOPDASH_V1 — HR sometimes holds a personal email, so "mine" is the same HR record, not the same address.
  const mineEmp = useMemo(() => findEmp(emps, upn), [emps, upn]);
  const isMine = !!(me && ((String(me.email||'').trim().toLowerCase() === String(upn||'').trim().toLowerCase()) || (mineEmp && mineEmp.hrid === me.hrid)));

  // #FLEET_TIMECLOCK_PMJOB_V1 — Job # options for the PM Code picker: In Progress / Called In jobs for
  // that PM always show; a job that's Complete still shows through TC_JOB_MGMT_DAYS (14) days past its
  // completion date, tagged so the picker can pop the "this job is complete" confirm and gate the
  // 6-14-day band to canApprove (Owner/Fleet) — Jerry's Time Clock has no real "PM" identity to check
  // against, so canApprove stands in for "manager tier" here; flagged as an approximation.
  const jobsByPm = useMemo(() => {
    const idx = jobIndex || {};
    const m = {};
    TC_PM_CODES.forEach(c => { m[c] = []; });
    Object.values(idx).forEach(j => {
      if (!TC_PM_CODES.includes(j.pm)) return;
      const meta = { duc:j.duc, cust:j.cust||'', city:j.city||'' };
      const st = String(j.stat||'').toLowerCase();
      if (st === 'called in' || st === 'in progress') { m[j.pm].push({ ...meta, tier:'live', days:null }); return; }
      if (st === 'complete') {
        const n = tcDaysSinceComplete(j.com);
        if (n == null || n > TC_JOB_MGMT_DAYS) return;
        m[j.pm].push({ ...meta, tier: n <= TC_JOB_GRACE_DAYS ? 'grace' : 'mgmt', days:n });
      }
    });
    Object.keys(m).forEach(c => m[c].sort((a,b)=>a.duc.localeCompare(b.duc)));
    return m;
  }, [jobIndex]);
  // #FLEET_TIMECLOCK_PMJOB_V2 (2026-09-02) - Bill, live-test: "customer, city does not follow. These
  // guys don't necessarily know the job number but do know the city or customer." Job # option text
  // carries city/customer so the OPEN list reads that way; the closed select box itself is styled
  // narrow (~8 chars, "Job Number box could be smaller") so day-to-day it just shows the DUC.
  const tcJobLabel = (j) => `${j.duc}${[j.city,j.cust].filter(Boolean).length ? ' - '+[j.city,j.cust].filter(Boolean).join(', ') : ''}${j.tier!=='live' ? ` (done ${j.days}d)` : ''}`;

  const btn = { background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:8, padding:'6px 12px', fontSize:12.5, fontWeight:700, cursor:'pointer' };
  const lbl = { fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 };
  const th = { padding:'7px 9px', fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, textAlign:'left', position:'sticky', top:0, background:F.bg, zIndex:1 };   // #FLEET_TIMECLOCK_PMJOB_V2 -- Bill: freeze header for mobile
  const td = { padding:'6px 9px', fontSize:12, color:F.text, verticalAlign:'middle' };
  const tinp = { background:F.bg, border:`1px solid ${F.line}`, borderRadius:6, padding:'3px 6px', fontSize:11.5, color:F.text, width:96 };   // #FLEET_TIMECLOCK_EDIT_V1

  if (emps === null || punches === null || weeks === null || roleMap === null) return <div style={{ fontSize:12, color:F.muted }}>Loading…</div>;

  const monDate = new Date(wkStart+'T12:00:00');
  const wkDates = CHEM_DAYS.map((_,i)=> iso(addDays(monDate,i)));
  const wkNum = isoWeekNum(monDate);
  const weekId = wkStart;   // that week's Monday ISO date is its natural key

  const myPunches = me ? (punches||[]).filter(p => p.hrid===me.hrid && wkDates.includes(p.date)) : [];
  const myWeekStatus = me ? (weeks||[]).find(w => w.hrid===me.hrid && w.weekId===weekId) : null;
  const myStatus = myWeekStatus ? myWeekStatus.status : 'open';
  const myLocked = myStatus !== 'open';
  const myOpenPunch = me ? (punches||[]).find(p => p.hrid===me.hrid && !p.outAt) : null;

  // #FLEET_TIMECLOCK_PAYCODE_V1 — OT is computed off worked (Regular) hours only, not vacation/sick/
  // perdiem/ride time — those don't count toward an overtime threshold. Week total still sums
  // everything so nothing's dropped even though only REG/OT/VAC/SICK/Ride time get their own tile.
  const myWorkedHrs = myPunches.filter(p => (p.payCode||'Regular')==='Regular').reduce((s,p) => s + tcPunchHours(p), 0);
  const myRideHrs = myPunches.filter(p => p.payCode==='Ride time').reduce((s,p) => s + tcPunchHours(p), 0);
  const myVacHrs = myPunches.filter(p => p.payCode==='Vacation').reduce((s,p) => s + tcPunchHours(p), 0);
  const mySickHrs = myPunches.filter(p => p.payCode==='Sick').reduce((s,p) => s + tcPunchHours(p), 0);
  const myOtherHrs = myPunches.filter(p => ['Perdiem','Training'].includes(p.payCode)).reduce((s,p) => s + tcPunchHours(p), 0);
  const myReg = Math.min(myWorkedHrs, 40);
  const myOT = Math.max(0, myWorkedHrs - 40);
  const myTotalHrs = myWorkedHrs + myRideHrs + myVacHrs + mySickHrs + myOtherHrs;
  const editableWeek = !myLocked && (isMine || canApprove);   // #FLEET_TIMECLOCK_EDIT_V1

  // #MECH_PUNCH_CONFIRM_V1 (2026-10-16) — same rule on the Time Card's own Clock In / Out: confirm the time (one chance to
  // fix a forgotten punch), then it is locked for everyone but Bob / Owner.
  const _hm = (dt) => `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
  const _askTime = (label, now) => {
    const v = window.prompt(`${label} — confirm the time (HH:MM, 24-hour).\nForgot to punch? Change it now — this is your one edit. Once confirmed it is locked and only Bob can change it.`, _hm(now));
    if (v == null) return null;
    const m = String(v).trim().match(/^(\d{1,2}):?(\d{2})$/); if (!m || +m[1] > 23 || +m[2] > 59) { alert('Use HH:MM, e.g. 07:30 or 16:45.'); return null; }
    return `${pad2(+m[1])}:${m[2]}`;
  };
  const clockIn = async () => {
    if (!me) return;
    const now = new Date();
    const t = _askTime('Clock In', now); if (!t) return;
    const ts = new Date(`${iso(now)}T${t}:00`); if (ts > new Date(Date.now() + 60000)) { alert('That time is in the future.'); return; }
    const rec = { id: 'tc-' + Date.now().toString(36) + Math.random().toString(36).slice(2,6), hrid: me.hrid, name: `${me.first} ${me.last}`, date: iso(now), inAt: ts.toISOString(), outAt: null,
      inLocked: true, inTapAt: now.toISOString(), inAdjusted: t !== _hm(now) };
    await mutatePunches(list => [ ...list, rec ]);
  };
  const clockOut = async () => {
    if (!myOpenPunch) return;
    const now = new Date();
    const t = _askTime('Clock Out', now); if (!t) return;
    const ts = new Date(`${myOpenPunch.date}T${t}:00`);
    if (ts <= new Date(myOpenPunch.inAt) || ts > new Date(Date.now() + 60000)) { alert('Clock-out has to be after clock-in and not in the future.'); return; }
    await mutatePunches(list => list.map(p => p.id===myOpenPunch.id ? { ...p, outAt: ts.toISOString(), outLocked: true, outTapAt: now.toISOString(), outAdjusted: t !== _hm(now) } : p));
  };

  // #FLEET_TIMECLOCK_EDIT_V1 (2026-09-02) — Bill, live-test: "he will need to edit his time and not
  // keep adding clock in/out repeating the day time." A mis-tap on Clock In/Out was the only way a
  // punch got made — no way to fix one, so a fumbled tap just piled up more punches on the same day
  // instead of correcting the first. These three let an in/out time (or a whole missed punch) be
  // corrected directly once a week is still open — same lock semantics as everything else here: once
  // the week is submitted or approved, editing is off same as clocking in/out is, until it's sent back.
  // #MECH_PUNCH_CONFIRM_V1 (2026-10-16) — a time confirmed at the clock is locked; only the approver (Bob / Owner) changes it.
  const punchLockedFor = (p, field) => !canApprove && p && ((field === 'inAt' && p.inLocked) || (field === 'outAt' && p.outLocked));
  const editPunchTime = async (punchId, field, timeStr) => {
    const p = (punches||[]).find(x => x.id===punchId); if (!p) return;
    if (punchLockedFor(p, field)) { alert('This time was confirmed and locked at the clock. Ask Bob to change it.'); return; }
    if (!timeStr) {   // cleared the box — only meaningful for Out (reopens the punch); In stays required
      if (field === 'outAt') await mutatePunches(list => list.map(x => x.id===punchId ? { ...x, outAt:null } : x));
      return;
    }
    const ts = new Date(`${p.date}T${timeStr}:00`);
    if (isNaN(ts.getTime())) return;
    await mutatePunches(list => list.map(x => x.id===punchId ? { ...x, [field]: ts.toISOString() } : x));
  };
  const addPunch = async (date) => {
    if (!me) return;
    const rec = { id: 'tc-' + Date.now().toString(36) + Math.random().toString(36).slice(2,6), hrid: me.hrid, name: `${me.first} ${me.last}`, date, payCode:'Regular', inAt: new Date(`${date}T08:00:00`).toISOString(), outAt: null, hours:'', pmCode:'', job:'', note:'' };
    await mutatePunches(list => [ ...list, rec ]);
  };
  const editPunchCode = async (punchId, code) => {   // #FLEET_TIMECLOCK_PAYCODE_V1
    await mutatePunches(list => list.map(p => p.id===punchId ? { ...p, payCode: code } : p));
  };
  // #FLEET_TIMECLOCK_PMJOB_V1 — PM Code / Job # / Note. Picking "Shop" locks Job # to "Maintenance"
  // (Bill: "If Shop is chosen the next field JOB # Defaults to Maintenance"); picking a PM clears
  // whatever job was set, since it almost certainly belonged to a different PM's list.
  const editPunchPM = async (punchId, val) => {
    await mutatePunches(list => list.map(p => p.id===punchId ? { ...p, pmCode: val, job: val==='Shop' ? 'Maintenance' : '' } : p));
  };
  const editPunchJob = async (punchId, duc, pmCode) => {
    if (!duc) { await mutatePunches(list => list.map(p => p.id===punchId ? { ...p, job:'' } : p)); return; }
    const meta = (jobsByPm[pmCode]||[]).find(j => j.duc === duc);
    if (meta && meta.tier !== 'live') {
      if (meta.tier === 'mgmt' && !canApprove) {
        alert(`${duc} was completed ${meta.days} days ago \u2014 that's past the ${TC_JOB_GRACE_DAYS}-day self-entry window. A manager needs to enter this one, or (past ${TC_JOB_MGMT_DAYS} days) it needs DM approval \u2014 not set up yet.`);
        return;
      }
      if (!window.confirm(`${duc} was completed ${meta.days} day${meta.days===1?'':'s'} ago.\n\nThis job is complete \u2014 confirm you want to charge time to it?`)) return;
    }
    await mutatePunches(list => list.map(p => p.id===punchId ? { ...p, job: duc } : p));
  };
  const editPunchNote = async (punchId, val) => {
    await mutatePunches(list => list.map(p => p.id===punchId ? { ...p, note: val } : p));
  };
  const editPunchHours = async (punchId, val) => {   // #FLEET_TIMECLOCK_PAYCODE_V1 — non-timed codes only
    await mutatePunches(list => list.map(p => p.id===punchId ? { ...p, hours: val } : p));
  };
  const deletePunch = async (punchId) => {
    { const p0 = (punches||[]).find(x => x.id===punchId); if (!canApprove && p0 && (p0.inLocked || p0.outLocked)) { alert('This punch was confirmed and locked at the clock. Ask Bob to remove it.'); return; } }
    if (!window.confirm("Delete this punch? If it's just got the wrong time or day, edit the boxes instead \u2014 Delete removes it completely and can't be undone.")) return;
    await mutatePunches(list => list.filter(p => p.id !== punchId));
  };

  const submitWeek = async () => {
    if (!me || myOpenPunch || !myPunches.length) return;
    const rec = { id: me.hrid+'_'+weekId, hrid: me.hrid, name: `${me.first} ${me.last}`, weekId, status:'submitted', submittedAt: new Date().toISOString(), submittedBy: upn };
    await mutateWeeks(list => [ ...list.filter(w => !(w.hrid===me.hrid && w.weekId===weekId)), rec ]);
  };
  const approveWeek = async (w) => {
    await mutateWeeks(list => list.map(x => x.id===w.id ? { ...x, status:'approved', approvedAt: new Date().toISOString(), approvedBy: upn } : x));
  };
  const reopenWeek = async (w) => {
    await mutateWeeks(list => list.map(x => x.id===w.id ? { ...x, status:'open', reopenedAt: new Date().toISOString(), reopenedBy: upn } : x));
  };

  const pending = canApprove ? (weeks||[]).filter(w => w.status==='submitted') : [];
  const recentApproved = canApprove ? (weeks||[]).filter(w=>w.status==='approved').sort((a,b)=> (b.approvedAt||'').localeCompare(a.approvedAt||'')).slice(0,5) : [];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px,1fr))', gap:16, alignItems:'start' }}>
      {/* LEFT — the employee's own clock — #FLEET_TIMECLOCK_PANELSKIN_V2 (2026-09-02): both the
          STEEL_BG and DIAMOND_PLATE background-texture attempts read as messy/noisy at real screen
          size (Bill: "That did not land... Likely best to revert back and focus on border maybe...
          or 3d"). Reverted to a flat F.card background; the "pop" now comes from a brighter border
          and an inset emboss shadow (light top edge, dark bottom edge) for a beveled-metal-plate
          feel without a repeating pattern. */}
      <div style={{ background:F.card, border:'1.5px solid #4A5058', borderRadius:12, padding:'14px 16px', position:'relative', boxShadow:'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -3px 6px rgba(0,0,0,0.5), 0 10px 24px -12px rgba(0,0,0,0.6)' }}>
        <RivetCorners />
        <div style={{ fontSize:13, fontWeight:900, marginBottom:2 }}>{me ? `${me.first} ${me.last}'s Time Clock` : 'Time Clock'}</div>
        {!me && <div style={{ fontSize:11.5, color:F.muted, marginTop:8 }}>{target ? `No HR record matches ${target} — HR needs that work email (or the same first and last name) on the employee record.` : selfClock ? `No HR record matches ${upn || 'this login'} — HR needs this work email (or the same first and last name) on the employee record before this clock works.` : `No Yard-role login found yet (checked role-map / hr-employees) — clock in/out isn't available here.`} {canApprove ? 'Approvals below still work.' : ''}</div>}
        {me && !isMine && <div style={{ fontSize:10.5, color:F.amber, fontWeight:700, marginTop:2 }}>{canApprove ? `You are looking at ${me.first}'s card as approver — you can correct a punch while the week is open; only ${me.first} clocks in or out.` : `Read-only — you're signed in as a different account than ${me.first} ${me.last}.`}</div>}
        {me && (
          <Fragment>
            <div style={{ display:'flex', alignItems:'center', gap:10, margin:'10px 0 14px' }}>
              {!isMine ? (
                myOpenPunch
                  ? <div style={{ fontSize:11.5, color:F.green, fontWeight:800 }}>● Clocked in at {tcFmtT(myOpenPunch.inAt)}</div>
                  : <div style={{ fontSize:11.5, color:F.muted }}>Not clocked in.</div>
              ) : myOpenPunch ? (
                <Fragment>
                  <div style={{ fontSize:11.5, color:F.green, fontWeight:800 }}>● Clocked in at {tcFmtT(myOpenPunch.inAt)}</div>
                  <button onClick={clockOut} style={{ ...btn, background:F.red, color:'#fff' }}>Clock Out</button>
                </Fragment>
              ) : myLocked ? (
                <div style={{ fontSize:11.5, color:F.muted }}>🔒 This week is {myStatus} — clocking in opens again once it's reopened.</div>
              ) : (
                <button onClick={clockIn} style={{ ...btn, background:F.green, color:'#04121C' }}>Clock In</button>
              )}
            </div>

            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <button onClick={()=>setWkStart(iso(addDays(monDate,-7)))} style={{ ...btn, padding:'3px 9px' }}>◀</button>
              <div style={{ fontSize:12.5, fontWeight:900 }}>WK {wkNum} · Week ending {fmtMD(new Date(wkDates[6]+'T12:00:00'))}</div>
              <button onClick={()=>setWkStart(iso(addDays(monDate,7)))} style={{ ...btn, padding:'3px 9px' }}>▶</button>
              {wkStart !== iso(mondayOf(new Date())) && <button onClick={()=>setWkStart(iso(mondayOf(new Date())))} style={{ ...btn, padding:'3px 9px', fontSize:11 }}>This week</button>}
              <div style={{ flex:1 }} />
              <span style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', padding:'2px 8px', borderRadius:10,
                background: myStatus==='approved' ? F.green+'22' : myStatus==='submitted' ? F.amber+'22' : F.line+'44',
                color: myStatus==='approved' ? F.green : myStatus==='submitted' ? F.amber : F.muted }}>
                {myStatus==='approved' ? '🔒 Approved' : myStatus==='submitted' ? 'Pending approval' : 'Open'}
              </span>
            </div>

            {editableWeek && <div style={{ fontSize:10.5, color:F.muted, marginBottom:6 }}>Times are editable while this week is open — fix a fumbled punch in place instead of clocking in again, or ✕ to remove one.</div>}
            <div style={{ background:F.bg, border:`1px solid ${F.line}`, borderRadius:10, overflow:'auto', marginBottom:10 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><th style={{...th,textAlign:'center'}}>Day</th><th style={th}>PM / Shop</th><th style={th}>Job #</th><th style={th}>Code</th><th style={th}>In</th><th style={th}>Out</th><th style={th}>Note</th><th style={{...th,textAlign:'right'}}>Hrs</th></tr></thead>
                <tbody>
                  {wkDates.map((d,i) => {
                    const dayPunches = myPunches.filter(p=>p.date===d).sort((a,b)=>String(a.inAt||'').localeCompare(String(b.inAt||'')));
                    const dayHrs = dayPunches.reduce((s,p)=>s+tcPunchHours(p),0);
                    // #FLEET_TIMECLOCK_DAYFMT_V1 (2026-09-02) — Bill: "DATE Then DAY so 08/31 (centered)
                    // followed by MON (Centered)", then live-testing the stacked version: "DATE - DAY
                    // could share same line (Keeps rows nice)." Centered, one line: date, then the
                    // all-caps day abbreviation right after it.
                    // Bill, next round: "DATE - DAY to share a line" -- the code already put them on
                    // one line, but the DAY column got squeezed once PM/Job/Note columns landed, so
                    // the browser was wrapping it back to two. whiteSpace:'nowrap' pins it to one line
                    // regardless of column width (the table's wrapper already scrolls horizontally).
                    const dayLbl = (
                      <div style={{ textAlign:'center', whiteSpace:'nowrap' }}>
                        <span style={{ fontWeight:800 }}>{fmtMD(new Date(d+'T12:00:00'))}</span>{' '}
                        <span style={{ fontSize:10, fontWeight:700, letterSpacing:0.5, color:F.muted }}>{CHEM_DAYS[i].toUpperCase()}</span>
                      </div>
                    );
                    // #FLEET_TIMECLOCK_ADDLINK_V1 — Bill: "+Add Punch button looks messy if completed
                    // for the day." A full bold pill sitting under an already-filled day read as
                    // clutter, so once a day has punches this recedes into a small muted text link
                    // instead of matching the row buttons' visual weight.
                    const addLink = (label) => <button onClick={()=>addPunch(d)} style={{ background:'transparent', border:'none', color:F.muted, fontSize:10.5, fontWeight:700, textDecoration:'underline', cursor:'pointer', padding:'2px 0' }}>{label}</button>;
                    if (!editableWeek) {
                      return (
                        <tr key={d} style={{ borderTop:`1px solid ${F.line}` }}>
                          <td style={td}>{dayLbl}</td>
                          <td style={td}>{dayPunches.length ? [...new Set(dayPunches.map(p=>p.pmCode||'—'))].join(', ') : '—'}</td>
                          <td style={td}>{dayPunches.length ? [...new Set(dayPunches.map(p=>p.job||'—'))].join(', ') : '—'}</td>
                          <td style={td}>{dayPunches.length ? [...new Set(dayPunches.map(p=>p.payCode||'Regular'))].join(', ') : '—'}</td>
                          <td style={td}>{dayPunches.length ? dayPunches.map(p=>tcFmtT(p.inAt)).join(', ') : '—'}</td>
                          <td style={td}>{dayPunches.length ? dayPunches.map(p=>p.outAt?tcFmtT(p.outAt):'…').join(', ') : '—'}</td>
                          <td style={td}>{dayPunches.map(p=>p.note).filter(Boolean).join('; ') || '—'}</td>
                          <td style={{...td,textAlign:'right',fontWeight:700}}>{dayHrs ? tcFmtHrs(dayHrs) : '—'}</td>
                        </tr>
                      );
                    }
                    if (!dayPunches.length) {
                      return (
                        <tr key={d} style={{ borderTop:`1px solid ${F.line}` }}>
                          <td style={td}>{dayLbl}</td>
                          <td style={td} colSpan={6}><span style={{color:F.muted}}>—</span></td>
                          <td style={{...td,textAlign:'right'}}>{addLink('+ Add')}</td>
                        </tr>
                      );
                    }
                    return (
                      <Fragment key={d}>
                        {dayPunches.map((p,pi) => {
                          const timed = TC_TIMED_CODES.has(p.payCode||'Regular');
                          return (
                            <tr key={p.id} style={{ borderTop: pi===0 ? `1px solid ${F.line}` : 'none' }}>
                              <td style={td}>{pi===0 ? dayLbl : ''}</td>
                              <td style={td}>
                                <select value={p.pmCode||''} onChange={e=>editPunchPM(p.id,e.target.value)} style={{ ...tinp, width:104, ...(p.pmCode ? {} : { borderColor:F.amber }) }}>
                                  <option value="">—</option>
                                  {TC_PM_CODES.map(c => <option key={c} value={c}>{TC_PM_NAMES[c]}</option>)}
                                  <option value="Shop">Shop</option>
                                </select>
                              </td>
                              <td style={td}>{p.pmCode === 'Shop'
                                ? <span style={{ fontWeight:700 }}>MTX</span>   /* #TC_MTX_LABEL_V1 — label only; saved job stays 'Maintenance' */
                                : p.pmCode
                                  ? <select value={p.job||''} onChange={e=>editPunchJob(p.id,e.target.value,p.pmCode)} title={p.job||''} style={{ ...tinp, width:72 }}>
                                      <option value="">—</option>
                                      {(jobsByPm[p.pmCode]||[]).map(j => <option key={j.duc} value={j.duc}>{tcJobLabel(j)}</option>)}
                                    </select>
                                  : <span style={{ fontSize:10.5, color:F.amber, fontWeight:700 }}>← Pick PM/Shop first</span>}
                              </td>
                              <td style={td}>
                                <select value={p.payCode||'Regular'} onChange={e=>editPunchCode(p.id,e.target.value)} style={{ ...tinp, width:82 }}>
                                  {TC_PAY_CODES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td style={td}>{timed
                                ? <><input type="time" value={tcTimeInputVal(p.inAt)} onChange={e=>editPunchTime(p.id,'inAt',e.target.value)} disabled={!p.pmCode || punchLockedFor(p,'inAt')} title={punchLockedFor(p,'inAt') ? 'Locked at the clock — Bob can change it' : (p.pmCode?'':'Pick PM/Shop first')} style={{ ...tinp, ...(p.pmCode?{}:{opacity:0.45,cursor:'not-allowed'}) }} />{p.inLocked ? <span title={p.inAdjusted ? `Changed at the clock — tapped at ${tcFmtT(p.inTapAt)}` : 'Confirmed at the clock'} style={{ marginLeft:3, fontSize:10.5, color: p.inAdjusted ? F.amber : F.muted }}>🔒{p.inAdjusted ? '✎' : ''}</span> : null}</>
                                : <input type="number" step="0.25" min="0" value={p.hours==null?'':p.hours} onChange={e=>editPunchHours(p.id,e.target.value)} disabled={!p.pmCode} title={p.pmCode?'':'Pick PM/Shop first'} placeholder="Hrs" style={{ ...tinp, width:70, ...(p.pmCode?{}:{opacity:0.45,cursor:'not-allowed'}) }} />}
                              </td>
                              <td style={td}>{timed
                                ? <><input type="time" value={tcTimeInputVal(p.outAt)} onChange={e=>editPunchTime(p.id,'outAt',e.target.value)} disabled={!p.pmCode || punchLockedFor(p,'outAt')} title={punchLockedFor(p,'outAt') ? 'Locked at the clock — Bob can change it' : (p.pmCode?'':'Pick PM/Shop first')} style={{ ...tinp, ...(p.pmCode?{}:{opacity:0.45,cursor:'not-allowed'}) }} />{p.outLocked ? <span title={p.outAdjusted ? `Changed at the clock — tapped at ${tcFmtT(p.outTapAt)}` : 'Confirmed at the clock'} style={{ marginLeft:3, fontSize:10.5, color: p.outAdjusted ? F.amber : F.muted }}>🔒{p.outAdjusted ? '✎' : ''}</span> : null}</>
                                : <span style={{ fontSize:10.5, color:F.muted }}>no clock</span>}
                              </td>
                              <td style={td}>
                                <input type="text" value={p.note||''} onChange={e=>editPunchNote(p.id,e.target.value)} placeholder="Note" style={{ ...tinp, width:170 }} />
                              </td>
                              <td style={{...td,textAlign:'right'}}>
                                <span style={{ fontWeight:700, marginRight:8 }}>{tcPunchHours(p) ? tcFmtHrs(tcPunchHours(p)) : '—'}</span>
                                <button onClick={()=>deletePunch(p.id)} title="Delete this punch" style={{ ...btn, padding:'2px 7px', fontSize:11, color:F.red }}>✕</button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td style={{...td, padding:'2px 9px'}}></td>
                          <td colSpan={7} style={{...td, padding:'2px 9px'}}>{addLink('+ Add punch')}</td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex', gap:18, flexWrap:'wrap', marginBottom:12 }}>
              <div><div style={lbl}>Regular</div><div style={{ fontSize:16, fontWeight:900, color:F.text }}>{tcFmtHrs(myReg)}</div></div>
              <div><div style={lbl}>Overtime</div><div style={{ fontSize:16, fontWeight:900, color: myOT ? F.amber : F.muted }}>{tcFmtHrs(myOT)}</div></div>
              <div><div style={lbl}>Vacation</div><div style={{ fontSize:16, fontWeight:900, color: myVacHrs ? F.text : F.muted }}>{tcFmtHrs(myVacHrs)}</div></div>
              <div><div style={lbl}>Sick</div><div style={{ fontSize:16, fontWeight:900, color: mySickHrs ? F.text : F.muted }}>{tcFmtHrs(mySickHrs)}</div></div>
              <div><div style={lbl}>Ride time</div><div style={{ fontSize:16, fontWeight:900, color: myRideHrs ? F.text : F.muted }}>{tcFmtHrs(myRideHrs)}</div></div>
              {myOtherHrs > 0 && <div><div style={lbl}>Perdiem/Training</div><div style={{ fontSize:16, fontWeight:900, color:F.text }}>{tcFmtHrs(myOtherHrs)}</div></div>}
              <div><div style={lbl}>Week total</div><div style={{ fontSize:16, fontWeight:900, color:F.gold }}>{tcFmtHrs(myTotalHrs)}</div></div>
            </div>

            {isMine && myStatus==='open' && (
              <Fragment>
                <button onClick={submitWeek} disabled={!!myOpenPunch || !myPunches.length}
                  style={{ ...btn, background: (myOpenPunch||!myPunches.length) ? F.cardHi : F.accent, color: (myOpenPunch||!myPunches.length) ? F.muted : '#04121C', cursor: (myOpenPunch||!myPunches.length) ? 'not-allowed' : 'pointer' }}>
                  Submit week for approval
                </button>
                {myOpenPunch && <div style={{ fontSize:10.5, color:F.muted, marginTop:6 }}>Clock out before submitting the week.</div>}
              </Fragment>
            )}
          </Fragment>
        )}
      </div>

      {/* RIGHT — approvals, Owner/Fleet (Bob Macy) only */}
      {canApprove && (
        <div style={{ background:F.card, border:'1.5px solid #4A5058', borderRadius:12, padding:'14px 16px', position:'relative', boxShadow:'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -3px 6px rgba(0,0,0,0.5), 0 10px 24px -12px rgba(0,0,0,0.6)' }}>
          <RivetCorners tone="brass" kind="bolt" />
          {/* #FLEET_TIMECARD_EACH_V1 — the roll-up: every Fleet / Yard person's card for the week on screen. Tap a name to open that card. */}
          <div style={{ fontSize:13, fontWeight:900, marginBottom:2 }}>Team time cards · WK {wkNum}</div>
          <div style={{ fontSize:10.5, color:F.muted, marginBottom:6 }}>Week ending {fmtMD(new Date(wkDates[6]+'T12:00:00'))}. Tap a name to open that person's card.</div>
          <div style={{ background:F.bg, border:`1px solid ${F.line}`, borderRadius:10, overflow:'hidden', marginBottom:14 }}>
            {team.map(tm => {
              const hrid = tm.emp && tm.emp.hrid;
              const ps = hrid ? (punches||[]).filter(p => p.hrid === hrid && wkDates.includes(p.date)) : [];
              const hrs = ps.reduce((s, p) => s + tcPunchHours(p), 0);
              const inNow = hrid ? (punches||[]).some(p => p.hrid === hrid && !p.outAt) : false;
              const wk = hrid ? (weeks||[]).find(w => w.hrid === hrid && w.weekId === weekId) : null;
              const st = wk ? wk.status : (ps.length ? 'open' : '');
              const on = !!(me && hrid && me.hrid === hrid);
              return (
                <div key={tm.email} onClick={() => { if (hrid) setPick(tm.email); }} title={hrid ? `Open ${tm.emp.first}'s time card` : 'No HR record matches this login'}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderBottom:`1px solid ${F.line}`, cursor: hrid ? 'pointer' : 'default', background: on ? F.cardHi : 'transparent' }}>
                  <span style={{ flex:1, minWidth:0, fontSize:12.5, fontWeight: on ? 900 : 700, color: hrid ? F.text : F.muted }}>
                    {tm.emp ? `${tm.emp.first} ${tm.emp.last}` : tm.email}{tm.role === 'Yard' ? <span style={{ color:F.muted, fontWeight:400 }}> · Yard</span> : null}
                    {inNow && <span style={{ color:F.green, fontWeight:800, fontSize:10.5 }}> ● in</span>}
                    {!hrid && <span style={{ color:F.muted, fontWeight:400, fontSize:10.5 }}> · no HR match</span>}
                  </span>
                  <span style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', color: st==='approved' ? F.green : st==='submitted' ? F.amber : F.muted }}>{st==='approved' ? 'Approved' : st==='submitted' ? 'Pending' : st==='open' ? 'Open' : '—'}</span>
                  <span style={{ fontSize:12.5, fontWeight:900, color: hrs ? F.text : F.muted, minWidth:52, textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{tcFmtHrs(hrs)}</span>
                </div>
              );
            })}
            {!team.length && <div style={{ padding:'8px 10px', fontSize:11.5, color:F.muted }}>No Fleet or Yard logins found.</div>}
            {team.length > 0 && (
              <div style={{ display:'flex', gap:8, padding:'7px 10px', fontSize:12, fontWeight:900 }}>
                <span style={{ flex:1, color:F.muted, textTransform:'uppercase', fontSize:10, letterSpacing:.4 }}>Team total</span>
                <span style={{ color:F.gold, fontVariantNumeric:'tabular-nums' }}>{tcFmtHrs(team.reduce((t, tm) => t + (tm.emp ? (punches||[]).filter(p => p.hrid === tm.emp.hrid && wkDates.includes(p.date)).reduce((s, p) => s + tcPunchHours(p), 0) : 0), 0))}</span>
              </div>
            )}
          </div>
          <div style={{ fontSize:13, fontWeight:900, marginBottom:8 }}>Approvals</div>
          {!pending.length ? <div style={{ fontSize:11.5, color:F.muted, marginBottom:14 }}>Nothing waiting on you.</div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
              {pending.map(w => {
                const weekEnd = iso(addDays(new Date(w.weekId+'T12:00:00'),6));
                const wPunches = (punches||[]).filter(p => p.hrid===w.hrid && p.date >= w.weekId && p.date <= weekEnd);
                const tot = wPunches.reduce((s,p)=>s+tcHours(p.inAt,p.outAt),0);
                const reg = Math.min(tot,40), ot = Math.max(0,tot-40);
                return (
                  <div key={w.id} style={{ background:F.cardHi, border:`1px solid ${F.line}`, borderRadius:10, padding:'10px 12px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4, gap:8, flexWrap:'wrap' }}>
                      <div style={{ fontSize:12.5, fontWeight:900 }}>{w.name}</div>
                      <div style={{ fontSize:11, color:F.muted }}>Week of {fmtMD(new Date(w.weekId+'T12:00:00'))}</div>
                    </div>
                    <div style={{ fontSize:11.5, color:F.muted, marginBottom:8 }}>
                      {tcFmtHrs(reg)} reg{ot ? ` + ${tcFmtHrs(ot)} OT` : ''} = <b style={{ color:F.gold }}>{tcFmtHrs(tot)} hrs</b>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={()=>approveWeek(w)} style={{ ...btn, background:F.green, color:'#04121C' }}>Approve &amp; lock</button>
                      <button onClick={()=>reopenWeek(w)} style={btn}>Send back</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:6, marginTop:4 }}>Recently approved</div>
          {!recentApproved.length ? <div style={{ fontSize:11, color:F.muted }}>None yet.</div> : recentApproved.map(w => (
            <div key={w.id} style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:F.muted, padding:'4px 0', borderTop:`1px solid ${F.line}` }}>
              <span>{w.name} — wk of {fmtMD(new Date(w.weekId+'T12:00:00'))}</span>
              <span>🔒 {new Date(w.approvedAt).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function YardInventoryList({ title, storageKey, sg, ss, canEdit, canDelete, F }) {   // #FLEET_YARD_SPLIT_V2 — Bill: "edit but unable to remove"
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  useEffect(() => { (async () => {
    try { const v = await sg(storageKey); setRows(Array.isArray(v) ? v : []); } catch { setRows([]); }
  })(); }, [sg, storageKey]);
  const save = async () => {
    if (!edit) return;
    const name = String(edit.name||'').trim();
    if (!name) { alert('Name is required.'); return; }
    const rec = { id: edit._orig ? edit._orig.id : (name.toLowerCase().replace(/[^a-z0-9]+/g,'-') + '-' + Date.now().toString(36)),
      name, qty: edit.qty||'', unit: edit.unit||'', location: edit.location||'', notes: edit.notes||'' };
    const next = edit._orig ? (rows||[]).map(r => r.id===edit._orig.id ? rec : r) : [ ...(rows||[]), rec ];
    setRows(next); setEdit(null);
    try { await ss(storageKey, next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name}?`)) return;
    const next = (rows||[]).filter(x => x.id !== r.id);
    setRows(next);
    try { await ss(storageKey, next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const btn = { background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:8, padding:'6px 12px', fontSize:12.5, fontWeight:700, cursor:'pointer' };
  const inp = { background:F.bg, border:`1px solid ${F.line}`, borderRadius:7, padding:'6px 9px', fontSize:12.5, color:F.text, width:'100%', boxSizing:'border-box' };
  const th = { padding:'8px 10px', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, textAlign:'left' };
  const td = { padding:'6px 10px', fontSize:12.5, color:F.text, verticalAlign:'middle' };
  const ql = q.trim().toLowerCase();
  const filtered = (rows||[]).filter(r => !ql || `${r.name} ${r.location||''} ${r.notes||''}`.toLowerCase().includes(ql));
  return (
    <Fragment>
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
        <div style={{ fontSize:13, fontWeight:900 }}>{title}</div>
        <div style={{ fontSize:11.5, color:F.muted }}>{(rows||[]).length} item{(rows||[]).length===1?'':'s'}</div>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" style={{ ...inp, width:200 }} />
        <div style={{ flex:1 }} />
        {canEdit && <button onClick={()=>setEdit({ name:'', qty:'', unit:'', location:'', notes:'' })} style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Add item</button>}
      </div>
      {edit && (
        <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10 }}>
            {[['name','Name'],['qty','Qty'],['unit','Unit'],['location','Location'],['notes','Notes']].map(([k,l]) => (
              <div key={k}>
                <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>{l}</div>
                <input value={edit[k]==null?'':edit[k]} onChange={e=>setEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} />
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <button onClick={save} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
            <button onClick={()=>setEdit(null)} style={btn}>Close without saving</button>
          </div>
        </div>
      )}
      {rows === null ? <div style={{ fontSize:12, color:F.muted }}>Loading…</div>
        : !filtered.length ? <div style={{ fontSize:12, color:F.muted }}>Nothing here yet.</div>
        : (
        <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
          <RivetCorners tone="brass" kind="bolt" />
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={th}>Name</th><th style={th}>Qty</th><th style={th}>Unit</th><th style={th}>Location</th><th style={th}>Notes</th><th style={th}></th></tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ borderTop:`1px solid ${F.line}` }}>
                  <td style={{ ...td, fontWeight:800 }}>{r.name}</td>
                  <td style={td}>{r.qty||'—'}</td>
                  <td style={{ ...td, color:F.muted }}>{r.unit||'—'}</td>
                  <td style={{ ...td, color:F.muted }}>{r.location||'—'}</td>
                  <td style={{ ...td, color:F.muted }}>{r.notes||'—'}</td>
                  <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                    {canEdit && <button onClick={()=>setEdit({ ...r, _orig:r })} style={{ ...btn, padding:'4px 8px', fontSize:11 }}>Edit</button>}{' '}
                    {canDelete && <button onClick={()=>del(r)} style={{ ...btn, padding:'4px 8px', fontSize:11, color:F.red }}>Remove</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Fragment>
  );
}

const CONSIGN_DEFAULT_BUCKETS = [
  { id:'vermeer',    name:'Vermeer',               acct:'', branch:'', asOf:'', color:'gold' },
  { id:'ditchwitch', name:'Ditch Witch',           acct:'', branch:'', asOf:'', color:'orange' },
  { id:'filters',    name:'Filters & Tires',       acct:'', branch:'', asOf:'', color:'accent' },   // #YARD_CONSIGN_SPLIT_V1 — was 'msc' ("MSC · Filters & Tires")
  { id:'msc',        name:'MSC',                   acct:'', branch:'', asOf:'', color:'violet' },
];

const CONSIGN_CHEM_CODE = { vermeer:'V CSGN', ditchwitch:'D CSGN' };

// which Chemicals code feeds which bucket
const CONSIGN_CHEM_DIST = { vermeer:'fleet-chem-dist-vermeer', ditchwitch:'fleet-chem-dist-ditchwitch' };

// default order recipients
const _csgnId = () => Date.now().toString(36) + Math.random().toString(36).slice(2,7);

const _csgnMdy = (s) => { const m = String(s||'').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : (s||''); };

// hub tile (typed on-hand only)

export function YardConsignment({ sg, ss, F, canEdit, canDelete, userRole, upn, units }) {
  const [data, setData] = useState(null);
  const [chemInv, setChemInv] = useState([]);
  const [chemDist, setChemDist] = useState({});
  const [bid, setBid] = useState('vermeer');
  const [tab, setTab] = useState('inv');            // 'inv' | 'pulls' | 'orders'
  const [q, setQ] = useState('');
  const [binF, setBinF] = useState('');
  const [csSort, setCsSort] = useState({ k:'', dir:1 });   // #YARD_CONSIGN_SORT_V1 — click-to-sort headers; k:'' = vendor report order
  const [onlyShort, setOnlyShort] = useState(false);
  const [ohDraft, setOhDraft] = useState({});        // itemId -> typed string (saved on blur)
  const [edit, setEdit] = useState(null);
  const [pull, setPull] = useState(null);            // { item, unitNo, qty, note }
  const [imp, setImp] = useState(null);
  const [newBucket, setNewBucket] = useState(null);
  const [hdrEdit, setHdrEdit] = useState(null);
  const [ord, setOrd] = useState(null);              // { qtys:{itemId:n}, po, note }
  const [sendMsg, setSendMsg] = useState('');
  const [pullMonth, setPullMonth] = useState('');

  useEffect(() => { (async () => {
    let d = null;
    try { d = await sg(CONSIGN_KEY); } catch {}
    d = (d && typeof d === 'object' && !Array.isArray(d)) ? d : {};
    const buckets = Array.isArray(d.buckets) && d.buckets.length ? d.buckets.slice() : CONSIGN_DEFAULT_BUCKETS.map(b => ({ ...b }));
    CONSIGN_DEFAULT_BUCKETS.forEach(b => { if (!buckets.some(x => x.id === b.id)) buckets.push({ ...b }); });
    const next = { buckets, items: { ...(d.items||{}) }, pulls: Array.isArray(d.pulls) ? d.pulls : [], orders: Array.isArray(d.orders) ? d.orders : [], seeded: { ...(d.seeded||{}) } };
    let changed = !d.buckets;
    // One-time Vermeer self-seed (same pattern as FUEL_IMPAC_SEED / PO_SEED): only if never seeded AND empty.
    if (!next.seeded.vermeer && !(next.items.vermeer||[]).length && CONSIGN_VERMEER_SEED && Array.isArray(CONSIGN_VERMEER_SEED.items)) {
      next.items.vermeer = CONSIGN_VERMEER_SEED.items.map(r => ({ id:_csgnId(), ...r }));
      next.buckets = next.buckets.map(b => b.id === 'vermeer' ? { ...b, acct:CONSIGN_VERMEER_SEED.acct, branch:CONSIGN_VERMEER_SEED.branch, asOf:CONSIGN_VERMEER_SEED.asOf } : b);
      next.seeded.vermeer = true; changed = true;
    }
    // #YARD_CONSIGN_SPLIT_V1 — one-time: old 'msc' bucket (all filters & tires) → 'filters'; 'msc' becomes MSC alone.
    if (!next.seeded.mscSplit) {
      const oldList = Array.isArray(next.items.msc) ? next.items.msc : [];
      const oldB = next.buckets.find(b => b.id === 'msc') || {};
      if (oldList.length) next.items.filters = [ ...(Array.isArray(next.items.filters) ? next.items.filters : []), ...oldList ];
      next.items.msc = [];
      const moved = (x) => (x && x.bucketId === 'msc') ? { ...x, bucketId:'filters', vendor:'Filters & Tires' } : x;
      next.pulls = next.pulls.map(moved); next.orders = next.orders.map(moved);
      const defIds = CONSIGN_DEFAULT_BUCKETS.map(b => b.id);
      next.buckets = next.buckets.map(b => b.id === 'filters' ? { ...b, acct:oldB.acct||'', branch:oldB.branch||'', asOf:oldB.asOf||'', ...(Array.isArray(oldB.to) && oldB.to.length ? { to:oldB.to } : {}) }
        : b.id === 'msc' ? { ...CONSIGN_DEFAULT_BUCKETS.find(x => x.id === 'msc') } : b);
      next.buckets = [ ...defIds.map(id => next.buckets.find(b => b.id === id)).filter(Boolean), ...next.buckets.filter(b => !defIds.includes(b.id)) ];
      next.seeded.mscSplit = true; changed = true;
    }
    // One-time MSC self-seed from the sales-history list (no ceilings — see header note).
    if (!next.seeded.msc && !(next.items.msc||[]).length && CONSIGN_MSC_SEED && Array.isArray(CONSIGN_MSC_SEED.items)) {
      next.items.msc = CONSIGN_MSC_SEED.items.map(r => ({ id:_csgnId(), ...r }));
      next.buckets = next.buckets.map(b => b.id === 'msc' ? { ...b, acct:CONSIGN_MSC_SEED.acct, branch:CONSIGN_MSC_SEED.branch||'', asOf:CONSIGN_MSC_SEED.asOf } : b);
      next.seeded.msc = true; changed = true;
    }
    // #YARD_CONSIGN_NODASH_V1 — one-time: MSC prints some part numbers with a leading "-" (-7554-A). Bill: strip it.
    // MSC's original is kept on the line as vPart. Pulls / order lines on those parts are renamed to match.
    if (!next.seeded.mscDash) {
      const nd = (x) => (x && typeof x.part === 'string' && /^-+/.test(x.part)) ? { ...x, vPart: x.vPart || x.part, part: x.part.replace(/^-+/, '') } : x;
      next.items.msc = (next.items.msc||[]).map(nd);
      next.pulls = next.pulls.map(p => (p && p.bucketId === 'msc') ? nd(p) : p);
      next.orders = next.orders.map(o => (o && o.bucketId === 'msc' && Array.isArray(o.lines)) ? { ...o, lines: o.lines.map(nd) } : o);
      next.seeded.mscDash = true; changed = true;
    }
    setData(next);
    if (changed) { try { await ss(CONSIGN_KEY, next); } catch {} }
    try { const ci = await sg('fleet-chem-inventory'); setChemInv(Array.isArray(ci) ? ci : []); } catch {}
    const dist = {};
    for (const [k, key] of Object.entries(CONSIGN_CHEM_DIST)) { try { const v = await sg(key); dist[k] = Array.isArray(v) ? v.filter(Boolean) : []; } catch { dist[k] = []; } }
    setChemDist(dist);
  })(); }, [sg, ss]);

  const persist = async (next) => {
    setData(next);
    try { await ss(CONSIGN_KEY, next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };

  const btn = { background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:8, padding:'6px 12px', fontSize:12.5, fontWeight:700, cursor:'pointer' };
  const btnS = { ...btn, padding:'3px 8px', fontSize:11 };
  const btnGo = { ...btn, background:F.accent, color:'#04121C', borderColor:F.accent };
  const inp = { background:F.bg, border:`1px solid ${F.line}`, borderRadius:7, padding:'6px 9px', fontSize:12.5, color:F.text, width:'100%', boxSizing:'border-box' };
  const th = { padding:'8px 10px', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, textAlign:'left', position:'sticky', top:0, background:F.card, zIndex:1, whiteSpace:'nowrap' };
  const thR = { ...th, textAlign:'right' };
  const td = { padding:'5px 10px', fontSize:12.5, color:F.text, verticalAlign:'middle' };
  const tdR = { ...td, textAlign:'right' };
  const thC = { ...th, textAlign:'center', cursor:'pointer', userSelect:'none' };   // #YARD_CONSIGN_SORT_V1
  const thS = { ...th, cursor:'pointer', userSelect:'none' };
  const tdC = { ...td, textAlign:'center' };
  const lbl = { fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 };
  const panel = { background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'12px 14px', marginBottom:10 };
  const clrOf = (b) => F[(b && b.color) || 'accent'] || F.accent;
  // Same money cell as Chemicals (ChemMoney): $ pinned to its figure, group right-aligned, tabular digits.
  // #YARD_CONSIGN_SORT_V1 — pass w (in characters) to CENTRE the cell: the $+figure sit in a block as wide
  // as the widest figure in the column, the block is centred under its header, and the figures stay
  // right-aligned inside it so the decimal points still stack.
  const CMoney = ({ v, color, dim, zero, w }) => (Number(v)||0) === 0 && !zero
    ? <span style={{ display:'block', textAlign: w ? 'center' : 'right', color:F.muted }}>—</span>
    : (
      <span style={{ display:'flex', justifyContent: w ? 'center' : 'flex-end', fontWeight:800, fontVariantNumeric:'tabular-nums', color: color || (dim ? F.muted : F.text) }}>
        <span style={{ display:'inline-flex', gap:3, alignItems:'baseline', ...(w ? { minWidth:`${w}ch`, justifyContent:'flex-end' } : {}) }}><span style={{ color:F.muted, fontWeight:700 }}>$</span><span>{cents(v)}</span></span>
      </span>
    );
  const BigMoney = ({ v, color }) => (
    <span style={{ display:'inline-flex', gap:2, alignItems:'baseline', whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums', color }}><span style={{ color:F.muted, fontWeight:700, fontSize:'.85em' }}>$</span><span>{cents(v)}</span></span>
  );

  if (data === null) return <div style={{ fontSize:12, color:F.muted }}>Loading consignment…</div>;

  const bucket = data.buckets.find(b => b.id === bid) || data.buckets[0];
  const list = data.items[bucket.id] || [];
  const c = clrOf(bucket);
  const chemCode = CONSIGN_CHEM_CODE[bucket.id];
  const chemRows = chemCode ? chemInv.filter(x => String(x.code||'').trim() === chemCode) : [];
  // Chemicals link: pinned by hand (chemLink = product id or 'none'), else auto by same vendor code + exact each-price.
  const chemFor = (r) => {
    if (!chemCode || r.chemLink === 'none') return null;
    const hits = r.chemLink ? chemRows.filter(x => x.id === r.chemLink)
      : chemRows.filter(x => Math.abs(_csgnNum(x.cost) - _csgnNum(r.unit)) < 0.005 && _csgnNum(r.unit) > 0);
    return hits.length ? hits : null;
  };
  // Effective on hand: Chemicals (truth) for linked lines, else what Jerry typed; null = not counted yet.
  const ohOf = (r) => { const h = chemFor(r); if (h) return h.reduce((s,x) => s + _csgnNum(x.qty), 0); return _csgnHas(r.onHand) ? _csgnNum(r.onHand) : null; };
  const needOf = (r) => { const oh = ohOf(r); return (oh == null || !_csgnHas(r.qty)) ? null : Math.max(0, _csgnNum(r.qty) - oh); };   // #YARD_CONSIGN_SPLIT_V1 — blank ceiling = no order suggested

  const bins = [...new Set(list.map(r => String(r.bin||'').trim() || '(no bin)'))].sort();
  const ql = q.trim().toLowerCase();
  let rows = list.filter(r => (!ql || `${r.part} ${r.desc} ${r.bin}`.toLowerCase().includes(ql))
    && (!binF || (String(r.bin||'').trim() || '(no bin)') === binF)
    && (!onlyShort || ((needOf(r) || 0) > 0 && !chemFor(r))));
  // #YARD_CONSIGN_SORT_V1 — one table serves every bucket (Vermeer, Ditch Witch, MSC…), so this sorts all of them.
  // Blank / not-counted always sinks to the bottom whichever way the arrow points; ties fall back to Part #.
  const CS_COLS = {
    part: ['Part #',        r => String(r.part||'')],
    bin:  ['Bin',           r => String(r.bin||'').trim()],
    desc: ['Description',   r => String(r.desc||'')],
    qty:  ['Ceiling',       r => _csgnNum(r.qty)],
    oh:   ['On hand',       r => ohOf(r)],
    unit: ['Each',          r => _csgnNum(r.unit)],
    val:  ['Value on hand', r => { const o = ohOf(r); return o == null ? null : o * _csgnNum(r.unit); }],
    need: ['To order',      r => (chemFor(r) ? null : needOf(r))],
  };
  const CS_NUM = { qty:1, oh:1, unit:1, val:1, need:1 };
  if (csSort.k && CS_COLS[csSort.k]) {
    const f = CS_COLS[csSort.k][1];
    const byPart = (a,b) => String(a.part).localeCompare(String(b.part), undefined, { numeric:true });
    rows = rows.slice().sort((a,b) => {
      const x = f(a), y = f(b); const xe = x == null || x === '', ye = y == null || y === '';
      if (xe || ye) return xe && ye ? byPart(a,b) : (xe ? 1 : -1);
      const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric:true });
      return (cmp * csSort.dir) || byPart(a,b);
    });
  }
  // 1st click: text A→Z, numbers biggest first. 2nd click flips. 3rd click returns to vendor report order.
  const clickSort = (k) => setCsSort(s => { const d0 = CS_NUM[k] ? -1 : 1; return s.k !== k ? { k, dir:d0 } : (s.dir === d0 ? { k, dir:-d0 } : { k:'', dir:1 }); });
  const sortArrow = (k) => csSort.k === k ? (csSort.dir === 1 ? ' \u25B2' : ' \u25BC') : '';
  // widest figure in each money column (footer total included) → block width for the centred CMoney
  const wEach = Math.max(4, ...rows.map(r => cents(r.unit).length)) + 2;
  const wVal  = Math.max(4, cents(rows.reduce((t,r) => t + (ohOf(r)||0) * _csgnNum(r.unit), 0)).length) + 2;

  const ceilVal = consignTotal(list);
  const counted = list.filter(r => ohOf(r) != null);
  const ohVal = counted.reduce((s,r) => s + ohOf(r) * _csgnNum(r.unit), 0);
  // CHEM lines are ordered through Chemicals' own V CSGN / D CSGN reorder flow — kept out of this order so nothing is ordered twice.
  const short = counted.filter(r => needOf(r) > 0 && !chemFor(r));
  const shortVal = short.reduce((s,r) => s + needOf(r) * _csgnNum(r.unit), 0);
  const chemLinked = list.filter(r => chemFor(r)).length;
  const bPulls = (data.pulls||[]).filter(p => p.bucketId === bucket.id);
  const bOrders = (data.orders||[]).filter(o => o.bucketId === bucket.id);
  const thisMonth = iso(new Date()).slice(0,7);
  const pulledMonthVal = bPulls.filter(p => String(p.date||'').slice(0,7) === thisMonth).reduce((s,p) => s + _csgnNum(p.qty) * _csgnNum(p.each), 0);

  const setItems = (bucketId, nextList, extra) => persist({ ...data, ...(extra||{}), items: { ...data.items, [bucketId]: nextList } });

  // ── On hand (free-write) ──────────────────────────────────────────────────
  const commitOh = (r) => {
    if (!(r.id in ohDraft)) return;
    const raw = String(ohDraft[r.id]).trim();
    const d = { ...ohDraft }; delete d[r.id]; setOhDraft(d);
    const nextVal = raw === '' ? undefined : _csgnNum(raw);
    if ((nextVal === undefined && !_csgnHas(r.onHand)) || nextVal === r.onHand) return;
    const nextList = list.map(x => { if (x.id !== r.id) return x; const y = { ...x, ohAt: iso(new Date()), ohBy: upn || String(userRole||'') }; if (nextVal === undefined) delete y.onHand; else y.onHand = nextVal; return y; });
    setItems(bucket.id, nextList);
  };

  // ── Pull → Unit ───────────────────────────────────────────────────────────
  const unitSet = new Set((units||[]).map(u => String(u).trim().toUpperCase()));
  const savePull = () => {
    if (!pull) return;
    const unitNo = String(pull.unitNo||'').trim();
    const qty = _csgnNum(pull.qty);
    if (!unitNo) { alert('Pick the unit this part went on.'); return; }
    if (unitSet.size && !unitSet.has(unitNo.toUpperCase())) { alert(`${unitNo} isn't on the Equipment Master. Pick a unit from the list.`); return; }
    if (!(qty > 0)) { alert('Enter how many were pulled.'); return; }
    const r = pull.item;
    const masterUnit = (units||[]).find(u => String(u).trim().toUpperCase() === unitNo.toUpperCase()) || unitNo;
    if (_csgnHas(r.onHand) && qty > _csgnNum(r.onHand) && !window.confirm(`On hand shows ${r.onHand}, pulling ${qty}. Pull anyway? (On hand goes to 0.)`)) return;
    const rec = { id:_csgnId(), date: iso(new Date()), at: new Date().toISOString(), bucketId: bucket.id, vendor: bucket.name, itemId: r.id,
      part: r.part, desc: r.desc, bin: r.bin||'', qty, each: _csgnNum(r.unit), unitNo: masterUnit, note: String(pull.note||'').trim(), by: upn || String(userRole||'') };
    const nextList = list.map(x => x.id === r.id && _csgnHas(x.onHand) ? { ...x, onHand: Math.max(0, _csgnNum(x.onHand) - qty), ohAt: rec.date } : x);
    setPull(null);
    setItems(bucket.id, nextList, { pulls: [ ...(data.pulls||[]), rec ] });
  };
  const undoPull = (p) => {
    if (!window.confirm(`Undo pull of ${p.qty} × ${p.part} to ${p.unitNo}? The qty goes back on hand.`)) return;
    const bl = data.items[p.bucketId] || [];
    const nextList = bl.map(x => x.id === p.itemId && _csgnHas(x.onHand) ? { ...x, onHand: _csgnNum(x.onHand) + _csgnNum(p.qty) } : x);
    persist({ ...data, pulls: (data.pulls||[]).filter(x => x.id !== p.id), items: { ...data.items, [p.bucketId]: nextList } });
  };

  // ── Edit / add / remove part ──────────────────────────────────────────────
  const saveItem = () => {
    if (!edit) return;
    const part = String(edit.part||'').trim().replace(/^-+/, '');   // #YARD_CONSIGN_NODASH_V1 — no leading dash on a part #
    if (!part) { alert('Part # is required.'); return; }
    const base = edit._orig ? { ...edit._orig } : { id:_csgnId() };
    const rec = { ...base, part, bin:String(edit.bin||'').trim().toUpperCase(), desc:String(edit.desc||'').trim(), qty:_csgnNum(edit.qty), unit:_csgnNum(edit.unit) };
    if (String(edit.qty==null?'':edit.qty).trim() === '') delete rec.qty;   // #YARD_CONSIGN_SPLIT_V1 — blank ceiling stays blank (not 0)
    if (edit.chemLink) rec.chemLink = edit.chemLink; else delete rec.chemLink;
    const nextList = edit._orig ? list.map(r => r.id === rec.id ? rec : r) : [ ...list, rec ];
    setEdit(null);
    setItems(bucket.id, nextList);
  };
  const delItem = (r) => {
    if (!window.confirm(`Remove ${r.part} — ${r.desc} from ${bucket.name}?`)) return;
    setItems(bucket.id, list.filter(x => x.id !== r.id));
  };

  // ── Paste import (vendor report → Ceiling). On Hand / links are kept by Part #. ─
  const parseImport = (text) => {
    const out = [];
    String(text||'').split(/\r?\n/).forEach(line => {
      if (!line.trim() || /totals/i.test(line)) return;
      const cc = line.includes('\t') ? line.split('\t') : line.includes('|') ? line.split('|') : line.split(',');
      if (cc.length < 5) return;
      const [part0, bin, desc, qty, unit] = cc.map(x => String(x||'').trim());
      const part = part0.replace(/^-+/, '');   // #YARD_CONSIGN_NODASH_V1
      if (!part || !/^-?[\d.,]+$/.test(qty.replace(/[$\s]/g,''))) return;
      out.push({ part, bin: bin.toUpperCase(), desc, qty:_csgnNum(qty), unit:_csgnNum(unit) });
    });
    return out;
  };
  const runImport = () => {
    const parsed = parseImport(imp && imp.text);
    if (!parsed.length) { alert('No rows found. Paste 5 columns: Part # · Bin · Description · Ceiling Qty · Each $'); return; }
    const byPart = new Map(list.map(r => [String(r.part).toUpperCase(), r]));
    let nextList;
    if (imp.mode === 'replace') {
      if (!window.confirm(`Replace all ${list.length} ${bucket.name} lines with ${parsed.length} pasted lines? On Hand is kept for Part #s that match.`)) return;
      nextList = parsed.map(p => { const old = byPart.get(String(p.part).toUpperCase()); return old ? { ...old, ...p } : { id:_csgnId(), ...p }; });
    } else {
      nextList = list.slice();
      parsed.forEach(p => {
        const hit = byPart.get(String(p.part).toUpperCase());
        if (hit) nextList = nextList.map(r => r.id === hit.id ? { ...r, ...p } : r);
        else nextList.push({ id:_csgnId(), ...p });
      });
    }
    const buckets = imp.asOf ? data.buckets.map(b => b.id === bucket.id ? { ...b, asOf: imp.asOf } : b) : data.buckets;
    setImp(null);
    setItems(bucket.id, nextList, { buckets });
  };

  // ── Bucket admin ──────────────────────────────────────────────────────────
  const recipientsOf = (b) => cleanEmails((Array.isArray(b.to) && b.to.length) ? b.to : (chemDist[b.id] || []));   // #EMAIL_ADDR_CLEAN_V1
  const saveHdr = () => {
    const to = cleanEmails(hdrEdit.to);   // #EMAIL_ADDR_CLEAN_V1
    const buckets = data.buckets.map(b => b.id === bucket.id ? { ...b, name:String(hdrEdit.name||'').trim() || b.name, acct:hdrEdit.acct||'', branch:hdrEdit.branch||'', asOf:hdrEdit.asOf||'', to } : b);
    setHdrEdit(null);
    persist({ ...data, buckets });
  };
  const addBucket = () => {
    const name = String((newBucket && newBucket.name)||'').trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g,'-') + '-' + Date.now().toString(36).slice(-4);
    const palette = ['green','violet','amber','red'];
    const b = { id, name, acct:'', branch:'', asOf:'', color: palette[Math.max(0, data.buckets.length - 3) % palette.length] };
    setNewBucket(null); setBid(id);
    persist({ ...data, buckets: [ ...data.buckets, b ] });
  };
  const delBucket = () => {
    if (CONSIGN_DEFAULT_BUCKETS.some(b => b.id === bucket.id)) return;
    if (!window.confirm(`Delete the ${bucket.name} bucket and its ${list.length} lines?`)) return;
    const items = { ...data.items }; delete items[bucket.id];
    setBid('vermeer');
    persist({ ...data, buckets: data.buckets.filter(b => b.id !== bucket.id), items });
  };

  // ── Order request (Ceiling − On Hand) ─────────────────────────────────────
  const startOrder = () => {
    const qtys = {}; short.forEach(r => { qtys[r.id] = needOf(r); });
    if (!short.length) { alert(counted.length ? 'Everything counted is at its ceiling — nothing to order.' : 'Nothing counted yet. Enter On Hand first (or let Chemicals feed it), then build the order.'); return; }
    setOrd({ qtys, po:'', note:'' }); setTab('inv');
  };
  const esc = (v) => String(v==null?'':v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const m2 = (n) => '$' + cents(n);
  const orderHtml = (rec) => {
    const logoUrl = ((typeof window !== 'undefined' && window.location) ? window.location.origin : '') + '/ddd-logo-black.png';   // #FLEET_LOGO_BW_V1
    const cell = 'padding:4px 7px;border:1px solid #ccc';
    const head = 'padding:4px 7px;border:1px solid #ccc;background:#f0f0f0;text-align:left';
    const r_ = cell + ';text-align:right';
    const orderRows = rec.lines.map(l => '<tr><td style="' + cell + '">' + esc(l.part) + '</td><td style="' + cell + '">' + esc(l.desc) + '</td><td style="' + cell + '">' + esc(l.bin) + '</td><td style="' + r_ + '">' + esc(l.ceiling) + '</td><td style="' + r_ + '">' + esc(l.onHand) + '</td><td style="' + r_ + ';font-weight:bold">' + esc(l.qty) + '</td><td style="' + r_ + '">' + m2(l.each) + '</td><td style="' + r_ + '">' + m2(l.qty * l.each) + '</td></tr>').join('');
    const ohRows = (rec.onHandLines||[]).map(l => '<tr><td style="' + cell + '">' + esc(l.part) + '</td><td style="' + cell + '">' + esc(l.desc) + '</td><td style="' + r_ + '">' + esc(l.onHand) + '</td><td style="' + r_ + '">' + m2(l.each) + '</td><td style="' + r_ + '">' + m2(l.onHand * l.each) + '</td></tr>').join('');
    const who = String(rec.by||'').split('@')[0].split(/[._-]+/).filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ') || rec.by || '—';
    return '<div style="font-family:Arial,Helvetica,sans-serif;color:#111;font-size:13px">'
      + '<div style="display:flex;align-items:center;gap:12px;border-bottom:2px solid #1a1206;padding-bottom:10px;margin-bottom:14px"><img src="' + logoUrl + '" alt="Dakota Directional Drilling" style="height:48px" onerror="this.style.display=\'none\'"><div style="font-size:11px;color:#333;line-height:1.5"><b>Dakota Utility Contractors</b><br>2501 S. Oak Grove Rd. &middot; Ennis, TX 75119<br>Office: (972) 875-6344 &middot; dakotautility.com</div></div>'
      + '<h2 style="margin:0 0 4px;font-size:18px">Consignment Order Request &mdash; ' + esc(rec.vendor) + '</h2>'
      + '<div style="font-size:12px;color:#555;margin-bottom:4px">' + esc(_csgnMdy(rec.createdAt.slice(0,10))) + (rec.acct ? ' &middot; Acct ' + esc(rec.acct) : '') + (rec.branch ? ' &middot; ' + esc(rec.branch) : '') + '</div>'
      + (rec.po ? '<div style="font-size:13px;font-weight:bold;color:#a3720a;margin-bottom:6px">PO ' + esc(rec.po) + '</div>' : '')
      + '<table style="border-collapse:collapse;margin:10px 0 14px"><tr>'
      + '<td style="padding:6px 14px 6px 0"><div style="font-size:10px;color:#777;text-transform:uppercase">Value on hand</div><div style="font-size:16px;font-weight:bold">' + m2(rec.onHandValue) + '</div></td>'
      + '<td style="padding:6px 14px"><div style="font-size:10px;color:#777;text-transform:uppercase">Consignment ceiling</div><div style="font-size:16px;font-weight:bold">' + m2(rec.ceilingValue) + '</div></td>'
      + '<td style="padding:6px 14px"><div style="font-size:10px;color:#777;text-transform:uppercase">Expected order</div><div style="font-size:16px;font-weight:bold;color:#a3720a">' + m2(rec.orderValue) + ' &middot; ' + rec.lines.length + ' line' + (rec.lines.length===1?'':'s') + '</div></td>'
      + '</tr></table>'
      + '<h3 style="font-size:14px;margin:6px 0">To be ordered (ceiling &minus; on hand)</h3>'
      + '<table style="width:100%;border-collapse:collapse"><thead><tr><th style="' + head + '">Part #</th><th style="' + head + '">Description</th><th style="' + head + '">Bin</th><th style="' + head + ';text-align:right">Ceiling</th><th style="' + head + ';text-align:right">On hand</th><th style="' + head + ';text-align:right">Order</th><th style="' + head + ';text-align:right">Each</th><th style="' + head + ';text-align:right">Ext</th></tr></thead><tbody>' + orderRows + '</tbody></table>'
      + (rec.note ? '<div style="margin-top:10px;font-size:12px"><b>Note:</b> ' + esc(rec.note) + '</div>' : '')
      + (ohRows ? '<h3 style="font-size:14px;margin:18px 0 6px">On hand as counted</h3><table style="width:100%;border-collapse:collapse"><thead><tr><th style="' + head + '">Part #</th><th style="' + head + '">Description</th><th style="' + head + ';text-align:right">On hand</th><th style="' + head + ';text-align:right">Each</th><th style="' + head + ';text-align:right">Value</th></tr></thead><tbody>' + ohRows + '</tbody></table>' : '')
      + '<p style="color:#888;font-size:11.5px;margin-top:22px">Submitted by: ' + esc(who) + '</p>'
      + '<p style="color:#888;font-size:11.5px;margin:2px 0 0">#WEAREDAKOTA</p></div>';
  };
  const buildOrderRec = () => {
    const lines = list.filter(r => _csgnNum(ord.qtys[r.id]) > 0).map(r => ({ itemId:r.id, part:r.part, desc:r.desc, bin:r.bin||'', ceiling:_csgnNum(r.qty), onHand: ohOf(r) || 0, qty:_csgnNum(ord.qtys[r.id]), each:_csgnNum(r.unit) }));
    const onHandLines = counted.filter(r => ohOf(r) > 0).map(r => ({ part:r.part, desc:r.desc, onHand: ohOf(r), each:_csgnNum(r.unit) }));
    return { id:_csgnId(), bucketId: bucket.id, vendor: bucket.name, acct: bucket.acct||'', branch: bucket.branch||'',
      createdAt: new Date().toISOString(), by: upn || String(userRole||''), po: String(ord.po||'').trim(), note: String(ord.note||'').trim(),
      lines, onHandLines, onHandValue: ohVal, ceilingValue: ceilVal, orderValue: lines.reduce((s,l) => s + l.qty * l.each, 0), status:'draft' };
  };
  const printHtml = (html, title) => {
    const w = window.open('', '_blank'); if (!w) { alert('Pop-up blocked — allow pop-ups for ducdope.com.'); return; }
    w.document.write('<html><head><title>' + esc(title) + '</title></head><body style="margin:22px">' + html + '</body></html>');
    w.document.close(); setTimeout(() => w.print(), 350);
  };
  const submitOrder = async (send) => {
    const rec = buildOrderRec();
    if (!rec.lines.length) { alert('Enter an order qty on at least one line.'); return; }
    const to = recipientsOf(bucket);
    if (send && !to.length) { alert(`No ${bucket.name} order recipients yet — click Vendor info and add at least one email.`); return; }
    rec.to = send ? to : [];
    rec.status = send ? 'sending' : 'printed';
    const orders = [ ...(data.orders||[]), rec ];
    setOrd(null);
    await persist({ ...data, orders });
    if (!send) { printHtml(orderHtml(rec), `${bucket.name} consignment order`); return; }
    setSendMsg('Sending…');
    let status = 'sent', msg;
    try {
      const out = await sendVendorEmail({ to, subject: `Consignment Order Request — ${bucket.name} — ${_csgnMdy(iso(new Date()))}`, html: orderHtml(rec) });   // #EMAIL_SEND_AS_USER_V1
      if (out && out.ok) msg = `✓ Sent to ${out.count||to.length} recipient${(out.count||to.length)===1?'':'s'}${sentNote(out)}`;
      else { status = 'send failed'; msg = 'Saved, but the email failed: ' + ((out && (out.message||out.error)) || 'unknown error') + ' — Print it from Orders and send manually.'; }
    } catch (err) { status = 'send failed'; msg = 'Saved, but the email failed: ' + ((err && err.message) || 'network error') + ' — Print it from Orders and send manually.'; }
    setData(d => { const nx = { ...d, orders: (d.orders||[]).map(o => o.id === rec.id ? { ...o, status } : o) }; ss(CONSIGN_KEY, nx).catch(()=>{}); return nx; });
    setSendMsg(msg); setTab('orders');
    setTimeout(() => setSendMsg(''), 10000);
  };
  const receiveOrder = (o) => {
    if (!window.confirm(`Mark this ${o.vendor} order received? Order qtys are added to On Hand (lines Chemicals feeds are skipped — receive those in Chemicals).`)) return;
    const bl = data.items[o.bucketId] || [];
    const nextList = bl.map(x => {
      const l = o.lines.find(y => y.itemId === x.id); if (!l || chemFor(x)) return x;
      return { ...x, onHand: (_csgnHas(x.onHand) ? _csgnNum(x.onHand) : 0) + _csgnNum(l.qty), ohAt: iso(new Date()) };
    });
    persist({ ...data, items: { ...data.items, [o.bucketId]: nextList }, orders: (data.orders||[]).map(x => x.id === o.id ? { ...x, status:'received', receivedAt: iso(new Date()) } : x) });
  };

  // ── Exports ──────────────────────────────────────────────────────────────
  const downloadCsv = (name, head, body) => {
    // #YARD_CONSIGN_NODASH_V1 — cells Excel would rewrite on open (leading zero; digit-dash-digit like 10-30, 05-4745 or 7378-1 read as a date; 1/4; 1E5; a leading = + - @)
    // go out as ="text" so they show exactly as stored. Plain numbers and MM/DD/YY dates are left alone.
    const xl = (s) => (/^0\d+$/.test(s) || /^\d+(-\d+){1,2}$/.test(s) || /^\d{1,2}\/\d{1,4}$/.test(s) || /^\d+[eE]\d+$/.test(s) || (/^[=+\-@]/.test(s) && !/^-?[\d.,]+$/.test(s))) ? '="' + s.replace(/"/g,'""') + '"' : s;
    const e = (v) => { const s = xl(String(v==null?'':v)); return /[",\n]/.test(s) ? '"' + s.replace(/"/g,'""') + '"' : s; };
    const blob = new Blob([[head, ...body].map(a => a.map(e).join(',')).join('\n')], { type:'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const exportInv = () => downloadCsv(`Consignment-${bucket.name.replace(/[^A-Za-z0-9]+/g,'')}-${iso(new Date())}.csv`,
    ['Part #','Bin','Description','Ceiling','On Hand','Source','Each','Value On Hand','To Order','Order $'],
    list.map(r => { const oh = ohOf(r); const n = needOf(r); return [r.part, r.bin, r.desc, r.qty, oh == null ? '' : oh, chemFor(r) ? 'Chemicals' : (oh == null ? '' : 'Yard'), _csgnNum(r.unit).toFixed(2), oh == null ? '' : (oh*_csgnNum(r.unit)).toFixed(2), n == null ? '' : n, n ? (n*_csgnNum(r.unit)).toFixed(2) : '']; }));
  const printSheet = () => {
    const groups = {};
    list.forEach(r => { const k = String(r.bin||'').trim() || '(no bin)'; (groups[k] = groups[k] || []).push(r); });
    const sections = Object.keys(groups).sort().map(k => '<tr><td colspan="5" style="background:#eee;font-weight:bold;padding:4px 6px">' + esc(k) + ' (' + groups[k].length + ')</td></tr>'
      + groups[k].sort((a,b)=>String(a.part).localeCompare(String(b.part))).map(r => '<tr><td>' + esc(r.part) + '</td><td>' + esc(r.desc) + (chemFor(r) ? ' <i style="color:#a3720a">(Chemicals)</i>' : '') + '</td><td style="text-align:right">' + esc(r.qty) + '</td><td style="text-align:right">' + (ohOf(r) == null ? '' : esc(ohOf(r))) + '</td><td style="border-bottom:1px solid #999;width:80px"></td></tr>').join('')).join('');
    printHtml('<style>body{font-family:Arial;font-size:11px}table{width:100%;border-collapse:collapse}td,th{padding:3px 6px;border-bottom:1px solid #ddd;text-align:left}th{background:#333;color:#fff}</style>'
      + '<h2 style="margin:0">' + esc(bucket.name) + ' — Consignment Count Sheet</h2><div style="margin:4px 0 10px;color:#555">'
      + (bucket.acct ? 'Acct ' + esc(bucket.acct) + ' · ' : '') + list.length + ' lines · Printed ' + esc(_csgnMdy(iso(new Date()))) + ' · Counted by ________</div>'
      + '<table><thead><tr><th>Part #</th><th>Description</th><th style="text-align:right">Ceiling</th><th style="text-align:right">Last on hand</th><th>Count</th></tr></thead><tbody>' + sections + '</tbody></table>', `${bucket.name} count sheet`);
  };

  const grandCeil = data.buckets.reduce((s,b) => s + consignTotal(data.items[b.id]), 0);
  const pickBucket = (id) => { setBid(id); setQ(''); setBinF(''); setEdit(null); setImp(null); setHdrEdit(null); setOrd(null); setPull(null); setOhDraft({}); };

  return (
    <Fragment>
      <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap', marginBottom:4 }}>
        <div style={{ fontSize:16, fontWeight:900 }}>Consignment</div>
        <div style={{ fontSize:11.5, color:F.muted }}>Vendor-owned stock on our shelves. Vendor qty = ceiling · On Hand = what's actually there · pulls to a unit are charged to Dakota.</div>
        <div style={{ flex:1 }} />
        <div style={{ fontSize:12, color:F.muted }}>All vendors · ceiling <b style={{ color:F.text }}><BigMoney v={grandCeil} /></b></div>
      </div>

      {/* Buckets */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px,1fr))', gap:10, margin:'10px 0 14px' }}>
        {data.buckets.map(b => {
          const bl = data.items[b.id] || []; const bc = clrOf(b); const on = b.id === bucket.id;
          return (
            <button key={b.id} onClick={()=>pickBucket(b.id)}
              style={{ textAlign:'left', cursor:'pointer', background: on ? F.cardHi : F.card, border:`1px solid ${on ? bc : F.line}`,
                borderTop:`3px solid ${bc}`, borderRadius:12, padding:'12px 14px', ...(on ? glow(bc) : {}) }}>
              <div style={{ fontSize:14, fontWeight:900, color:F.text }}>{b.name}</div>
              <div style={{ fontSize:12, fontWeight:800, color:bc, marginTop:4 }}>{bl.length ? (consignTotal(bl) > 0 ? <Fragment><BigMoney v={consignTotal(bl)} /> ceiling · {bl.length} lines</Fragment> : `${bl.length} lines · ceilings not set`) : 'empty — paste a report'}</div>
              <div style={{ fontSize:10.5, color:F.muted, marginTop:2 }}>{b.asOf ? `vendor report ${_csgnMdy(b.asOf)}` : ' '}</div>
            </button>
          );
        })}
        {canEdit && (newBucket ? (
          <div style={{ background:F.card, border:`1px dashed ${F.line}`, borderRadius:12, padding:'10px 12px', display:'flex', flexDirection:'column', gap:6 }}>
            <input autoFocus value={newBucket.name} onChange={e=>setNewBucket({ name:e.target.value })} onKeyDown={e=>{ if (e.key==='Enter') addBucket(); }} placeholder="Vendor name" style={inp} />
            <div style={{ display:'flex', gap:6 }}><button onClick={addBucket} style={{ ...btnGo, padding:'4px 10px' }}>Add</button><button onClick={()=>setNewBucket(null)} style={{ ...btn, padding:'4px 10px' }}>Cancel</button></div>
          </div>
        ) : (
          <button onClick={()=>setNewBucket({ name:'' })} style={{ cursor:'pointer', background:'transparent', border:`1px dashed ${F.line}`, borderRadius:12, color:F.muted, fontSize:13, fontWeight:800 }}>+ Bucket</button>
        ))}
      </div>

      {/* Selected bucket summary */}
      <div style={{ background:F.card, border:`1px solid ${F.line}`, borderLeft:`4px solid ${c}`, borderRadius:12, padding:'10px 14px', marginBottom:10, display:'flex', gap:22, alignItems:'center', flexWrap:'wrap' }}>
        <div>
          <div style={{ fontSize:15, fontWeight:900 }}>{bucket.name}</div>
          <div style={{ fontSize:11, color:F.muted }}>{[bucket.acct && `Acct ${bucket.acct}`, bucket.branch, bucket.asOf && `vendor report ${_csgnMdy(bucket.asOf)}`].filter(Boolean).join(' · ') || 'no vendor report loaded yet'}</div>
          <div style={{ fontSize:10.5, color:F.muted }}>Orders to: {recipientsOf(bucket).length ? recipientsOf(bucket).join(', ') : <span style={{ color:F.amber }}>not set — Vendor info</span>}</div>
        </div>
        <div><div style={lbl}>Ceiling</div><div style={{ fontSize:16, fontWeight:900 }}><BigMoney v={ceilVal} /></div><div style={{ fontSize:10, color:F.muted }}>{list.length} line{list.length===1?'':'s'} on report</div></div>{/* #YARD_CONSIGN_SORT_V1 — third line added so Ceiling sits level with the other three tiles */}
        <div><div style={lbl}>Value on hand</div><div style={{ fontSize:16, fontWeight:900, color:F.green }}><BigMoney v={ohVal} /></div><div style={{ fontSize:10, color:F.muted }}>{counted.length} of {list.length} counted{chemLinked ? ` · ${chemLinked} from Chemicals` : ''}</div></div>
        <div><div style={lbl}>To order</div><div style={{ fontSize:16, fontWeight:900, color: short.length ? F.orange : F.muted }}><BigMoney v={shortVal} /></div><div style={{ fontSize:10, color:F.muted }}>{short.length} line{short.length===1?'':'s'} below ceiling</div></div>
        <div><div style={lbl}>Pulled {thisMonth.slice(5)}/{thisMonth.slice(2,4)}</div><div style={{ fontSize:16, fontWeight:900, color:F.violet }}><BigMoney v={pulledMonthVal} /></div><div style={{ fontSize:10, color:F.muted }}>charged to Dakota</div></div>
        <div style={{ flex:1 }} />
        <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
          {canEdit && <button onClick={startOrder} style={{ ...btn, background:F.orange, color:'#1A1300', borderColor:F.orange }}>Build order request</button>}
          <div style={{ display:'flex', gap:6 }}>
            {canEdit && <button onClick={()=>setHdrEdit({ name:bucket.name, acct:bucket.acct||'', branch:bucket.branch||'', asOf:bucket.asOf||'', to:(Array.isArray(bucket.to) && bucket.to.length ? bucket.to : recipientsOf(bucket)).join(', ') })} style={btnS}>Vendor info</button>}
            {canDelete && !CONSIGN_DEFAULT_BUCKETS.some(b => b.id === bucket.id) && <button onClick={delBucket} style={{ ...btnS, color:F.red }}>Delete bucket</button>}
          </div>
        </div>
      </div>
      {sendMsg && <div style={{ fontSize:12, fontWeight:800, color: sendMsg.startsWith('✓') ? F.green : sendMsg === 'Sending…' ? F.gold : F.red, marginBottom:8 }}>{sendMsg}</div>}

      {hdrEdit && (
        <div style={panel}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:10 }}>
            {[['name','Vendor / bucket name'],['acct','Our acct #'],['branch','Branch'],['asOf','Vendor report date']].map(([k,l]) => (
              <div key={k}><div style={lbl}>{l}</div><input type={k==='asOf'?'date':'text'} value={hdrEdit[k]||''} onChange={e=>setHdrEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} /></div>
            ))}
          </div>
          <div style={{ marginTop:10 }}><div style={lbl}>Order request emails go to (comma-separated)</div><input value={hdrEdit.to||''} onChange={e=>setHdrEdit(d=>({ ...d, to:e.target.value }))} style={inp} placeholder="rep@vendor.com, bill.payne@dakotautility.com" /></div>
          <div style={{ display:'flex', gap:8, marginTop:10 }}><button onClick={saveHdr} style={btnGo}>Save</button><button onClick={()=>setHdrEdit(null)} style={btn}>Cancel</button></div>
        </div>
      )}

      {/* Order request builder */}
      {ord && (
        <div style={{ ...panel, borderColor:F.orange }}>
          <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap', marginBottom:8 }}>
            <div style={{ fontSize:14, fontWeight:900 }}>Order request — {bucket.name}</div>
            <div style={{ fontSize:11.5, color:F.muted }}>Qty defaults to Ceiling − On Hand. Change or zero out any line.</div>
          </div>
          <div style={{ maxHeight:'45vh', overflow:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr><th style={th}>Part #</th><th style={th}>Description</th><th style={thR}>Ceiling</th><th style={thR}>On hand</th><th style={thR}>Order</th><th style={thR}>Each</th><th style={thR}>Ext</th></tr></thead>
              <tbody>
                {short.map(r => (
                  <tr key={r.id} style={{ borderTop:`1px solid ${F.line}` }}>
                    <td style={{ ...td, fontFamily:'Consolas, monospace', fontSize:12, fontWeight:800 }}>{r.part}</td>
                    <td style={td}>{r.desc}</td>
                    <td style={tdR}>{r.qty}</td>
                    <td style={tdR}>{ohOf(r)}</td>
                    <td style={tdR}><input inputMode="numeric" value={ord.qtys[r.id]==null?'':ord.qtys[r.id]} onChange={e=>{ const v = e.target.value.replace(/[^\d.]/g,''); setOrd(o=>({ ...o, qtys:{ ...o.qtys, [r.id]:v } })); }} style={{ ...inp, width:64, textAlign:'right', padding:'3px 6px' }} /></td>
                    <td style={tdR}><CMoney v={r.unit} dim /></td>
                    <td style={tdR}><CMoney v={_csgnNum(ord.qtys[r.id]) * _csgnNum(r.unit)} color={F.orange} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'160px 1fr', gap:10, marginTop:10 }}>
            <div><div style={lbl}>PO #</div><input value={ord.po} onChange={e=>setOrd(o=>({ ...o, po:e.target.value }))} style={inp} /></div>
            <div><div style={lbl}>Note to vendor</div><input value={ord.note} onChange={e=>setOrd(o=>({ ...o, note:e.target.value }))} style={inp} /></div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:10, flexWrap:'wrap' }}>
            <button onClick={()=>submitOrder(true)} style={{ ...btn, background:F.orange, color:'#1A1300', borderColor:F.orange }}>Send to {bucket.name}{recipientsOf(bucket).length ? ` (${recipientsOf(bucket).length})` : ''}</button>
            <button onClick={()=>submitOrder(false)} style={btn}>🖨 Print instead</button>
            <button onClick={()=>setOrd(null)} style={btn}>Cancel</button>
            <div style={{ flex:1 }} />
            <div style={{ fontSize:13, fontWeight:900 }}>Order total <span style={{ color:F.orange }}><BigMoney v={short.reduce((s,r) => s + _csgnNum(ord.qtys[r.id]) * _csgnNum(r.unit), 0)} /></span></div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
        {[['inv','Inventory', list.length], ['pulls','Pulls → Units', bPulls.length], ['orders','Order requests', bOrders.length]].map(([k,l,n]) => (
          <button key={k} onClick={()=>setTab(k)} style={{ ...btn, borderRadius:20, ...(tab===k ? { background:c, color:'#1A1300', borderColor:c } : {}) }}>{l} <span style={{ opacity:.75 }}>({n})</span></button>
        ))}
      </div>

      {tab === 'inv' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search part #, description, bin…" style={{ ...inp, width:240 }} />
            <select value={binF} onChange={e=>setBinF(e.target.value)} style={{ ...inp, width:150 }}>
              <option value="">All bins ({bins.length})</option>
              {bins.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <button onClick={()=>setCsSort(s => s.k ? { k:'', dir:1 } : { k:'bin', dir:1 })} title={csSort.k ? 'Click to go back to vendor report order' : 'Click to sort by bin — or click any column header'} style={{ ...btn, ...(csSort.k ? { borderColor:c, color:c } : {}) }}>{csSort.k ? `Sorted by ${CS_COLS[csSort.k][0]}${sortArrow(csSort.k)}` : 'Report order'}</button>
            <button onClick={()=>setOnlyShort(v=>!v)} style={{ ...btn, ...(onlyShort ? { borderColor:F.orange, color:F.orange } : {}) }}>{onlyShort ? 'Below ceiling only' : 'All lines'}</button>
            <div style={{ flex:1 }} />
            {list.length > 0 && <button onClick={printSheet} style={btn}>🖨 Count sheet</button>}
            {list.length > 0 && <button onClick={exportInv} style={btn}>CSV</button>}
            {canEdit && <button onClick={()=>setImp({ text:'', mode: list.length ? 'merge' : 'replace', asOf: iso(new Date()) })} style={btn}>Paste report</button>}
            {canEdit && <button onClick={()=>setEdit({ part:'', bin:'', desc:'', qty:'', unit:'', chemLink:'' })} style={btnGo}>+ Add part</button>}
          </div>

          {imp && (
            <div style={panel}>
              <div style={{ fontSize:12, color:F.muted, marginBottom:6 }}>Paste the vendor's consignment report (Excel or CSV) — 5 columns in this order: <b style={{ color:F.text }}>Part # · Bin · Description · Qty (ceiling) · Each $</b>. Header and TOTALS lines are skipped. On Hand is kept for matching Part #s.</div>
              <textarea value={imp.text} onChange={e=>setImp(d=>({ ...d, text:e.target.value }))} rows={8} style={{ ...inp, fontFamily:'Consolas, monospace', fontSize:11.5 }} placeholder={'296505099\t220X500\tSWIVEL-MUD SWIV\t1\t7445.13'} />
              <div style={{ display:'flex', gap:12, alignItems:'center', marginTop:8, flexWrap:'wrap' }}>
                <label style={{ fontSize:12 }}><input type="radio" checked={imp.mode==='merge'} onChange={()=>setImp(d=>({ ...d, mode:'merge' }))} /> Merge — update matching Part #s, add new</label>
                <label style={{ fontSize:12 }}><input type="radio" checked={imp.mode==='replace'} onChange={()=>setImp(d=>({ ...d, mode:'replace' }))} /> Replace whole list (new vendor report)</label>
                <span style={{ fontSize:12 }}>Report date <input type="date" value={imp.asOf||''} onChange={e=>setImp(d=>({ ...d, asOf:e.target.value }))} style={{ ...inp, width:150, display:'inline-block' }} /></span>
                <span style={{ fontSize:11.5, color:F.muted }}>{parseImport(imp.text).length} rows read</span>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:10 }}><button onClick={runImport} style={btnGo}>Import</button><button onClick={()=>setImp(null)} style={btn}>Cancel</button></div>
            </div>
          )}

          {edit && (
            <div style={panel}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px,1fr))', gap:10 }}>
                {[['part','Part #'],['bin','Bin'],['desc','Description'],['qty','Ceiling qty'],['unit','Each $']].map(([k,l]) => (
                  <div key={k}><div style={lbl}>{l}</div><input value={edit[k]==null?'':edit[k]} onChange={e=>setEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} /></div>
                ))}
                {chemCode && (
                  <div style={{ gridColumn:'span 2' }}><div style={lbl}>On hand comes from Chemicals ({chemCode})</div>
                    <select value={edit.chemLink||''} onChange={e=>setEdit(d=>({ ...d, chemLink:e.target.value }))} style={inp}>
                      <option value="">Auto — match by each-price</option>
                      <option value="none">Not a Chemicals item (Yard counts it here)</option>
                      {chemRows.map(x => <option key={x.id} value={x.id}>{x.name} · {x.uom||''} · ${cents(x.cost)} · on hand {x.qty}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:10 }}><button onClick={saveItem} style={btnGo}>Save</button><button onClick={()=>setEdit(null)} style={btn}>Close without saving</button></div>
            </div>
          )}

          {pull && (
            <div style={{ ...panel, borderColor:F.violet }}>
              <div style={{ fontSize:13.5, fontWeight:900, marginBottom:8 }}>Pull → Unit · <span style={{ fontFamily:'Consolas, monospace' }}>{pull.item.part}</span> {pull.item.desc} <span style={{ fontSize:11.5, color:F.muted, fontWeight:700 }}>· on hand {_csgnHas(pull.item.onHand) ? pull.item.onHand : 'not counted'} · <BigMoney v={pull.item.unit} /> ea · charged to Dakota</span></div>
              <div style={{ display:'grid', gridTemplateColumns:'170px 90px 1fr', gap:10 }}>
                <div><div style={lbl}>Unit (Equipment Master)</div><input autoFocus list="fleet-master-unit-list" value={pull.unitNo} onChange={e=>setPull(p=>({ ...p, unitNo:e.target.value }))} style={inp} placeholder="Unit #" /></div>
                <div><div style={lbl}>Qty</div><input inputMode="numeric" value={pull.qty} onChange={e=>setPull(p=>({ ...p, qty:e.target.value.replace(/[^\d.]/g,'') }))} style={{ ...inp, textAlign:'right' }} /></div>
                <div><div style={lbl}>Note (job, mechanic, why)</div><input value={pull.note} onChange={e=>setPull(p=>({ ...p, note:e.target.value }))} onKeyDown={e=>{ if (e.key==='Enter') savePull(); }} style={inp} /></div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:10 }}>
                <button onClick={savePull} style={{ ...btn, background:F.violet, color:'#fff', borderColor:F.violet }}>Pull</button>
                <button onClick={()=>setPull(null)} style={btn}>Cancel</button>
                <span style={{ fontSize:12, color:F.muted }}>Charge: <b style={{ color:F.text }}><BigMoney v={_csgnNum(pull.qty) * _csgnNum(pull.item.unit)} /></b></span>
              </div>
            </div>
          )}

          {!list.length ? (
            <div style={{ background:F.card, border:`1px dashed ${F.line}`, borderRadius:12, padding:'26px 18px', textAlign:'center', color:F.muted, fontSize:13 }}>
              No {bucket.name} consignment loaded yet. {canEdit ? 'Use Paste report (from their Excel/CSV) or + Add part.' : ''}
            </div>
          ) : (
            <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', maxHeight:'70vh' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {/* #YARD_CONSIGN_SORT_V1 — every header sorts; the five figure columns are centred */}
                  <th style={thS} onClick={()=>clickSort('part')}>Part #{sortArrow('part')}</th><th style={thS} onClick={()=>clickSort('bin')}>Bin{sortArrow('bin')}</th><th style={thS} onClick={()=>clickSort('desc')}>Description{sortArrow('desc')}</th>
                  <th style={thC} onClick={()=>clickSort('qty')} title="Vendor report qty — the most we should have on the shelf">Ceiling{sortArrow('qty')}</th>
                  <th style={{ ...thC, color:F.green }} onClick={()=>clickSort('oh')} title="What's actually on the shelf">On hand{sortArrow('oh')}</th>
                  <th style={thC} onClick={()=>clickSort('unit')}>Each{sortArrow('unit')}</th>
                  <th style={thC} onClick={()=>clickSort('val')}>Value on hand{sortArrow('val')}</th>
                  <th style={{ ...thC, color:F.orange }} onClick={()=>clickSort('need')}>To order{sortArrow('need')}</th>
                  <th style={th}></th>
                </tr></thead>
                <tbody>
                  {rows.map(r => {
                    const chem = chemFor(r); const oh = ohOf(r); const need = needOf(r);
                    return (
                      <tr key={r.id} style={{ borderTop:`1px solid ${F.line}`, ...(_csgnHas(r.qty) && _csgnNum(r.qty) === 0 && !oh ? { opacity:.55 } : {}) }}>
                        <td style={{ ...td, fontWeight:800, fontFamily:'Consolas, monospace', fontSize:12 }}>{r.part}</td>
                        <td style={{ ...td, color:F.muted, whiteSpace:'nowrap' }}>{r.bin||'—'}</td>
                        <td style={td}>{r.desc}{r.ordN ? <div style={{ fontSize:10.5, color:F.muted }}>ordered {r.ordN}× · {r.ordQty} pcs{_csgnNum(r.pkg) > 1 ? ` · pack of ${r.pkg}` : ''} · ’{r.ordYrs}</div> : null}</td>{/* #YARD_CONSIGN_SPLIT_V1 — MSC order history, to help set a ceiling */}
                        <td style={{ ...tdC, color:F.muted, fontWeight:700 }} title={_csgnHas(r.qty) ? '' : 'No ceiling set — Edit to add one'}>{_csgnHas(r.qty) ? r.qty : '—'}</td>
                        <td style={{ ...tdC, whiteSpace:'nowrap' }}>
                          {chem ? (
                            <span title={'From Chemicals: ' + chem.map(x => `${x.name} ${x.uom||''} (${x.qty})`).join(' + ')} style={{ display:'inline-flex', gap:6, alignItems:'center' }}>
                              <span style={{ fontSize:9, fontWeight:900, color:F.gold, border:`1px solid ${F.gold}66`, borderRadius:4, padding:'0 4px' }}>CHEM</span>
                              <b style={{ color:F.green }}>{oh}</b>
                            </span>
                          ) : canEdit ? (
                            <input inputMode="numeric" value={r.id in ohDraft ? ohDraft[r.id] : (_csgnHas(r.onHand) ? r.onHand : '')}
                              onChange={e=>{ const v = e.target.value.replace(/[^\d.]/g,''); setOhDraft(d=>({ ...d, [r.id]:v })); }}
                              onBlur={()=>commitOh(r)} onKeyDown={e=>{ if (e.key==='Enter') e.currentTarget.blur(); }}
                              placeholder="—" title={r.ohAt ? `Last set ${_csgnMdy(r.ohAt)}` : 'Not counted yet'}
                              style={{ ...inp, width:62, textAlign:'center', padding:'3px 6px', fontWeight:800, color:F.green }} />
                          ) : (oh == null ? <span style={{ color:F.muted }}>—</span> : <b style={{ color:F.green }}>{oh}</b>)}
                        </td>
                        <td style={tdC}><CMoney v={r.unit} dim w={wEach} /></td>
                        <td style={tdC}>{oh == null ? <span style={{ color:F.muted }}>—</span> : <CMoney v={oh * _csgnNum(r.unit)} color={F.green} w={wVal} />}</td>
                        <td style={{ ...tdC, fontWeight:900, color: chem ? F.gold : need ? F.orange : F.muted }} title={chem ? 'Ordered through Chemicals (V/D CSGN reorder)' : ''}>{chem ? <span style={{ fontSize:9.5 }}>via Chem</span> : need == null ? '' : (need || '✓')}</td>
                        <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                          {canEdit && !chem && <button onClick={()=>setPull({ item:r, unitNo:'', qty:'1', note:'' })} style={{ ...btnS, color:F.violet, borderColor:`${F.violet}66` }} title="Part removed — assign it to a unit">Pull</button>}{' '}
                          {canEdit && <button onClick={()=>setEdit({ ...r, chemLink:r.chemLink||'', _orig:r })} style={btnS}>Edit</button>}{' '}
                          {canDelete && <button onClick={()=>delItem(r)} style={{ ...btnS, color:F.red }}>✕</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr style={{ borderTop:`2px solid ${F.line}` }}>
                  <td style={{ ...td, fontWeight:900 }} colSpan={3}>{rows.length === list.length ? 'Total' : `Showing ${rows.length} of ${list.length}`}</td>
                  <td style={{ ...tdC, fontWeight:900, color:F.muted }}>{rows.reduce((s,r)=>s+_csgnNum(r.qty),0)}</td>
                  <td style={{ ...tdC, fontWeight:900, color:F.green }}>{rows.reduce((s,r)=>s+(ohOf(r)||0),0)}</td>
                  <td style={td}></td>
                  <td style={tdC}><CMoney v={rows.reduce((s,r)=>s+(ohOf(r)||0)*_csgnNum(r.unit),0)} color={F.green} zero w={wVal} /></td>
                  <td style={{ ...tdC, fontWeight:900, color:F.orange }}>{rows.reduce((s,r)=>s+(chemFor(r) ? 0 : (needOf(r)||0)),0) || ''}</td>
                  <td style={td}></td>
                </tr></tfoot>
              </table>
            </div>
          )}
          {canEdit && list.length > 0 && <div style={{ fontSize:11, color:F.muted, marginTop:6 }}>Type what's actually on the shelf in On Hand — saves when you leave the box. Blank = not counted yet (no order suggested). CHEM lines read live from Chemicals — they're allocated to jobs and reordered in Chemicals, not here.</div>}
        </Fragment>
      )}

      {tab === 'pulls' && (() => {
        const months = [...new Set(bPulls.map(p => String(p.date||'').slice(0,7)))].sort().reverse();
        const pr = bPulls.filter(p => !pullMonth || String(p.date||'').slice(0,7) === pullMonth).slice().sort((a,b) => String(b.at||b.date).localeCompare(String(a.at||a.date)));
        const byUnit = {}; pr.forEach(p => { byUnit[p.unitNo] = (byUnit[p.unitNo]||0) + _csgnNum(p.qty) * _csgnNum(p.each); });
        const tot = pr.reduce((s,p) => s + _csgnNum(p.qty) * _csgnNum(p.each), 0);
        return (
          <Fragment>
            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
              <select value={pullMonth} onChange={e=>setPullMonth(e.target.value)} style={{ ...inp, width:160 }}>
                <option value="">All months</option>
                {months.map(m => <option key={m} value={m}>{m.slice(5)}/{m.slice(0,4)}</option>)}
              </select>
              <div style={{ fontSize:13, fontWeight:900 }}>Charged to Dakota <span style={{ color:F.violet }}><BigMoney v={tot} /></span></div>
              <div style={{ flex:1 }} />
              {pr.length > 0 && <button onClick={()=>downloadCsv(`Consignment-Pulls-${bucket.name.replace(/[^A-Za-z0-9]+/g,'')}-${pullMonth||'all'}.csv`, ['Date','Unit','Part #','Description','Qty','Each','Charge','Note','By'], pr.map(p => [_csgnMdy(p.date), p.unitNo, p.part, p.desc, p.qty, _csgnNum(p.each).toFixed(2), (_csgnNum(p.qty)*_csgnNum(p.each)).toFixed(2), p.note||'', p.by||'']))} style={btn}>CSV</button>}
            </div>
            {Object.keys(byUnit).length > 0 && (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                {Object.entries(byUnit).sort((a,b) => b[1]-a[1]).map(([u,v]) => (
                  <span key={u} style={{ fontSize:11.5, fontWeight:800, background:F.cardHi, border:`1px solid ${F.line}`, borderRadius:20, padding:'3px 10px' }}>{u} · <span style={{ color:F.violet }}><BigMoney v={v} /></span></span>
                ))}
              </div>
            )}
            {!pr.length ? <div style={{ fontSize:12.5, color:F.muted, padding:'14px 2px' }}>No pulls yet. Use the Pull button on a line in Inventory when a part comes off the shelf.</div> : (
              <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', maxHeight:'65vh' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr><th style={th}>Date</th><th style={th}>Unit</th><th style={th}>Part #</th><th style={th}>Description</th><th style={thR}>Qty</th><th style={thR}>Each</th><th style={thR}>Charge</th><th style={th}>Note</th><th style={th}>By</th><th style={th}></th></tr></thead>
                  <tbody>
                    {pr.map(p => (
                      <tr key={p.id} style={{ borderTop:`1px solid ${F.line}` }}>
                        <td style={{ ...td, whiteSpace:'nowrap' }}>{_csgnMdy(p.date)}</td>
                        <td style={{ ...td, fontWeight:900 }}>{p.unitNo}</td>
                        <td style={{ ...td, fontFamily:'Consolas, monospace', fontSize:12 }}>{p.part}</td>
                        <td style={td}>{p.desc}</td>
                        <td style={{ ...tdR, fontWeight:800 }}>{p.qty}</td>
                        <td style={tdR}><CMoney v={p.each} dim /></td>
                        <td style={tdR}><CMoney v={_csgnNum(p.qty) * _csgnNum(p.each)} color={F.violet} /></td>
                        <td style={{ ...td, color:F.muted }}>{p.note||''}</td>
                        <td style={{ ...td, color:F.muted, fontSize:11 }}>{String(p.by||'').split('@')[0]}</td>
                        <td style={{ ...td, textAlign:'right' }}>{canDelete && <button onClick={()=>undoPull(p)} style={{ ...btnS, color:F.red }}>Undo</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Fragment>
        );
      })()}

      {tab === 'orders' && (
        !bOrders.length ? <div style={{ fontSize:12.5, color:F.muted, padding:'14px 2px' }}>No order requests yet. Enter On Hand, then Build order request.</div> : (
          <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>PO</th><th style={thR}>Lines</th><th style={thR}>On hand $</th><th style={thR}>Order $</th><th style={th}>Status</th><th style={th}>By</th><th style={th}></th></tr></thead>
              <tbody>
                {bOrders.slice().sort((a,b) => String(b.createdAt).localeCompare(String(a.createdAt))).map(o => (
                  <tr key={o.id} style={{ borderTop:`1px solid ${F.line}` }}>
                    <td style={{ ...td, whiteSpace:'nowrap' }}>{_csgnMdy(String(o.createdAt).slice(0,10))}</td>
                    <td style={{ ...td, fontWeight:800, color:F.gold }}>{o.po||'—'}</td>
                    <td style={tdR}>{(o.lines||[]).length}</td>
                    <td style={tdR}><CMoney v={o.onHandValue} color={F.green} /></td>
                    <td style={tdR}><CMoney v={o.orderValue} color={F.orange} /></td>
                    <td style={{ ...td, fontWeight:800, color: o.status === 'received' ? F.green : o.status === 'send failed' ? F.red : F.gold }}>{o.status}{o.receivedAt ? ` ${_csgnMdy(o.receivedAt)}` : ''}</td>
                    <td style={{ ...td, color:F.muted, fontSize:11 }}>{String(o.by||'').split('@')[0]}</td>
                    <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                      <button onClick={()=>printHtml(orderHtml(o), `${o.vendor} consignment order`)} style={btnS}>🖨 Print</button>{' '}
                      {canEdit && o.status !== 'received' && <button onClick={()=>receiveOrder(o)} style={{ ...btnS, color:F.green }}>Mark received</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Fragment>
  );
}

// #FLEET_FUELWEEKLY_V1 (2026-09-02) — Fuel Weekly Spend tracker, added to the Yard hub per Bill's
// ask: "Fuel Weekly Spend (from Owner's Dash, under construction) — adding this tracker here that
// will feed Owner's Dash." Same manual-entry-grid posture as Job Cost's Weekly Payroll
// (#JC_PAYROLL_MANUAL_V1): 52 fixed week rows, gallons + $ per fuel type, TOTAL computes live, Save
// writes the whole year. Storage `fleet-fuel-weekly` = { [year]: { [wk]: { redGal, redCost,
// greenGal, greenCost, dieselGal, dieselCost, unlGal, unlCost, TOTAL } } } — Owner Dash's
// Fuel · Weekly tile (still a placeholder as of #OWNER_LEADCAL_ROW3_V1) reads this store read-only,
// same pattern its Weekly Payroll tile already uses against cost-payroll-weekly.
// #FLEET_FUELWEEKLY_V2 (2026-09-02) — Bill, after seeing the flat 52-week grid: "most current week
// on top... add the Sunday date... first 4 columns should be YARD/Avenue Fuel, next 4 would be Impac
// fuel (Operations)... Avenue fuel comes twice a week, whereas Impac is once a week... a scrolling
// table to add and it could summarize here... two tiles, one for entry and one for summary." Replaces
// the fixed 52-row grid (#FLEET_FUELWEEKLY_V1) with a dated ledger appended to as deliveries/reports
// actually come in (Avenue ~2x/wk, Impac 1x/wk — a fixed weekly grid assumed one entry per week,
// which was never true) plus an auto-computed weekly summary rolled up from it, newest week first,
// week-ending Sunday shown next to each WK. The summary is what still writes `fleet-fuel-weekly`
// (same shape/key as V1) so Owner Dash's FuelWeeklyTile and the YardFuelWeekly hub widget below need
// no changes — they keep reading the same store, it's just populated automatically now instead of
// typed in by hand.
//
// Column grouping, per Bill's literal column-position call on the old grid: the two fuel types that
// used to be columns 1-4 (RED Diesel, Green Diesel) are the YARD/AVENUE FUEL group — the bulk yard-
// tank delivery; the two that were columns 5-8 (Diesel, Unleaded) are the IMPAC FUEL (OPERATIONS)
// group — the weekly by-unit fuel-card report (unit-level detail isn't captured yet — see the Sep 2
// project notes on Job Cost fuel allocation being a future, not-yet-mapped-out step).
const FUEL_GROUPS = [
  { key: 'yard', label: 'YARD / AVENUE FUEL', short: 'Yard/Avenue', clr: 'gold', types: [['red', 'RED Diesel'], ['green', 'Green Diesel']] },
  { key: 'impac', label: 'IMPAC FUEL (OPERATIONS)', short: 'Impac', clr: 'accent', types: [['diesel', 'Diesel'], ['unl', 'Unleaded']] },
];

const FUEL_TYPES = FUEL_GROUPS.flatMap(g => g.types);

// same 4 types/order #FLEET_FUELWEEKLY_V1 used — fleet-fuel-weekly's stored shape is unchanged

// Monday of ISO week `w` in year `y` (inverse of weekOf.js's isoWeek/isoWeekYear) — used only to
// print the week-ending Sunday date next to a stored WK number; ledger entries themselves always
// carry a real date, so isoWeek/mondayOf handle those directly without this.
function isoWeekMonday(y, w) {
  const jan4 = new Date(y, 0, 4);
  const mon1 = mondayOf(jan4);
  const d = new Date(mon1);
  d.setDate(d.getDate() + (w - 1) * 7);
  return d;
}

const _fuelMdy = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;

const fuelWeekEndLabel = (y, w) => { const mon = isoWeekMonday(y, w); const sun = new Date(mon); sun.setDate(sun.getDate() + 6); return _fuelMdy(sun); };

const FUEL_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const _fuelMdyy = (d) => `${_fuelMdy(d)}/${String(d.getFullYear()).slice(-2)}`;

// #FLEET_FUELWEEKLY_V4_FIX — Bill: ledger date was rendering as raw YYYY-MM-DD; wants MM/DD/YY to match the rest of the app

// #FLEET_FUELWEEKLY_V2 — owns the ledger (`fleet-fuel-log`) and keeps the weekly rollup
// (`fleet-fuel-weekly`) in sync with it on every add/delete. Renders the two tiles Bill asked for.
// #FLEET_FUELWEEKLY_V3 (2026-09-02) — one-time self-seed from FUEL_IMPAC_SEED (Bill's real 34-week
// Impac history, pulled from "2026 FUEL by WEEK.xlsx" Summary tab) when the log is still empty —
// same self-seed pattern as CHECKRUN_SEED/PO_SEED elsewhere in the app.
export function FuelWeeklyPage({ sg, ss, F, canEdit }) {
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // Recompute the weekly rollup from the full entry list and persist it to `fleet-fuel-weekly` — same
  // shape #FLEET_FUELWEEKLY_V1 wrote by hand, so Owner Dash's tile and the hub widget below don't need
  // to know anything changed.
  const rollupAndSave = async (list) => {
    const roll = {};
    list.forEach(e => {
      if (!e.date) return;
      const d = new Date(e.date + 'T12:00:00');
      const y = isoWeekYear(d), w = isoWeek(d);
      roll[y] = roll[y] || {};
      const row = roll[y][w] = roll[y][w] || { redGal: 0, redCost: 0, greenGal: 0, greenCost: 0, dieselGal: 0, dieselCost: 0, unlGal: 0, unlCost: 0, TOTAL: 0 };
      FUEL_TYPES.forEach(([k]) => { row[k + 'Gal'] += Number(e[k + 'Gal']) || 0; row[k + 'Cost'] += Number(e[k + 'Cost']) || 0; });
    });
    Object.keys(roll).forEach(y => Object.keys(roll[y]).forEach(w => { const row = roll[y][w]; row.TOTAL = FUEL_TYPES.reduce((s, [k]) => s + row[k + 'Cost'], 0); }));
    try { await ss('fleet-fuel-weekly', roll); } catch (e) { setMsg('Saved the log, but could not update the weekly summary: ' + (e.message || e)); }
  };

  useEffect(() => { (async () => {
    try {
      const raw = await sg('fleet-fuel-log');
      let list = raw && Array.isArray(raw.entries) ? raw.entries : [];
      let toSave = null;
      if (!list.length) {
        // #FLEET_FUELWEEKLY_V4 — empty log: seed both real-history sources at once — Avenue
        // tank-log deliveries (FUEL_YARD_SEED) + Impac weekly reports (FUEL_IMPAC_SEED).
        toSave = [...FUEL_YARD_SEED, ...FUEL_IMPAC_SEED];
      } else if (!list.some(e => e.source === 'yard')) {
        // #FLEET_FUELWEEKLY_V4_FIX (2026-09-02) — a log that already has Impac-only history (from
        // before FUEL_YARD_SEED existed) never hits the empty-log branch above, so Avenue never got
        // added — caught when Bill's live Fuel page showed Impac rows only. Top up with the Avenue
        // seed once, without touching what's already logged.
        toSave = [...list, ...FUEL_YARD_SEED];
      }
      if (toSave) {
        list = toSave;
        setEntries(list);
        try { if (ss) await ss('fleet-fuel-log', { v: 1, entries: list }); } catch {}
        await rollupAndSave(list);
      } else {
        setEntries(list);
      }
    } catch { setEntries([]); }
    setLoaded(true);
  })(); }, [sg]);

  const addEntry = async (rec) => {
    if (!canEdit) return;
    setBusy(true);
    const next = [...entries, { id: Date.now(), by: '', at: new Date().toISOString(), ...rec }];
    setEntries(next);
    try {
      await ss('fleet-fuel-log', { v: 1, entries: next });
      await rollupAndSave(next);
      setMsg('Added.'); setTimeout(() => setMsg(''), 2500);
    } catch (e) { setMsg('Could not save: ' + (e.message || e)); }
    setBusy(false);
  };

  // #FLEET_FUELWEEKLY_V3 — batch add for the paste importer: one save for however many rows were
  // pasted in, instead of one round trip per row.
  const addEntries = async (recs) => {
    if (!canEdit || !recs || !recs.length) return;
    setBusy(true);
    const stamp = Date.now();
    const withIds = recs.map((rec, i) => ({ id: stamp + i, by: '', at: new Date().toISOString(), ...rec }));
    const next = [...entries, ...withIds];
    setEntries(next);
    try {
      await ss('fleet-fuel-log', { v: 1, entries: next });
      await rollupAndSave(next);
      setMsg(`Added ${withIds.length} row${withIds.length === 1 ? '' : 's'}.`); setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Could not save: ' + (e.message || e)); }
    setBusy(false);
  };

  const deleteEntry = async (id) => {
    if (!canEdit) return;
    if (!window.confirm('Remove this fuel entry?')) return;
    const next = entries.filter(e => e.id !== id);
    setEntries(next);
    try { await ss('fleet-fuel-log', { v: 1, entries: next }); await rollupAndSave(next); }
    catch (e) { setMsg('Could not save: ' + (e.message || e)); }
  };

  if (!loaded) return <div style={{ fontSize: 12, color: F.muted }}>Loading…</div>;

  return (
    <Fragment>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 4 }}>Fuel · Weekly</div>
      <div style={{ fontSize: 12, color: F.muted, marginBottom: 14, maxWidth: 680, lineHeight: 1.5 }}>
        Log each Yard/Avenue delivery and Impac report as it comes in — Avenue runs about twice a week,
        Impac once a week, so this isn't one row per week. The summary rolls whatever's logged into
        weeks (Mon–Sun) automatically and feeds Owner Dash's Fuel · Weekly tile.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: 14, alignItems: 'start' }}>
        <FuelEntryLedger F={F} canEdit={canEdit} entries={entries} busy={busy} msg={msg} onAdd={addEntry} onAddBatch={addEntries} onDelete={deleteEntry} />
        <FuelWeeklySummary F={F} entries={entries} />
      </div>
    </Fragment>
  );
}

// #FLEET_FUELWEEKLY_V2 — the "entry" tile: a dated log appended to, not a fixed weekly grid. The
// source picker (Yard/Avenue vs Impac) determines which two fuel types are being logged, matching
// the FUEL_GROUPS column-grouping above.
// #FLEET_FUELWEEKLY_V3 — adds a paste importer for Impac's Summary tab so a week (or a whole month's
// worth of rows, caught up in one paste) can be dropped in straight out of Excel instead of retyped.
function FuelEntryLedger({ F, canEdit, entries, busy, msg, onAdd, onAddBatch, onDelete }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayISO);
  const [source, setSource] = useState('yard');
  const [vals, setVals] = useState({});
  const [note, setNote] = useState('');
  const [invoice, setInvoice] = useState('');   // #FLEET_FUELWEEKLY_V4 — Avenue invoices carry an invoice number (see the sample invoice); only relevant for the Yard/Avenue source

  const group = FUEL_GROUPS.find(g => g.key === source);
  const num = (v) => Number(String(v || '').replace(/[^0-9.\-]/g, '')) || 0;

  const submit = () => {
    if (!date) return;
    const rec = { date, source, note: note.trim(), invoice: source === 'yard' ? invoice.trim() : '' };
    FUEL_TYPES.forEach(([k]) => { rec[k + 'Gal'] = 0; rec[k + 'Cost'] = 0; });
    let any = false;
    group.types.forEach(([k]) => { const g = num(vals[k + 'Gal']), c = num(vals[k + 'Cost']); rec[k + 'Gal'] = g; rec[k + 'Cost'] = c; if (g || c) any = true; });
    if (!any) return;
    onAdd(rec);
    setVals({}); setNote(''); setInvoice('');
  };

  // Paste importer — expects Bill's own Impac Summary-tab row order, tab-separated, copied straight
  // out of Excel: WEEK, START, END, DIESEL GALLON, Total Diesel Cost, PPG, Unleaded GALLON, Total
  // Unleaded Cost, ... (columns after that, e.g. Total Gallon/Spend, are ignored — they're re-derived).
  // START is Bill's Sunday week-start; it's snapped +1 day to the Monday so the row lands in the same
  // ISO week his own report calls it — the same alignment used to build the 34-week FUEL_IMPAC_SEED
  // backfill. Any line that doesn't parse to a real date (a header row, a blank row) is skipped.
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pastePreview, setPastePreview] = useState(null);

  const parsePastedDate = (s) => {
    s = (s || '').trim();
    let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m) { let yr = Number(m[3]); if (yr < 100) yr += 2000; return new Date(yr, Number(m[1]) - 1, Number(m[2])); }
    return null;
  };

  const parsePasteRows = (text) => {
    const rows = [];
    text.split('\n').map(l => l.trim()).filter(Boolean).forEach(line => {
      const c = line.split('\t');
      if (c.length < 5) return;
      const startD = parsePastedDate(c[1]);
      if (!startD) return;   // header row or junk — skip
      const dieselGal = num(c[3]), dieselCost = num(c[4]), unlGal = num(c[6]), unlCost = num(c[7]);
      if (!dieselGal && !dieselCost && !unlGal && !unlCost) return;
      const d = new Date(startD); d.setDate(d.getDate() + 1);
      rows.push({
        date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        source: 'impac', note: 'Pasted from Impac Summary',
        redGal: 0, redCost: 0, greenGal: 0, greenCost: 0, dieselGal, dieselCost, unlGal, unlCost,
      });
    });
    return rows;
  };

  const doParse = (txt) => { setPasteText(txt); setPastePreview(txt.trim() ? parsePasteRows(txt) : null); };
  const commitPaste = () => { if (pastePreview && pastePreview.length) { onAddBatch(pastePreview); setPasteText(''); setPastePreview(null); setShowPaste(false); } };

  const inp = { width: 88, background: F.bg, border: `1px solid ${F.line}`, borderRadius: 5, color: F.text, padding: '5px 7px', fontSize: 12 };
  const lbl = { fontSize: 9, fontWeight: 800, color: F.muted, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 3 };
  const srcBtn = (k, l) => {
    const clr = F[FUEL_GROUPS.find(g => g.key === k).clr];
    return (
      <button key={k} onClick={() => { setSource(k); setVals({}); }} style={{
        background: source === k ? clr : F.cardHi, color: source === k ? '#04121C' : F.text,
        border: `1px solid ${source === k ? clr : F.line}`, borderRadius: 20, padding: '6px 14px',
        fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}>{l}</button>
    );
  };

  return (
    <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 900, color: F.text, marginBottom: 10 }}>Log a delivery / report</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {srcBtn('yard', 'Yard / Avenue')}
        {srcBtn('impac', 'Impac (Ops)')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 10 }}>
        <div><span style={lbl}>Date</span><input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={!canEdit} style={{ ...inp, width: 138 }} /></div>
        {group.types.map(([k, l]) => (
          <Fragment key={k}>
            <div><span style={lbl}>{l} Gal</span><input value={vals[k + 'Gal'] || ''} disabled={!canEdit} onChange={e => setVals(v => ({ ...v, [k + 'Gal']: e.target.value }))} style={inp} /></div>
            <div><span style={lbl}>{l} $</span><input value={vals[k + 'Cost'] || ''} disabled={!canEdit} onChange={e => setVals(v => ({ ...v, [k + 'Cost']: e.target.value }))} style={inp} /></div>
          </Fragment>
        ))}
        {source === 'yard' && <div><span style={lbl}>Invoice #</span><input value={invoice} disabled={!canEdit} onChange={e => setInvoice(e.target.value)} placeholder="e.g. 86372" style={{ ...inp, width: 90 }} /></div>}
        <div><span style={lbl}>Note</span><input value={note} disabled={!canEdit} onChange={e => setNote(e.target.value)} placeholder="optional" style={{ ...inp, width: 130 }} /></div>
        {canEdit && <button onClick={submit} disabled={busy} style={{ background: F.accent, color: '#04121C', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>+ Add</button>}
      </div>
      {msg && <div style={{ fontSize: 11, fontWeight: 700, color: /Could not/.test(msg) ? F.red : F.green, marginBottom: 8 }}>{msg}</div>}
      {!canEdit && <div style={{ fontSize: 10.5, color: F.muted, marginBottom: 8 }}>View only — no edit access.</div>}

      {canEdit && (
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => setShowPaste(s => !s)} style={{ background: 'none', border: `1px dashed ${F.line}`, borderRadius: 6, color: F.accent, padding: '5px 10px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
            {showPaste ? '– Hide paste importer' : '+ Paste weekly / monthly rows (Impac)'}
          </button>
          {showPaste && (
            <div style={{ marginTop: 8, background: F.bg, border: `1px solid ${F.line}`, borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 10.5, color: F.muted, marginBottom: 6, lineHeight: 1.4 }}>
                Copy row(s) straight out of the Impac Summary tab — WEEK, START, END, DIESEL GALLON, Total
                Diesel Cost, PPG, Unleaded GALLON, Total Unleaded Cost — and paste below. One row updates a
                week; paste several rows at once to catch up a month. Sunday START dates auto-align to the
                app's Monday-start week.
              </div>
              <textarea
                value={pasteText}
                onChange={e => doParse(e.target.value)}
                placeholder={'34\t8/16/2026\t8/22/2026\t4597.65\t23864.27\t5.19\t744.6\t2789.33\t...'}
                style={{ width: '100%', minHeight: 70, background: F.card, border: `1px solid ${F.line}`, borderRadius: 6, color: F.text, padding: 8, fontSize: 11, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: pastePreview && pastePreview.length ? F.green : F.muted }}>
                  {pasteText.trim() ? (pastePreview && pastePreview.length ? `${pastePreview.length} row${pastePreview.length === 1 ? '' : 's'} recognized` : "No valid rows found — check it's tab-separated") : ' '}
                </div>
                <button onClick={commitPaste} disabled={!pastePreview || !pastePreview.length || busy} style={{
                  background: pastePreview && pastePreview.length ? F.accent : F.cardHi, color: pastePreview && pastePreview.length ? '#04121C' : F.muted,
                  border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11.5, fontWeight: 900, cursor: pastePreview && pastePreview.length ? 'pointer' : 'default' }}>
                  Add {pastePreview && pastePreview.length ? pastePreview.length : ''} row{pastePreview && pastePreview.length === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 10, fontWeight: 800, color: F.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 6, marginBottom: 6 }}>Logged entries · newest first</div>
      <div style={{ maxHeight: 420, overflow: 'auto', border: `1px solid ${F.line}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: '#0A0E14' }}>
              {['Date', 'Source', 'Detail', 'Total', ''].map((h, i) => (
                <th key={i} style={{ textAlign: i === 3 ? 'right' : 'left', padding: '5px 7px', fontSize: 9, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#0A0E14' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!entries.length && <tr><td colSpan={5} style={{ padding: '12px 8px', color: F.muted, fontSize: 11.5 }}>Nothing logged yet.</td></tr>}
            {[...entries].sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.at || '').localeCompare(a.at || '')).map(e => {
              const g = FUEL_GROUPS.find(x => x.key === e.source) || FUEL_GROUPS[0];
              const total = FUEL_TYPES.reduce((s, [k]) => s + (Number(e[k + 'Cost']) || 0), 0);
              const detail = g.types.map(([k, l]) => { const gal = Number(e[k + 'Gal']) || 0, cost = Number(e[k + 'Cost']) || 0; return (gal || cost) ? `${l} ${gal.toLocaleString()}gal $${Math.round(cost).toLocaleString()}` : null; }).filter(Boolean).join(' · ');
              return (
                <tr key={e.id} style={{ borderTop: `1px solid ${F.line}` }}>
                  <td style={{ padding: '4px 7px', color: F.text, fontWeight: 700, whiteSpace: 'nowrap' }}>{e.date ? _fuelMdyy(new Date(e.date + 'T12:00:00')) : ''}</td>
                  <td style={{ padding: '4px 7px', whiteSpace: 'nowrap' }}><span style={{ fontSize: 9.5, fontWeight: 800, color: F[g.clr], border: `1px solid ${F[g.clr]}55`, borderRadius: 5, padding: '1px 6px' }}>{g.short}</span></td>
                  <td style={{ padding: '4px 7px', color: F.muted, whiteSpace: 'nowrap' }}>{detail || '—'}{e.invoice ? ` · Inv #${e.invoice}` : ''}{e.note ? ` · ${e.note}` : ''}</td>
                  <td style={{ padding: '4px 7px', textAlign: 'right', fontWeight: 800, color: F.text, whiteSpace: 'nowrap' }}>${Math.round(total).toLocaleString()}</td>
                  <td style={{ padding: '4px 7px', textAlign: 'right' }}>{canEdit && <button onClick={() => onDelete(e.id)} style={{ background: 'none', border: 'none', color: F.red, cursor: 'pointer', fontSize: 13, fontWeight: 900 }} title="Remove">✕</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// #FLEET_FUELWEEKLY_V2 — the "summary" tile: auto-rolled totals from the ledger, most current period
// on top, split by the two source groups.
// #FLEET_FUELWEEKLY_V3 — adds a Weekly/Monthly toggle (monthly is derived by grouping weeks into the
// calendar month their Monday falls in — no separate monthly entry needed) and ‹ Prev / Next › paging
// that focuses and scrolls to one period at a time while leaving the full trend table visible.
function FuelWeeklySummary({ F, entries }) {
  const roll = {};
  entries.forEach(e => {
    if (!e.date) return;
    const d = new Date(e.date + 'T12:00:00');
    const y = isoWeekYear(d), w = isoWeek(d);
    roll[y] = roll[y] || {};
    const row = roll[y][w] = roll[y][w] || {};
    FUEL_TYPES.forEach(([k]) => { row[k + 'Gal'] = (row[k + 'Gal'] || 0) + (Number(e[k + 'Gal']) || 0); row[k + 'Cost'] = (row[k + 'Cost'] || 0) + (Number(e[k + 'Cost']) || 0); });
  });
  const weekRows = [];
  Object.keys(roll).forEach(y => Object.keys(roll[y]).forEach(w => weekRows.push({ y: Number(y), w: Number(w), row: roll[y][w] })));
  weekRows.sort((a, b) => b.y - a.y || b.w - a.w);   // most current week on top

  const monthMap = {};
  weekRows.forEach(({ y, w, row }) => {
    const mon = isoWeekMonday(y, w);
    const my = mon.getFullYear(), mm = mon.getMonth();
    const key = my + '-' + mm;
    if (!monthMap[key]) monthMap[key] = { y: my, m: mm, row: {} };
    FUEL_TYPES.forEach(([k]) => {
      monthMap[key].row[k + 'Gal'] = (monthMap[key].row[k + 'Gal'] || 0) + (row[k + 'Gal'] || 0);
      monthMap[key].row[k + 'Cost'] = (monthMap[key].row[k + 'Cost'] || 0) + (row[k + 'Cost'] || 0);
    });
  });
  const monthRows = Object.values(monthMap).sort((a, b) => b.y - a.y || b.m - a.m);

  const allTimeTotal = weekRows.reduce((s, r) => s + FUEL_TYPES.reduce((ss, [k]) => ss + (r.row[k + 'Cost'] || 0), 0), 0);

  const [gran, setGran] = useState('week');
  const rows = gran === 'week' ? weekRows : monthRows;
  const [focus, setFocus] = useState(0);
  useEffect(() => { setFocus(0); }, [gran]);
  useEffect(() => { if (focus > rows.length - 1) setFocus(Math.max(0, rows.length - 1)); }, [rows.length, focus]);

  const rowRefs = useRef({});
  useEffect(() => {
    const el = rowRefs.current[focus];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focus, gran]);

  const rowLabel = (r) => gran === 'week' ? `WK ${r.w} · ${fuelWeekEndLabel(r.y, r.w)}` : `${FUEL_MONTH_NAMES[r.m]} ${r.y}`;
  const rowKey = (r) => gran === 'week' ? `${r.y}-${r.w}` : `${r.y}-m${r.m}`;

  // #FLEET_FUELWEEKLY_V4_FIX2 (2026-09-02) — Bill: "Odd as you scroll the WK, Total & AVG $/GAL
  // black board sneaking into data box?" A sticky rowSpan={2} cell's box isn't automatically sized
  // to the combined height of both header rows, so while scrolling it can drift out of alignment
  // with the row-2 subheader cells (which stick independently at their own top:0) — a known
  // rowSpan+position:sticky mismatch. Fix: give every header cell an explicit height and give row-2
  // cells a top offset equal to row-1's height instead of top:0, so the two rows stick as one solid
  // block instead of two independently-drifting ones.
  const ROW_H = 22;
  const th = { textAlign: 'right', padding: '4px 6px', fontSize: 9, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', position: 'sticky', background: '#0A0E14', zIndex: 2, boxSizing: 'border-box' };
  const th1 = { ...th, top: 0, height: ROW_H };           // row-1: group labels (colSpan only)
  const th1span = { ...th, top: 0, height: ROW_H * 2 };   // row-1: WK / TOTAL / TOTAL GAL / AVG $/GAL (rowSpan={2})
  const th2 = { ...th, top: ROW_H, height: ROW_H };       // row-2: per-type Gal/$ subheaders
  const navDisabled = { prev: !rows.length || focus >= rows.length - 1, next: !rows.length || focus <= 0 };
  const navBtnStyle = (dis) => ({ background: F.cardHi, border: `1px solid ${F.line}`, borderRadius: 6, color: F.text, padding: '4px 10px', fontSize: 11, fontWeight: 800, cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.4 : 1 });

  return (
    <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, fontWeight: 900, color: F.text }}>{gran === 'week' ? 'Weekly' : 'Monthly'} summary · auto-rolled</div>
        <div style={{ background: F.cardHi, border: `1px solid ${F.line}`, borderRadius: 8, padding: '5px 10px' }}>
          <span style={{ fontSize: 8.5, fontWeight: 800, color: F.muted, textTransform: 'uppercase', letterSpacing: 0.3 }}>All-time</span>{' '}
          <span style={{ fontSize: 13.5, fontWeight: 900, color: F.gold }}>${Math.round(allTimeTotal).toLocaleString()}</span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['week', 'month'].map(g => (
            <button key={g} onClick={() => setGran(g)} style={{
              background: gran === g ? F.accent : F.cardHi, color: gran === g ? '#04121C' : F.text,
              border: `1px solid ${gran === g ? F.accent : F.line}`, borderRadius: 16, padding: '4px 12px',
              fontSize: 10.5, fontWeight: 800, cursor: 'pointer', textTransform: 'capitalize' }}>{g}ly</button>
          ))}
        </div>
        {!!rows.length && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setFocus(f => Math.min(rows.length - 1, f + 1))} disabled={navDisabled.prev} style={navBtnStyle(navDisabled.prev)}>‹ Prev</button>
            <div style={{ fontSize: 11, fontWeight: 800, color: F.text, minWidth: 110, textAlign: 'center' }}>{rowLabel(rows[focus])}</div>
            <button onClick={() => setFocus(f => Math.max(0, f - 1))} disabled={navDisabled.next} style={navBtnStyle(navDisabled.next)}>Next ›</button>
          </div>
        )}
      </div>

      {!weekRows.length && <div style={{ fontSize: 11.5, color: F.muted }}>Nothing logged yet.</div>}
      {weekRows.length > 0 && (
        <div style={{ maxHeight: 460, overflow: 'auto', border: `1px solid ${F.line}`, borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10.5 }}>
            <thead>
              <tr>
                <th style={{ ...th1span, textAlign: 'left' }} rowSpan={2}>{gran === 'week' ? 'WK' : 'MONTH'}</th>
                {FUEL_GROUPS.map(g => <th key={g.key} style={{ ...th1, textAlign: 'center', color: F[g.clr] }} colSpan={g.types.length * 2}>{g.label}</th>)}
                <th style={th1span} rowSpan={2}>TOTAL</th>
                <th style={th1span} rowSpan={2}>TOTAL GAL</th>
                <th style={th1span} rowSpan={2}>AVG $/GAL</th>
              </tr>
              <tr>
                {FUEL_GROUPS.flatMap(g => g.types.flatMap(([k, l]) => [
                  <th key={k + 'g'} style={th2}>{l} Gal</th>,
                  <th key={k + 'c'} style={th2}>{l} $</th>,
                ]))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const total = FUEL_TYPES.reduce((s, [k]) => s + (r.row[k + 'Cost'] || 0), 0);
                const totalGal = FUEL_TYPES.reduce((s, [k]) => s + (r.row[k + 'Gal'] || 0), 0);
                const avgPPG = totalGal ? total / totalGal : null;
                const prevRow = rows[idx + 1];   // rows sort newest-first, so idx+1 is the prior (older) period
                const prevTotal = prevRow ? FUEL_TYPES.reduce((s, [k]) => s + (prevRow.row[k + 'Cost'] || 0), 0) : 0;
                const prevGal = prevRow ? FUEL_TYPES.reduce((s, [k]) => s + (prevRow.row[k + 'Gal'] || 0), 0) : 0;
                const prevAvgPPG = prevGal ? prevTotal / prevGal : null;
                const avgChg = (avgPPG != null && prevAvgPPG) ? avgPPG - prevAvgPPG : null;
                const isFocus = idx === focus;
                return (
                  <tr key={rowKey(r)} ref={el => { rowRefs.current[idx] = el; }} onClick={() => setFocus(idx)} style={{ borderTop: `1px solid ${F.line}`, background: isFocus ? `${F.accent}22` : 'transparent', cursor: 'pointer' }}>
                    <td style={{ padding: '3px 6px', fontWeight: 800, color: isFocus ? F.accent : F.text, whiteSpace: 'nowrap' }}>{rowLabel(r)}</td>
                    {FUEL_TYPES.map(([k]) => (
                      <Fragment key={k}>
                        <td style={{ padding: '2px 5px', textAlign: 'right', color: F.muted }}>{r.row[k + 'Gal'] ? Math.round(r.row[k + 'Gal']).toLocaleString() : '—'}</td>
                        <td style={{ padding: '2px 5px', textAlign: 'right', color: F.text }}>{r.row[k + 'Cost'] ? '$' + Math.round(r.row[k + 'Cost']).toLocaleString() : '—'}</td>
                      </Fragment>
                    ))}
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 800, color: F.gold }}>${Math.round(total).toLocaleString()}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 700, color: F.text }}>{totalGal ? Math.round(totalGal).toLocaleString() : '—'}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {avgPPG != null ? (
                        <Fragment>
                          <span style={{ fontWeight: 800, color: F.text }}>${avgPPG.toFixed(2)}</span>
                          {avgChg != null && Math.abs(avgChg) >= 0.005 && (
                            <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 800, color: avgChg >= 0 ? F.red : F.green }}>{avgChg >= 0 ? '▲' : '▼'}{Math.abs(avgChg).toFixed(2)}</span>
                          )}
                        </Fragment>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 10, color: F.muted, marginTop: 8, lineHeight: 1.4 }}>Rolls up automatically from the log above — nothing to save here. Weekly is what Owner Dash's Fuel · Weekly tile reads; Monthly is the same data grouped by month, no separate entry needed.</div>
    </div>
  );
}

// #YARD_STAFFCAL_V1 (2026-09-02) — Staff Calendar for the Yard hub, mirrors Owner Dash's
// StaffCalendarTile (#OWNER_STAFFCAL_V1) — same source of truth (`hr-timeoff`), read-only.
// Duplicated here rather than imported since FleetModule has no shared scope with App.js.
export function YardStaffCalendar({ sg, F }) {
  const [timeOff, setTimeOff] = useState([]);
  const calName = useCalName(sg);   // #STAFF_CALNAME_V1 — HR nickname on the Yard calendar
  const [wk, setWk] = useState(0);
  const [open, setOpen] = useState(false);
  // #YARD_WIDGET_REFRESH_V1 (2026-09-02) — Bill: "how do we make it refresh, maybe each day?"
  // Loads on mount, then again once every 24h AND any time the tab/window regains focus (covers the
  // common case — someone leaves this open all shift, HR logs a new time-off entry elsewhere, it
  // shows up next time they glance back at this tab — without polling on a timer while it's not
  // being looked at).
  useEffect(() => {
    const load = async () => { try { const t = await sg('hr-timeoff'); if (Array.isArray(t)) setTimeOff(t); } catch {} };
    load();
    const iv = setInterval(load, 24 * 60 * 60 * 1000);
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [sg]);

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const _mondayOf = (d) => { const x = new Date(d); const b = (x.getDay() + 6) % 7; x.setDate(x.getDate() - b); x.setHours(0, 0, 0, 0); return x; };
  const _isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const weekStart = (() => { const ws = _mondayOf(new Date()); ws.setDate(ws.getDate() + wk * 7); return ws; })();
  const dates = DAYS.map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const todayISO = _isoD(new Date());
  const _md = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  const range = `${_md(dates[0])} – ${_md(dates[6])}`;
  const _isUnplanned = (t) => /no ?call|no ?show|ncns|absent|absence|unplanned|tardy/i.test(String(t || ''));   // #TIMEOFF_PM_TYPES_V1 + absence
  const shortName = (nm) => { const p = String(nm || '').trim().split(/\s+/).filter(Boolean); return p.length <= 1 ? (p[0] || '') : `${p[0]} ${p[p.length - 1][0]}.`; };
  const _staffFor = (pred, withType) => { const out = {}; DAYS.forEach((dn, i) => { const ds = _isoD(dates[i]); out[dn] = timeOff.filter(e => e.start && e.end && e.start <= ds && ds <= e.end && pred(e)).map(e => { const nm = calName(e); return withType ? (nm ? { name: nm, type: e.type || '' } : null) : (nm || null); }).filter(Boolean); }); return out; };
  const planned = _staffFor(e => !_isUnplanned(e.type));
  const unplanned = _staffFor(e => _isUnplanned(e.type), true);   // carries type for the Absent/Tardy icon
  const hasUnplanned = Object.values(unplanned).some(a => a && a.length);
  const allEmpty = Object.values(planned).every(a => !a || a.length === 0) && !hasUnplanned;
  const navBtn = { background: F.cardHi, border: `1px solid ${F.line}`, color: F.text, borderRadius: 6, padding: '2px 9px', fontSize: 13, fontWeight: 800, cursor: 'pointer' };
  const navBtnAccent = { ...navBtn, color: F.accent, fontSize: 10.5, padding: '2px 8px' };

  return (
    <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 12, padding: 14, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: F.text }}>Staff Calendar · Planned Off</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => setWk(w => w - 1)} title="Previous week" style={navBtn}>‹</button>
          {wk !== 0 && <button onClick={() => setWk(0)} title="Back to current week" style={navBtnAccent}>This week</button>}
          <button onClick={() => setWk(w => w + 1)} title="Next week" style={navBtn}>›</button>
          <span style={{ fontSize: 11, fontWeight: 700, color: F.muted, whiteSpace: 'nowrap' }}>{range}</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {DAYS.map((d, i) => { const isToday = _isoD(dates[i]) === todayISO; return (
          <div key={d} style={{ textAlign: 'center', borderBottom: `1px solid ${isToday ? F.accent : F.line}`, paddingBottom: 4 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: isToday ? F.accent : F.muted }}>{d.toUpperCase()}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: isToday ? F.accent : F.muted, opacity: isToday ? 1 : 0.75 }}>{_md(dates[i])}</div>
          </div>
        ); })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {DAYS.map((d) => { const names = Array.isArray(planned[d]) ? planned[d] : []; const shown = open ? names : names.slice(0, 6); const more = names.length - shown.length; return (
          <div key={d} style={{ minWidth: 0 }}>
            {(shown.length ? shown : ['']).map((nm, r) => (<div key={r} style={{ fontSize: open ? 11 : 9.5, color: nm ? F.text : F.muted, textAlign: 'center', whiteSpace: open ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '2px 1px', borderBottom: `1px solid ${F.line}33`, minHeight: 13 }}>{nm || '—'}</div>))}
            {more > 0 && <div style={{ fontSize: 9, color: F.accent, textAlign: 'center', paddingTop: 2, cursor: 'pointer' }} onClick={() => setOpen(true)}>+{more} more</div>}
          </div>
        ); })}
      </div>
      {hasUnplanned && (
        <Fragment>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '9px 0 6px' }}>
            <div style={{ flex: 1, height: 2, background: F.red }} />
            <span style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap' }}><span style={{ color: F.red }}>⚠ Unplanned Absence &amp;</span> <span style={{ color: '#F59E0B' }}>▲ Tardy</span></span>
            <div style={{ flex: 1, height: 2, background: F.red }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {DAYS.map((d) => { const names = Array.isArray(unplanned[d]) ? unplanned[d] : []; return (
              <div key={d} style={{ minWidth: 0 }}>
                {(names.length ? names : [null]).map((it, r) => { const nm = it ? it.name : ''; const isTardy = it && /tardy/i.test(it.type || ''); return (
                  <div key={r} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 9.5, fontWeight: nm ? 800 : 400, color: nm ? (isTardy ? '#F59E0B' : F.red) : F.muted, whiteSpace: 'nowrap', overflow: 'hidden', padding: '2px 1px', minHeight: 13 }}>
                    {nm && (isTardy
                      ? <span title='Tardy' style={{ color: '#F59E0B', fontSize: 9, lineHeight: 1, flexShrink: 0 }}>▲</span>
                      : <span title='Absent / No Call-No Show' style={{ width: 11, height: 11, borderRadius: '50%', background: '#EF4444', color: '#fff', fontSize: 7, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1 }}>A</span>)}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{nm || '—'}</span>
                  </div>
                ); })}
              </div>
            ); })}
          </div>
        </Fragment>
      )}
      {allEmpty && <div style={{ color: F.muted, fontSize: 10, marginTop: 6, textAlign: 'center' }}>Nobody off this week — fills from the HR time-off calendar.</div>}
    </div>
  );
}

// #YARD_CELEB_V1 (2026-09-02) — Celebrations widget for the Yard hub, same static Mon-Sun week +
// backfill math as Owner Dash (#OWNER_CELEB_V2) and PM Dash's Special Occasions (#PM_CELEB_V1) —
// duplicated here since FleetModule has no shared scope with those files.
export function YardCelebrations({ sg, F }) {
  const [hrEmps, setHrEmps] = useState([]);
  // #YARD_WIDGET_REFRESH_V1 — same daily + on-focus refresh as YardStaffCalendar above.
  useEffect(() => {
    const load = async () => { try { const h = await sg('hr-employees'); const arr = Array.isArray(h) ? h : (h && Array.isArray(h.rows) ? h.rows : []); if (arr.length) setHrEmps(arr); } catch {} };
    load();
    const iv = setInterval(load, 24 * 60 * 60 * 1000);
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [sg]);

  const _active = hrEmps.filter(e => String(e.status || 'Active') === 'Active');
  const _ymd = v => { if (typeof v !== 'string') return null; const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null; };
  const _today = new Date(); const _startToday = new Date(_today.getFullYear(), _today.getMonth(), _today.getDate());
  const _monthDay = dt => dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const _fullName = e => [e.first, e.last].filter(Boolean).join(' ') || '—';
  const _daysRel = dt => Math.round((dt - _startToday) / 86400000);
  const _dayLbl = n => n === 0 ? 'today' : n === 1 ? 'tomorrow' : n === -1 ? 'yesterday' : n > 1 ? `in ${n} days` : `${-n} days ago`;
  const _mondayOf = () => { const x = new Date(); const b = (x.getDay() + 6) % 7; x.setDate(x.getDate() - b); x.setHours(0, 0, 0, 0); return x; };
  const _mondayWksAgo = wksAgo => { const d = _mondayOf(); d.setDate(d.getDate() - 7 * wksAgo); return d; };
  const _occInWeek = (mo, d, wkMon) => { const wkEnd = new Date(wkMon); wkEnd.setDate(wkEnd.getDate() + 6);
    for (const yy of [wkMon.getFullYear() - 1, wkMon.getFullYear(), wkMon.getFullYear() + 1]) { const o = new Date(yy, mo - 1, d); if (o >= wkMon && o <= wkEnd) return o; } return null; };
  const CAP = 5, LOOKBACK = 8;
  const bdays = [], annivs = []; let bdayCut = 0, annivCut = 0;
  for (let w = 0; w <= LOOKBACK && (bdays.length < CAP || annivs.length < CAP); w++) {
    const wkMon = _mondayWksAgo(w);
    const bWk = [], aWk = [];
    _active.forEach(e => {
      const bd = _ymd(e.dob); if (bd) { const o = _occInWeek(bd.mo, bd.d, wkMon); if (o) bWk.push({ e, days: _daysRel(o), label: _monthDay(o), wk: w }); }
      const hd = _ymd(e.hire); if (hd) { const o = _occInWeek(hd.mo, hd.d, wkMon); if (o) { const yrs = o.getFullYear() - hd.y; if (yrs > 0) aWk.push({ e, days: _daysRel(o), yrs, label: _monthDay(o), wk: w }); } }
    });
    bWk.sort((a, b) => a.days - b.days); aWk.sort((a, b) => a.days - b.days);
    bWk.forEach(x => { if (bdays.length < CAP) bdays.push(x); });
    aWk.forEach(x => { if (annivs.length < CAP) annivs.push(x); });
    if (w === 0) { bdayCut = bdays.length; annivCut = annivs.length; }
  }
  const cutLabel = wk => wk === 1 ? 'Last Wk' : `${wk}wk ago`;

  return (
    <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 12, padding: 14, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, color: F.text, marginBottom: 10 }}>Celebrations</div>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: F.gold, marginBottom: 5 }}>🎂 Birthdays</div>
      {bdays.length ? bdays.map((x, i) => (
        <Fragment key={i}>
          {i === bdayCut && bdayCut < bdays.length && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '3px 0 5px' }}>
              <div style={{ flex: 1, height: 1, background: F.red, opacity: 0.5 }} />
              <span style={{ fontSize: 8, fontWeight: 800, color: F.red, letterSpacing: 0.3, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{cutLabel(x.wk)}</span>
              <div style={{ flex: 1, height: 1, background: F.red, opacity: 0.5 }} />
            </div>
          )}
          <div style={{ padding: '4px 0', borderBottom: i < bdays.length - 1 && i !== bdayCut - 1 ? `1px solid ${F.line}` : 'none' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: F.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{_fullName(x.e)}</div>
            <div style={{ fontSize: 9.5, fontWeight: x.days === 0 ? 800 : 600, color: x.days === 0 ? F.gold : F.muted }}>{x.label} · {_dayLbl(x.days)}</div>
          </div>
        </Fragment>
      )) : <div style={{ fontSize: 10.5, color: F.muted, marginBottom: 6 }}>None on file.</div>}
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: F.accent, marginTop: 10, marginBottom: 5 }}>💼 Anniversaries</div>
      {annivs.length ? annivs.map((x, i) => (
        <Fragment key={i}>
          {i === annivCut && annivCut < annivs.length && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '3px 0 5px' }}>
              <div style={{ flex: 1, height: 1, background: F.red, opacity: 0.5 }} />
              <span style={{ fontSize: 8, fontWeight: 800, color: F.red, letterSpacing: 0.3, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{cutLabel(x.wk)}</span>
              <div style={{ flex: 1, height: 1, background: F.red, opacity: 0.5 }} />
            </div>
          )}
          <div style={{ padding: '4px 0', borderBottom: i < annivs.length - 1 && i !== annivCut - 1 ? `1px solid ${F.line}` : 'none' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: F.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{_fullName(x.e)}</div>
            <div style={{ fontSize: 9.5, fontWeight: x.days === 0 ? 800 : 600, color: x.days === 0 ? F.accent : F.muted }}>{x.yrs} yrs · {x.label} · {_dayLbl(x.days)}</div>
          </div>
        </Fragment>
      )) : <div style={{ fontSize: 10.5, color: F.muted }}>None on file.</div>}
    </div>
  );
}

// #YARD_FUELWIDGET_V1 (2026-09-02) — Bill: "Can we add the Fuel Widget" (to the hub, alongside Staff
// Calendar/Celebrations, not just behind the Fuel · Weekly button). Compact read-only snapshot of
// `fleet-fuel-weekly` — most recent weeks on file + YTD total — with a jump button into the full
// FuelWeeklyPage (log + summary) for entry/edit. Same daily + on-focus refresh as the other two hub widgets.
// #FLEET_FUELWEEKLY_YARDMATCH_V1 (2026-09-03) — Bill: "this fuel weekly summary should match what we
// did for the Owners Dash to include Avenue & Impac with gallons etc." Brings this hub widget up to
// the same content Owner Dash's FuelWeeklyTile (App.js, #OWNER_FUELWEEKLY_V2) already shows: Avenue $
// / Impac $ split per week instead of one blended Total $, a Gal column on every row, YTD Gal, an
// Avg $/Gal stat with a ▲/▼ trend badge vs the prior filled week, and the by-type gallon pills (RED
// Diesel / Green Diesel / Diesel / Unleaded). Reuses this file's own FUEL_GROUPS/FUEL_TYPES (already
// in module scope here, unlike App.js which keeps its own duplicate) — same math, no new storage.
// Row count stays at 5 (this widget sits in a 3-up hub row next to Staff Calendar/Celebrations,
// unlike Owner Dash's own single-column tile) — say the word if it should show more.
export function YardFuelWeekly({ sg, F, onOpen }) {
  const [store, setStore] = useState({});
  useEffect(() => {
    const load = async () => { try { const f = await sg('fleet-fuel-weekly'); if (f && typeof f === 'object' && !Array.isArray(f)) setStore(f); } catch {} };
    load();
    const iv = setInterval(load, 24 * 60 * 60 * 1000);
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [sg]);

  const years = Object.keys(store).filter(k => /^\d+$/.test(k)).map(Number).sort((a, b) => b - a);
  const y = years[0];
  const wks = (y && store[y]) || {};
  const filledWeeks = Object.keys(wks).map(Number).filter(w => Number((wks[w] || {}).TOTAL) > 0).sort((a, b) => b - a);
  const MAX_ROWS = 5;
  const rows = filledWeeks.slice(0, MAX_ROWS);
  const ytd = filledWeeks.reduce((s, w) => s + (Number((wks[w] || {}).TOTAL) || 0), 0);

  const galKeys = FUEL_TYPES.map(([k]) => k + 'Gal');
  const weekGal = (w) => galKeys.reduce((s, gk) => s + (Number((wks[w] || {})[gk]) || 0), 0);
  const ytdGal = (gk) => filledWeeks.reduce((s, w) => s + (Number((wks[w] || {})[gk]) || 0), 0);
  const ytdGalTotal = galKeys.reduce((s, gk) => s + ytdGal(gk), 0);
  const srcLabel = { yard: 'Avenue', impac: 'Impac' };
  const srcCost = (w, group) => group.types.reduce((s, t) => s + (Number((wks[w] || {})[t[0] + 'Cost']) || 0), 0);
  const weekAvgPPG = (w) => { const g = weekGal(w); return g ? (Number((wks[w] || {}).TOTAL) || 0) / g : null; };
  // #OWNERDASH_FUELCOMPLETEWK_V1 (2026-09-03) — Bill: "could we consider displaying the
  // increase/decrease to reflect only completed weeks as this could be misleading" (raised against
  // the matching Owner Dash tile; applied here too since this widget carries the identical Avg
  // $/Gal + trend-badge math). A week with Avenue logged but Impac not reported yet still counted as
  // "latest" (TOTAL > 0 from Avenue alone), so the badge could compare a still-in-progress week's
  // partial mix against a fully-reported prior week and read as a dramatic (and false) swing.
  // Restrict the Avg $/Gal stat + trend badge to weeks that have fully elapsed on the calendar
  // (wk < the current ISO week, or an earlier year) — the trend table above still shows the
  // in-progress week with whatever's logged so far; this only changes what the rate comparison is
  // built from.
  const nowIsoYr = isoWeekYear(new Date()), nowIsoWk = isoWeek(new Date());
  const completedWeeks = filledWeeks.filter(w => y < nowIsoYr || (y === nowIsoYr && w < nowIsoWk));
  const latestAvg = completedWeeks.length ? weekAvgPPG(completedWeeks[0]) : null;
  const prevAvg = completedWeeks.length > 1 ? weekAvgPPG(completedWeeks[1]) : null;
  const avgPctChg = (latestAvg != null && prevAvg) ? ((latestAvg - prevAvg) / prevAvg * 100) : null;

  return (
    <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 12, padding: 14, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: F.text }}>Fuel · Weekly{y ? ` · ${y}` : ''}</div>
        <button onClick={onOpen} style={{ background: F.cardHi, border: `1px solid ${F.line}`, color: F.gold, borderRadius: 6, padding: '3px 9px', fontSize: 10.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>Log / view full →</button>
      </div>
      {!rows.length && <div style={{ color: F.muted, fontSize: 11.5 }}>Nothing logged yet — tap above to start this week's numbers.</div>}
      {rows.length > 0 && (
        <Fragment>
          <div style={{ border: `1px solid ${F.line}`, borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 6px', color: F.muted, fontWeight: 800, fontSize: 9 }}>WK</th>
                  {FUEL_GROUPS.map(g => <th key={g.key} style={{ textAlign: 'right', padding: '4px 6px', color: F.muted, fontWeight: 800, fontSize: 9 }}>{srcLabel[g.key] || g.short} $</th>)}
                  <th style={{ textAlign: 'right', padding: '4px 6px', color: F.text, fontWeight: 800, fontSize: 9 }}>Total $</th>
                  <th style={{ textAlign: 'right', padding: '4px 6px', color: F.muted, fontWeight: 800, fontSize: 9 }}>Gal</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(w => {
                  const wTotal = Number((wks[w] || {}).TOTAL) || 0;
                  const wGal = weekGal(w);
                  return (
                  <tr key={w} style={{ borderTop: `1px solid ${F.line}` }}>
                    <td style={{ padding: '3px 6px', fontWeight: 700, color: F.text, whiteSpace: 'nowrap' }}>WK{w}</td>
                    {FUEL_GROUPS.map(g => {
                      const c = srcCost(w, g);
                      return <td key={g.key} style={{ padding: '3px 6px', textAlign: 'right', color: c ? F.text : F.muted, whiteSpace: 'nowrap' }}>{c ? '$' + Math.round(c).toLocaleString() : '—'}</td>;
                    })}
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 800, color: F.text, whiteSpace: 'nowrap' }}>{wTotal ? '$' + Math.round(wTotal).toLocaleString() : '—'}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', color: F.muted, whiteSpace: 'nowrap' }}>{wGal ? Math.round(wGal).toLocaleString() : '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <div style={{ flex: '1 1 76px', background: F.cardHi, borderRadius: 8, padding: '6px 9px', minWidth: 0 }}>
              <div style={{ fontSize: 8.5, color: F.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>YTD {y}</div>
              <div style={{ fontSize: 14.5, fontWeight: 900, color: F.gold, whiteSpace: 'nowrap' }}>${Math.round(ytd).toLocaleString()}</div>
            </div>
            {ytdGalTotal > 0 && (
              <div style={{ flex: '1 1 76px', background: F.cardHi, borderRadius: 8, padding: '6px 9px', minWidth: 0 }}>
                <div style={{ fontSize: 8.5, color: F.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>YTD Gal</div>
                <div style={{ fontSize: 14.5, fontWeight: 900, color: F.text, whiteSpace: 'nowrap' }}>{Math.round(ytdGalTotal).toLocaleString()}</div>
              </div>
            )}
            {latestAvg != null && (
              <div style={{ flex: '1 1 76px', background: F.cardHi, borderRadius: 8, padding: '6px 9px', minWidth: 0 }}>
                <div style={{ fontSize: 8.5, color: F.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>Avg $/Gal · WK{completedWeeks[0]}</div>
                <div style={{ fontSize: 14.5, fontWeight: 900, color: F.text, whiteSpace: 'nowrap' }}>${latestAvg.toFixed(2)}</div>
              </div>
            )}
            {avgPctChg != null && (
              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', borderRadius: 8, background: avgPctChg >= 0 ? `${F.red}22` : `${F.green}22`, border: `1px solid ${avgPctChg >= 0 ? F.red : F.green}` }}>
                <span style={{ fontSize: 12, fontWeight: 900, color: avgPctChg >= 0 ? F.red : F.green, whiteSpace: 'nowrap' }}>{avgPctChg >= 0 ? '▲' : '▼'} {Math.abs(avgPctChg).toFixed(1)}%{prevAvg != null && ` ($${Math.abs(latestAvg - prevAvg).toFixed(2)})`}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {FUEL_TYPES.map(([k, l]) => <span key={k} style={{ fontSize: 9.5, fontWeight: 700, color: F.text, background: F.cardHi, border: `1px solid ${F.line}`, borderRadius: 5, padding: '3px 8px' }}>{l} {Math.round(ytdGal(k + 'Gal')).toLocaleString()} gal</span>)}
          </div>
        </Fragment>
      )}
    </div>
  );
}

// #YARD_CHEMSPEND_V1 (2026-09-24) — Bill: "Chemicals he would like to see MTD, YTD and Last Year
// compared... If there is room we can forecast YEAR Spend Projection." Same self-contained hub
// widget pattern as YardFuelWeekly above (own sg() fetch, no shared scope with
// ChemicalsYardDashboard) — reads the same fleet-chem-disbursement-log / fleet-chem-inventory keys
// Chemicals itself writes, no new storage. MTD/YTD are straight sums off the log; "Same period LY"
// is the matching Jan 1 → today span one year back, for an apples-to-apples comparison instead of
// a full prior year total. Year projection is a simple run-rate (YTD ÷ days elapsed × 365) — labeled
// as a projection, not a real forecast model, since that's genuinely all the data supports today.
// #YARD_CHEMTREND_V1 (2026-10-01) — Bill: "could we get Prior Month Summary here… we have totals from prior
// years… make a cool graph." The log only starts Sep 2026, so YTD / projection / last-year were all wrong.
// Now: prior-year monthly totals live in chemSpendHistorySeed.json (2021 → Aug 2026); the card merges that
// with the live log month by month, adds a Prior Month tile, a this-year-vs-any-year monthly graph, a
// year-by-year bar graph (click a year to compare it), and a Table view of every month.
export function YardChemSpend({ sg, F, onOpen }) {
  const [log, setLog] = useState(null);
  const [inv, setInv] = useState(null);
  const [cmpPick, setCmpPick] = useState(null);     // #YARD_CHEMTREND_V1 — year the monthly graph compares against (null = last year)
  const [showTable, setShowTable] = useState(false);
  const [hov, setHov] = useState(null);             // { c:'m'|'y', i } — hovered month / year
  const wrapRef = useRef(null);
  const [W, setW] = useState(360);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const l = await sg('fleet-chem-disbursement-log'); if (alive && Array.isArray(l)) setLog(l); } catch {}
      try { const i = await sg('fleet-chem-inventory'); if (alive && Array.isArray(i)) setInv(i); } catch {}
    };
    load();
    const iv = setInterval(load, 24 * 60 * 60 * 1000);
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [sg]);
  const ready = log !== null && inv !== null;
  // The graphs are drawn at the card's real pixel width so the type never scales up or down with the layout.
  useEffect(() => {
    const el = wrapRef.current; if (!el) return undefined;
    const fit = () => setW(Math.max(260, Math.floor(el.clientWidth)));
    fit();
    if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', fit); return () => window.removeEventListener('resize', fit); }
    const ro = new ResizeObserver(fit); ro.observe(el); return () => ro.disconnect();
  }, [ready, showTable]);

  if (!ready) return (
    <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:14 }}>
      <div style={{ fontSize:13, fontWeight:900, color:F.text, marginBottom:6 }}>Chemicals · Spend</div>
      <div style={{ fontSize:11.5, color:F.muted }}>Loading…</div>
    </div>
  );

  const amt = (e) => e.kind === 'return' ? -Number(e.totalCost||0) : Number(e.totalCost||0);
  const now = new Date();
  const y = now.getFullYear();
  const cm = now.getMonth();
  const startOfYear = new Date(y, 0, 1);
  const doy = Math.floor((now - startOfYear) / 86400000) + 1;
  const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // #YARD_CHEMTREND_V1 — one month grid, two sources. The history file (chemSpendHistorySeed.json, 2021 →
  // Aug 2026) wins wherever it has a figure; every other month is summed live off the disbursement log.
  // So the card is right today and keeps building on its own — nothing to re-load each month.
  const byYM = {};
  log.forEach(e => { const k = String(e.date||'').slice(0,7); if (/^\d{4}-\d{2}$/.test(k)) byYM[k] = (byYM[k]||0) + amt(e); });
  const HIST = (CHEM_SPEND_HIST && CHEM_SPEND_HIST.months) || {};
  const cell = (yr, m) => {
    const h = (HIST[yr]||[])[m];
    if (h != null && h !== '') return Number(h) || 0;
    const v = byYM[`${yr}-${String(m+1).padStart(2,'0')}`];
    return v == null ? null : v;
  };
  let histThru = '';
  Object.keys(HIST).map(Number).sort((p,q) => p-q).forEach(yr => (HIST[yr]||[]).forEach((v, m) => { if (v != null && v !== '') histThru = `${MON[m]} ${yr}`; }));
  const years = [...new Set([...Object.keys(HIST).map(Number), ...Object.keys(byYM).map(k => +k.slice(0,4)), y])].filter(n => n && n <= y).sort((a,b) => a-b);
  const S = {};
  years.forEach(yr => { S[yr] = MON.map((_, m) => (yr === y && m > cm) ? null : cell(yr, m)); });
  const sum = (a) => (a||[]).reduce((s,v) => s + (v||0), 0);

  const mtd = S[y][cm] || 0;
  const ytd = sum(S[y]);
  const pmY = cm === 0 ? y-1 : y, pmM = cm === 0 ? 11 : cm-1;
  const priorMonth = (S[pmY]||[])[pmM];
  const priorMonthLY = (S[pmY-1]||[])[pmM];
  const pmPct = priorMonth != null && priorMonthLY ? ((priorMonth - priorMonthLY) / priorMonthLY * 100) : null;
  // History is monthly, so "same period last year" = last year's full months to date + this month pro-rated by day.
  const ly = S[y-1] || [];
  const daysInMonth = new Date(y, cm+1, 0).getDate();
  const lastYearSamePeriod = sum(ly.slice(0, cm)) + (ly[cm]||0) * (now.getDate() / daysInMonth);
  const yoyDelta = ytd - lastYearSamePeriod;
  const yoyPct = lastYearSamePeriod ? (yoyDelta / lastYearSamePeriod * 100) : null;
  const yearProjection = doy > 0 ? (ytd / doy) * 365 : 0;
  const lyTotal = sum(ly);

  const onHand = inv.filter(r => { const c = String(r.code||'').trim(); return c !== 'V CSGN' && c !== 'D CSGN'; }).reduce((s,r) => s + (Number(r.qty)||0) * (Number(r.cost)||0), 0);

  const kfmt = (v) => { const n = Number(v)||0; return n >= 1e6 ? '$' + (n/1e6).toFixed(2) + 'M' : n >= 1e3 ? '$' + Math.round(n/1e3) + 'K' : '$' + Math.round(n); };
  const Stat = ({ label, value, color, sub, title }) => (
    <div title={title} style={{ flex:'1 1 90px', background:F.cardHi, borderRadius:8, padding:'6px 9px', minWidth:0 }}>
      <div style={{ fontSize:8.5, color:F.muted, fontWeight:800, textTransform:'uppercase', letterSpacing:.3 }}>{label}</div>
      <div style={{ fontSize:14.5, fontWeight:900, color: color||F.text, whiteSpace:'nowrap' }}>{value == null ? '—' : money(value)}</div>
      {sub && <div style={{ fontSize:9, color:F.muted, fontWeight:700, whiteSpace:'nowrap' }}>{sub}</div>}
    </div>
  );

  // ── graph colours: this year is the point (accent), the comparison year is steel, the rest recede ──
  const C_CUR = F.accent, C_CMP = '#9AA6B0', C_DIM = '#59626A', GRID = F.line;
  const pastYears = years.filter(yr => yr !== y && sum(S[yr]) > 0);
  const cmp = pastYears.includes(cmpPick) ? cmpPick : (pastYears.includes(y-1) ? y-1 : pastYears[pastYears.length-1]);
  const tipBox = { position:'absolute', top:0, pointerEvents:'none', background:F.cardHi, border:`1px solid ${F.line}`, borderRadius:8, padding:'6px 9px', fontSize:11, color:F.text, zIndex:5, whiteSpace:'nowrap', boxShadow:'0 6px 18px rgba(0,0,0,.45)' };
  const keyLine = (col) => <span style={{ display:'inline-block', width:12, height:0, borderTop:`2px solid ${col}`, verticalAlign:'middle', marginRight:5 }} />;
  const cap = { fontSize:8.5, color:F.muted, fontWeight:800, textTransform:'uppercase', letterSpacing:.3 };

  // ── monthly graph: this year (through the last finished month) against the comparison year ──
  const mH = 150, mL = 36, mR = 14, mT = 14, mB = 18;
  const curPts = S[y].map((v, m) => (m < cm && v != null) ? v : null);   // the month in progress is not plotted — a half-month would read as a crash
  const cmpPts = cmp ? S[cmp] : [];
  const mMaxRaw = Math.max(1, ...curPts.map(v => v||0), ...cmpPts.map(v => v||0));
  const stepOf = (mx) => { const raw = mx / 3; const p = Math.pow(10, Math.floor(Math.log10(raw))); const f = raw / p; return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p; };
  const mStep = stepOf(mMaxRaw); const mMax = Math.ceil(mMaxRaw / mStep) * mStep;
  const mx = (m) => mL + m * ((W - mL - mR) / 11);
  const my = (v) => mT + (1 - v / mMax) * (mH - mT - mB);
  const pathOf = (pts) => { let d = '', pen = false; pts.forEach((v, m) => { if (v == null) { pen = false; return; } d += `${pen ? 'L' : 'M'}${mx(m).toFixed(1)},${my(v).toFixed(1)} `; pen = true; }); return d.trim(); };
  let lastM = -1; curPts.forEach((v, m) => { if (v != null) lastM = m; });
  const hm = hov && hov.c === 'm' ? hov.i : null;
  const monthAt = (e) => { const r = e.currentTarget.getBoundingClientRect(); const px = (e.clientX - r.left) * (W / r.width); return Math.max(0, Math.min(11, Math.round((px - mL) / ((W - mL - mR) / 11)))); };

  // ── yearly graph: every full year, plus this year as YTD with the projected remainder stacked on top ──
  const aH = 124, aL = 6, aR = 74, aT = 18, aB = 18;
  const tot = {}; years.forEach(yr => { tot[yr] = sum(S[yr]); });
  const barYears = years.filter(yr => tot[yr] > 0 || yr === y);
  const proj = Math.max(yearProjection, ytd);
  const aMax = Math.max(1, proj, ...barYears.map(yr => tot[yr]));
  const band = (W - aL - aR) / Math.max(1, barYears.length);
  const bw = Math.min(24, Math.max(10, band - 12));
  const ay = (v) => aT + (1 - v / aMax) * (aH - aT - aB);
  const base = aH - aB;
  const col = (x, top, bot, r) => { const h = Math.max(0, bot - top); const rr = Math.min(r, h, bw/2); return `M${x},${bot} L${x},${top+rr} Q${x},${top} ${x+rr},${top} L${x+bw-rr},${top} Q${x+bw},${top} ${x+bw},${top+rr} L${x+bw},${bot} Z`; };
  const hy = hov && hov.c === 'y' ? hov.i : null;

  return (
    <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:14, minWidth:0, display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, gap:8, flexWrap:'wrap' }}>
        <div style={{ fontSize:13, fontWeight:900, color:F.text }}>Chemicals · Spend</div>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={()=>{ setShowTable(v=>!v); setHov(null); }} style={{ background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:6, padding:'3px 9px', fontSize:10.5, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>{showTable ? 'Graph' : 'Table'}</button>
          <button onClick={onOpen} style={{ background:F.cardHi, border:`1px solid ${F.line}`, color:F.gold, borderRadius:6, padding:'3px 9px', fontSize:10.5, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>Open →</button>
        </div>
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        <Stat label="On hand" value={onHand} color={F.green} />
        <Stat label="MTD" value={mtd} sub={`${MON[cm]} so far`} />
        <Stat label={`Prior month · ${MON[pmM]}`} value={priorMonth}
          sub={pmPct == null ? null : `${pmPct > 0 ? '▲' : '▼'} ${Math.abs(pmPct).toFixed(0)}% vs ${MON[pmM]} ${pmY-1}`}
          title={priorMonthLY ? `${MON[pmM]} ${pmY-1}: ${money(priorMonthLY)}` : undefined} />
        <Stat label={`YTD ${y}`} value={ytd} color={F.gold} />
        <Stat label={`Same period ${y-1}`} value={lastYearSamePeriod} title={`${y-1} through ${MON[cm]} ${now.getDate()} — full months plus ${MON[cm]} pro-rated by day`} />
      </div>
      {yoyPct != null && (
        <div style={{ marginTop:8 }}>
          <span style={{ fontSize:11, fontWeight:800, color: yoyDelta<=0?F.green:F.red }}>{yoyDelta<=0?'▼':'▲'} {Math.abs(yoyPct).toFixed(0)}% vs same point last year</span>
          <span style={{ fontSize:11, color:F.muted }}> · {yoyDelta<=0?'−':'+'}{money(Math.abs(yoyDelta))}</span>
        </div>
      )}
      <div style={{ marginTop:10, borderTop:`1px solid ${F.line}`, paddingTop:8, display:'flex', gap:18, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div>
          <div style={cap}>Year projection (YTD pace)</div>
          <div style={{ fontSize:16, fontWeight:900, color:F.accent }}>{money(yearProjection)}</div>
        </div>
        {lyTotal > 0 && <div style={{ fontSize:11, color:F.muted, paddingBottom:2 }}>{y-1} full year <b style={{ color:F.text }}>{money(lyTotal)}</b></div>}
      </div>

      <div ref={wrapRef} style={{ marginTop:10, borderTop:`1px solid ${F.line}`, paddingTop:8, minWidth:0 }}>
        {showTable ? (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10.5, fontVariantNumeric:'tabular-nums' }}>
              <thead><tr>
                <th style={{ textAlign:'left', padding:'3px 4px', color:F.muted, fontWeight:800 }}></th>
                {barYears.map(yr => <th key={yr} style={{ textAlign:'right', padding:'3px 4px', color: yr === y ? F.text : F.muted, fontWeight:800 }}>{yr}</th>)}
              </tr></thead>
              <tbody>
                {MON.map((mn, m) => (
                  <tr key={mn} style={{ borderTop:`1px solid ${F.line}` }}>
                    <td style={{ padding:'3px 4px', color:F.muted, fontWeight:800 }}>{mn}</td>
                    {barYears.map(yr => { const v = S[yr][m]; return <td key={yr} style={{ textAlign:'right', padding:'3px 4px', color: v == null ? F.muted : F.text, fontWeight: yr === y ? 800 : 600 }}>{v == null ? '—' : money(v)}{yr === y && m === cm && v != null ? '*' : ''}</td>; })}
                  </tr>
                ))}
                <tr style={{ borderTop:`2px solid ${F.line}` }}>
                  <td style={{ padding:'4px', color:F.text, fontWeight:900 }}>Total</td>
                  {barYears.map(yr => <td key={yr} style={{ textAlign:'right', padding:'4px', color:F.text, fontWeight:900 }}>{money(tot[yr])}</td>)}
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize:9.5, color:F.muted, marginTop:5 }}>* {MON[cm]} is month to date. {y} total is year to date.</div>
          </div>
        ) : (
          <Fragment>
            {/* By month */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:2 }}>
              <div style={cap}>By month</div>
              <div style={{ fontSize:10.5, color:F.muted, fontWeight:700 }}>
                <span style={{ marginRight:10 }}>{keyLine(C_CUR)}<span style={{ color:F.text }}>{y}</span></span>
                {cmp && <span>{keyLine(C_CMP)}{cmp}</span>}
              </div>
            </div>
            <div style={{ position:'relative' }}>
              <svg width={W} height={mH} style={{ display:'block', maxWidth:'100%' }} role="img" aria-label={`Chemical spend by month, ${y}${cmp ? ' against ' + cmp : ''}`}>
                {Array.from({ length: Math.round(mMax / mStep) + 1 }, (_, i) => i * mStep).map(t => (
                  <g key={t}>
                    <line x1={mL} x2={W - mR} y1={my(t)} y2={my(t)} stroke={GRID} strokeWidth={1} opacity={t === 0 ? 1 : .55} />
                    <text x={mL - 6} y={my(t) + 3} textAnchor="end" fontSize={9} fill={F.muted} style={{ fontVariantNumeric:'tabular-nums' }}>{kfmt(t)}</text>
                  </g>
                ))}
                {MON.map((mn, m) => <text key={mn} x={mx(m)} y={mH - 5} textAnchor="middle" fontSize={9} fill={hm === m ? F.text : F.muted} fontWeight={hm === m ? 800 : 600}>{mn[0]}</text>)}
                {hm != null && <line x1={mx(hm)} x2={mx(hm)} y1={mT - 4} y2={mH - mB} stroke={F.muted} strokeWidth={1} />}
                {cmp && <path d={pathOf(cmpPts)} fill="none" stroke={C_CMP} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
                <path d={pathOf(curPts)} fill="none" stroke={C_CUR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {lastM >= 0 && hm == null && (
                  <g>
                    <circle cx={mx(lastM)} cy={my(curPts[lastM])} r={4.5} fill={C_CUR} stroke={F.card} strokeWidth={2} />
                    <text x={mx(lastM) + (lastM >= 9 ? -8 : 8)} y={my(curPts[lastM]) - 7} textAnchor={lastM >= 9 ? 'end' : 'start'} fontSize={10} fontWeight={800} fill={F.text}>{MON[lastM]} {kfmt(curPts[lastM])}</text>
                  </g>
                )}
                {hm != null && cmp && cmpPts[hm] != null && <circle cx={mx(hm)} cy={my(cmpPts[hm])} r={4} fill={C_CMP} stroke={F.card} strokeWidth={2} />}
                {hm != null && curPts[hm] != null && <circle cx={mx(hm)} cy={my(curPts[hm])} r={4.5} fill={C_CUR} stroke={F.card} strokeWidth={2} />}
                <rect x={0} y={0} width={W} height={mH} fill="transparent" tabIndex={0} style={{ outline:'none', cursor:'crosshair' }}
                  onPointerMove={e => { const i = monthAt(e); setHov(h => (h && h.c === 'm' && h.i === i) ? h : { c:'m', i }); }}
                  onPointerLeave={() => setHov(null)} onBlur={() => setHov(null)}
                  onFocus={() => setHov(h => h && h.c === 'm' ? h : { c:'m', i: Math.max(0, lastM) })}
                  onKeyDown={e => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); setHov(h => ({ c:'m', i: Math.max(0, Math.min(11, ((h && h.c === 'm') ? h.i : Math.max(0, lastM)) + (e.key === 'ArrowRight' ? 1 : -1))) })); } }} />
              </svg>
              {hm != null && (() => {
                const a = S[y][hm], b = cmp ? cmpPts[hm] : null; const inProg = hm === cm;
                const pct = (!inProg && a != null && b) ? ((a - b) / b * 100) : null;
                const right = mx(hm) > W / 2;
                return (
                  <div style={{ ...tipBox, ...(right ? { right: Math.max(0, W - mx(hm) + 10) } : { left: mx(hm) + 10 }) }}>
                    <div style={{ fontWeight:900, marginBottom:2 }}>{MON[hm]}</div>
                    <div>{keyLine(C_CUR)}<b>{a == null ? '—' : money(a)}</b> <span style={{ color:F.muted }}>{y}{inProg ? ' · so far' : ''}</span></div>
                    {cmp && <div>{keyLine(C_CMP)}<b>{b == null ? '—' : money(b)}</b> <span style={{ color:F.muted }}>{cmp}</span></div>}
                    {pct != null && <div style={{ color:F.muted, marginTop:2 }}>{pct > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}% vs {cmp}</div>}
                  </div>
                );
              })()}
            </div>

            {/* By year */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:10, marginBottom:2 }}>
              <div style={cap}>By year</div>
              <div style={{ fontSize:10, color:F.muted }}>click a year to compare it above</div>
            </div>
            <div style={{ position:'relative' }}>
              <svg width={W} height={aH} style={{ display:'block', maxWidth:'100%' }} role="img" aria-label="Chemical spend by year">
                <line x1={aL} x2={W - aR + 6} y1={base} y2={base} stroke={GRID} strokeWidth={1} />
                {barYears.map((yr, i) => {
                  const x = aL + i * band + (band - bw) / 2; const isCur = yr === y; const isCmp = yr === cmp; const lift = hy === i;
                  const solidTop = ay(isCur ? ytd : tot[yr]);
                  const hasProj = isCur && proj > ytd * 1.005;
                  return (
                    <g key={yr}>
                      {hasProj && <path d={col(x, ay(proj), solidTop - 2, 4)} fill={C_CUR} opacity={.3} />}
                      <path d={col(x, solidTop, base, hasProj ? 0 : 4)} fill={isCur ? C_CUR : isCmp ? C_CMP : C_DIM} opacity={lift && !isCur && !isCmp ? .8 : 1} stroke={lift ? F.text : 'none'} strokeWidth={lift ? 1 : 0} />
                      {!isCur && <text x={x + bw/2} y={solidTop - 5} textAnchor="middle" fontSize={9.5} fontWeight={isCmp ? 800 : 600} fill={isCmp ? F.text : F.muted}>{kfmt(tot[yr])}</text>}
                      {isCur && hasProj && <text x={x + bw + 6} y={ay(proj) + 8} fontSize={9.5} fontWeight={600} fill={F.muted}>{kfmt(proj)} proj.</text>}
                      {isCur && <text x={x + bw + 6} y={Math.max(solidTop + 9, ay(proj) + 20)} fontSize={9.5} fontWeight={800} fill={F.text}>{kfmt(ytd)} YTD</text>}
                      <text x={x + bw/2} y={aH - 5} textAnchor="middle" fontSize={9.5} fontWeight={isCur || isCmp ? 800 : 600} fill={isCur || isCmp ? F.text : F.muted}>{yr}</text>
                      <rect x={aL + i * band} y={0} width={band} height={aH} fill="transparent" tabIndex={0}
                        style={{ outline:'none', cursor: isCur ? 'default' : 'pointer' }}
                        onPointerEnter={() => setHov({ c:'y', i })} onPointerLeave={() => setHov(null)}
                        onFocus={() => setHov({ c:'y', i })} onBlur={() => setHov(null)}
                        onClick={() => { if (!isCur) setCmpPick(yr); }}
                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !isCur) { e.preventDefault(); setCmpPick(yr); } }} />
                    </g>
                  );
                })}
              </svg>
              {hy != null && barYears[hy] != null && (() => {
                const yr = barYears[hy]; const isCur = yr === y;
                const cx = aL + hy * band + band / 2; const right = cx > W / 2;
                const full = S[yr].filter((v, m) => v != null && !(isCur && m === cm));
                return (
                  <div style={{ ...tipBox, ...(right ? { right: Math.max(0, W - cx + bw/2 + 8) } : { left: cx + bw/2 + 8 }) }}>
                    <div style={{ fontWeight:900, marginBottom:2 }}>{yr}</div>
                    <div><b>{money(tot[yr])}</b> <span style={{ color:F.muted }}>{isCur ? 'year to date' : 'full year'}</span></div>
                    {isCur && <div><b>{money(proj)}</b> <span style={{ color:F.muted }}>projected at this pace</span></div>}
                    {full.length > 0 && <div style={{ color:F.muted, marginTop:2 }}>{money(sum(full) / full.length)} a month avg{isCur ? ` (${full.length} finished months)` : ''}</div>}
                  </div>
                );
              })()}
            </div>
          </Fragment>
        )}
        {histThru && <div style={{ fontSize:9.5, color:F.muted, marginTop:6 }}>History file through {histThru} · live Chemicals log after that.</div>}
      </div>
    </div>
  );
}
